import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import MolstarViewerPath from '../../components/MolstarViewerPath/MolstarViewerPath';
import { structureMap } from '../../ApiFunctions';

// PrimeReact Components
import { InputText } from 'primereact/inputtext';
import { MultiSelect } from 'primereact/multiselect';
import { ListBox } from 'primereact/listbox';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Timeline } from 'primereact/timeline';
import { Message } from 'primereact/message';
import { Divider } from 'primereact/divider';
import { Tag } from 'primereact/tag';
import { Dropdown } from 'primereact/dropdown';
import ColumnToggleDataTable from '../../components/ColumnToggleDataTable/ColumnToggleDataTable';
import { TabView, TabPanel } from 'primereact/tabview';
import { Column } from 'primereact/column';
import { Tooltip } from 'primereact/tooltip';
import { Activity, Layers, Network, Share2, AlertTriangle, Download, BarChart2, Tornado, Route, Grip } from 'lucide-react';
import { InputNumber } from 'primereact/inputnumber';
import { Checkbox } from 'primereact/checkbox';
import { SelectButton } from 'primereact/selectbutton';
import complexMapping from '../../complex_mapping.json';
import multimerData from '../../mapa_alphafold.json';
import BetweennessBarChart from '../../components/BetweennessBarChart/BetweennessBarChart';
import GroupedResidueBarChart from '../../components/GroupedResidueBarChart/GroupedResidueBarChart';
import SpmProfileTablePath from '../../components/SpmProfileTablePath/SpmProfileTablePath';
import EgoNetworkPlot from '../../components/EgoNetworkPlot/EgoNetworkPlot';
import GenericAnalysisResult from '../../components/GenericAnalysisResult/GenericAnalysisResult';
import Plot from 'react-plotly.js';

import geneMapData from '../../map_gene_pro.json';

// --- GRAPHQL QUERY DEFINITION ---
const GRAPHQL_QUERY = `
  query GetGraphAnalysis($input: GraphAnalysisInput!) {
    graphAnalysis(inputData: $input) {
      resultType
      totalDistance
      totalGeometricDistance
      path {
        name
        annotations {
          has_clinvar: hasClinvar
          has_intra_contact: hasIntraContact
          has_af_intra_contact: hasAfIntraContact
          has_inter_contact: hasInterContact
          has_af_inter_contact: hasAfInterContact
          has_ligand: hasLigand
          is_functional: isFunctional
          has_site: hasSite
        }
      }
      tableData
      text
      enrichmentStats {
        __typename
        ... on Level3EnrichmentStats {
          meanDensityInterface
          availableStructures
          structuresData
        }
        ... on Level4EnrichmentStats {
          bindingFisher {
            observed
            interfaceTotal
            expected
            pValue
            foldEnrichment
            backgroundRate
            interfaceRate
            oddsRatio
            oddsRatioIsInfinite
            ciLow
            ciHigh
            ciHighIsInfinite
            backgroundPathogenic
            backgroundTotal
            structure
          }
          activeFisher {
            observed
            interfaceTotal
            expected
            pValue
            foldEnrichment
            backgroundRate
            interfaceRate
            oddsRatio
            oddsRatioIsInfinite
            ciLow
            ciHigh
            ciHighIsInfinite
            backgroundPathogenic
            backgroundTotal
            structure
          }
          meanDensityGlobal
          availableStructures
          structuresData
          variantClusterSynergy
          allostericBridges
          pinnedStructure
          analysisWarning
        }
        ... on Level13Stats {
          structure
          proteinA
          nVariants
          baselinePtmN
          analysisWarning
        }
      }
    }
  }
`;


// ─────────────────────────────────────────────────────────────────────────────
// UniProt -> structure position mapping, keyed by position, all chains.
//
// ApiFunctions.structureMap() is left untouched (other pages depend on it) but
// cannot serve this case: getPDB2Uniprot only requests `pdbresPos`, so the two
// flat arrays it returns carry no record of which UniProt position produced
// each entry. Callers therefore walk them index-parallel with their input —
// which breaks twice over. The API deduplicates `IN $uniprotPositions`, so a
// row list with repeated positions (105 rows over 21 residues, typical for a
// variant table) gets back far fewer items than it sent, and it answers
// chain-major, so a homodimer returns two blocks. Reading by index then pins
// one residue's mapping onto a different residue.
//
// getPdbPosition also returns `uniacPos`. Asking for it removes the guesswork:
// one request, every chain, grouped by the position it actually belongs to.
// ─────────────────────────────────────────────────────────────────────────────
const PDB_POSITION_QUERY = `
  query getPdbPosition($uniprotPositions: [Int!]!, $pdb: String!, $uniac: String!) {
    getPdbPosition(uniprotPositions: $uniprotPositions, pdb: $pdb, uniac: $uniac) {
      items { pdbresPos uniacPos }
    }
  }
`;

async function mapPositionsAllChains(positions, pdb, uniac) {
  const uniq = [...new Set((positions || []).map(p => parseInt(p, 10)).filter(Number.isInteger))];
  if (!uniq.length || !pdb || !uniac) return {};

  const response = await fetch('https://api.atlantis.bioinfolab.sns.it/graphql', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      query: PDB_POSITION_QUERY,
      variables: { uniprotPositions: uniq, pdb: String(pdb).toLowerCase(), uniac },
    }),
  });
  if (!response.ok) throw new Error(`Position mapping failed: ${response.status}`);
  const json = await response.json();
  if (json.errors) throw new Error(json.errors[0].message);

  const byPos = {};
  (json?.data?.getPdbPosition?.items || []).forEach(({ pdbresPos, uniacPos }) => {
    // uniacPos is "V36"; pdbresPos is "A|VAL16".
    const uni = parseInt(String(uniacPos || '').replace(/[^0-9]/g, ''), 10);
    const [chain, resPart] = String(pdbresPos || '').split('|');
    const mapped = parseInt(String(resPart || '').match(/\d+/)?.[0], 10);
    if (!Number.isInteger(uni) || !Number.isInteger(mapped) || !chain) return;
    if (!byPos[uni]) byPos[uni] = { mapped: String(mapped), chains: [] };
    if (!byPos[uni].chains.includes(chain)) byPos[uni].chains.push(chain);
  });
  Object.values(byPos).forEach(v => v.chains.sort());
  return byPos;
}

const PathfinderPage = () => {
  // --- STATE ---
  const [mode, setMode] = useState('level1');

  const [formData, setFormData] = useState({
    startResidue: '',
    endResidue: '',
    proteinId: '',
    proteinA: '',
    proteinB: '',
    pdbId: '',
    pdbId2: '',
    afId: '',
    distance: '',
    pathMode: 'functional',
    contactScope: 'intra',
    addLigand: false,
    spmPercentile: 10
  });

  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [submittedPdbId, setSubmittedPdbId] = useState(null);
  const [submittedPdbId2, setSubmittedPdbId2] = useState(null);
  const [submittedAfId, setSubmittedAfId] = useState(null);
  const [chain, setChain] = useState(undefined);
  const [positions, setPositions] = useState([]);
  const [level3ViewMode, setLevel3ViewMode]       = useState('variants'); // 'variants' | 'enrichment'
  const [level3SelectedRow, setLevel3SelectedRow] = useState(null);
  const [level3PathogenicityFilters, setLevel3PathogenicityFilters] = useState([
  'pathogenic', 'conflicting', 'vus_am_pathogenic'
  ]);

  const [level3ViewerFilters, setLevel3ViewerFilters] = useState([
    'pathogenic',
    'likely_pathogenic',
    'pathogenic/likely_pathogenic',
    'conflicting_classifications_of_pathogenicity',
    'uncertain_significance',
    'likely_benign',
    'benign'
  ]);


  const [showOnlyPathogenic, setShowOnlyPathogenic] = useState(false);
  const [showOnlyPathogenicLevel4, setShowOnlyPathogenicLevel4] = useState(false);
  // Level 4 3D viewer (Mol*) — residue→structure mapping runs async after render
  // so the table/plots are never blocked while structureMap resolves.
  const [level4Mol, setLevel4Mol] = useState({ loading: false, ready: false, byPos: {} });
  const [level9Mol, setLevel9Mol] = useState({ loading: false, ready: false, byPos: {}, structureId: null });
  const [level4ViewerMode, setLevel4ViewerMode] = useState('variants'); // variants | enrichment | severity
  const [level4ViewerSite, setLevel4ViewerSite] = useState('both');     // both | binding | active
  const [level4ViewerFilters, setLevel4ViewerFilters] = useState([]); // viewer-only ClinVar filter (empty = all)
  const [level5ColorBy, setLevel5ColorBy] = useState('plddt'); // plddt | class
  const [level5Superpose, setLevel5Superpose] = useState(false); // load PDB overlay on demand
  const [level14ColorBy, setLevel14ColorBy] = useState('class'); // class | rewiring
  const [level14Superpose, setLevel14Superpose] = useState(false); // overlay structure B on demand
  const [centralityMetric, setCentralityMetric] = useState('Degree');

  // SPM analysis: top-N% percentile for hub/edge classification
  const [spmPercentile, setSpmPercentile] = useState(10);

  const [submittedPdbIdLevel4, setSubmittedPdbIdLevel4] = useState(null);
  const [level9ViewMode, setLevel9ViewMode] = useState('variants'); // variants | enrichment
  const [level9ViewerFilters, setLevel9ViewerFilters] = useState(['pathogenic','benign']); // show in structure
  const [level11Structure, setLevel11Structure] = useState(null);        // inter: selected PDB/AF
  const [level11IntraStructure, setLevel11IntraStructure] = useState(null); // intra: selected PDB/AF
  const [level11ClusterPick, setLevel11ClusterPick] = useState('all');      // intra: which cluster to show
  const [level11InterShow, setLevel11InterShow] = useState('all');          // inter: which partner variants to show

  // Level 3: auto-select first available structure when a new result arrives,
  // so plots and viewer render immediately without the user picking one.
  useEffect(() => {
    if (mode !== 'level3') return;
    const structs = result?.enrichmentStats?.availableStructures || [];
    if (structs.length === 0) return;
    // Only auto-select if current value isn't in the new list
    if (!submittedPdbId || !structs.includes(submittedPdbId)) {
      setSubmittedPdbId(structs[0]);
    }
  }, [result, mode]);

  // Level 4 always runs against the user-pinned structure, so just select it,
  // falling back to the first real PDB and then to the UniProt bucket.
  useEffect(() => {
    if (mode !== 'level4') return;
    const structs = result?.enrichmentStats?.availableStructures || [];
    if (structs.length === 0) return;

    const pinned = result?.enrichmentStats?.pinnedStructure;
    const preferred =
      (pinned && structs.includes(pinned) && pinned)
      || structs.find(s => s !== 'UniProt')
      || structs[0];

    // Always snap to the preferred (pinned) structure. There is no manual
    // structure picker anymore, so a previous selection must not be preserved:
    // 'UniProt' is present in nearly every result, and the old guard
    // (!structs.includes(...)) would leave the view stuck on the UniProt bucket
    // — flat-zero proximity, empty 3D enrichment — instead of the pinned PDB.
    if (preferred && submittedPdbIdLevel4 !== preferred) {
      setSubmittedPdbIdLevel4(preferred);
    }
  }, [result, mode]);

  // Level 4: map every variant position onto the pinned structure for the Mol*
  // viewer. Done in an effect (not in the result handler) and batched into a
  // single structureMap call, so the page renders immediately and the viewer
  // fills in when the mapping resolves — never blocking the browser.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const structureId = result?.enrichmentStats?.pinnedStructure || formData.pdbId;
      if (mode !== 'level4' || !result?.data?.length || !structureId) {
        setLevel4Mol({ loading: false, ready: false, byPos: {} });
        return;
      }
      setLevel4Mol({ loading: true, ready: false, byPos: {} });
      const proteinUid = (result.data.find(
        r => typeof r.col1 === 'string' && r.col1.includes('_')
      )?.col1 || '').split('_')[0] || null;
      const posNums = [...new Set(
        result.data
          .map(r => { const p = String(r.col1 || '').split('_'); return p.length > 1 ? parseInt(p[1], 10) : NaN; })
          .filter(Number.isInteger)
      )];
      const byPos = {};
      try {
        if (String(structureId).toUpperCase().startsWith('AF-')) {
          const hyphens = (structureId.match(/-/g) || []).length;
          const chain = (hyphens >= 2 || /^AF-\d{16}$/i.test(structureId)) ? resolveAlphaFoldChain(structureId, proteinUid) : 'A';
          posNums.forEach(p => { byPos[p] = { mapped: String(p), chains: [chain] }; });
        } else if (posNums.length && proteinUid) {
          // Group by the UniProt position the API reports rather than by index.
          // The list is deduplicated here, so it does not scramble the way the
          // Level 3 one did, but the answer is still chain-major: reading only
          // res.mappedPositions[i] took the first chain and dropped the rest,
          // colouring half of a homodimer and a quarter of a tetramer.
          Object.assign(byPos, await mapPositionsAllChains(posNums, structureId, proteinUid));
        }
      } catch (e) {
        console.warn('Level 4 Mol* mapping failed:', e);
      }
      if (!cancelled) setLevel4Mol({ loading: false, ready: true, byPos });
    })();
    return () => { cancelled = true; };
  }, [result, mode, formData.pdbId]);

  // Level 8 (mode level9): the analysis reports UniProt positions, but Mol*
  // selects on auth_seq_id / auth_asym_id. On the AlphaFold monomer the two
  // coincide (chain A, UniProt numbering), which is why hardcoding chain 'A'
  // worked while the viewer only ever showed the monomer. On an experimental
  // structure they do not: the protein may be another chain entirely (beta-2 AR
  // is chain R in 3sn6, where chain A is Gs-alpha) and unresolved stretches have
  // no auth_seq_id at all. Without this mapping the enrichment and PTM layers
  // silently select nothing, or colour the wrong molecule.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (mode !== 'level9' || !result?.data?.length) {
        setLevel9Mol({ loading: false, ready: false, byPos: {}, structureId: null });
        return;
      }
      const rows = result.data;
      const statsRow = rows.find(r => r.kind === 'stats');
      const pairs = rows.filter(r => r.kind === 'pair');
      let acc = statsRow?.accession || null;
      if (!acc) {
        const m = ((pairs[0] && pairs[0].col2) || '').match(/at\s+([A-Za-z0-9]+)_/);
        acc = m ? m[1] : null;
      }
      const afId = statsRow?.af_id || (acc ? `AF-${acc}` : null);
      const structureId = submittedPdbId || afId;
      if (!structureId || !acc) {
        setLevel9Mol({ loading: false, ready: false, byPos: {}, structureId: null });
        return;
      }

      // Every UniProt position the viewer may need: variants, enrichment, PTMs.
      const posNums = [...new Set([
        ...rows.filter(r => r.kind === 'vpos').map(r => parseInt(r.pos, 10)),
        ...rows.filter(r => r.kind === 'enr').map(r => parseInt(r.col1, 10)),
        ...pairs.flatMap(p => {
          const m = String(p.col2 || '').match(/_(\d+)/);
          return m ? [parseInt(m[1], 10)] : [];
        }),
      ].filter(Number.isInteger))];

      setLevel9Mol({ loading: true, ready: false, byPos: {}, structureId });
      const byPos = {};
      try {
        if (String(structureId).toUpperCase().startsWith('AF-')) {
          const hyphens = (structureId.match(/-/g) || []).length;
          const chain = (hyphens >= 2 || /^AF-\d{16}$/i.test(structureId))
            ? resolveAlphaFoldChain(structureId, acc)
            : 'A';
          posNums.forEach(p => { byPos[p] = { mapped: String(p), chains: [chain] }; });
        } else if (posNums.length) {
          Object.assign(byPos, await mapPositionsAllChains(posNums, structureId, acc));
        }
      } catch (e) {
        console.warn('Level 8 Mol* mapping failed:', e);
      }
      if (!cancelled) setLevel9Mol({ loading: false, ready: true, byPos, structureId });
    })();
    return () => { cancelled = true; };
  }, [result, mode, submittedPdbId]);

  const renderNumericFilter = (options) => (
    <InputText
      type="number"
      value={options.value ?? ''}
      onChange={(e) => options.filterApplyCallback(
        e.target.value === '' ? null : parseFloat(e.target.value)
      )}
      placeholder="Search"
      className="p-inputtext-sm"
    />
  );


  // --- ANALYSIS OPTIONS ---
  // Flat list kept for any code that still needs to resolve a label from a value.
  const analysisModes = [
    { label: 'Level 1: Physical Path (Residue to Residue)', value: 'level1' },
    { label: 'Level 2: Structural Hubs (Critical Residues)', value: 'level2' },
    { label: 'Level 3: PPI Interface Hotspots (Disease)', value: 'level3' },
    { label: 'Level 4: Functional Sites Overlap', value: 'level4' },
    { label: 'Level 5: Structure differences (PDB vs AlphaFold)', value: 'level5' },
    { label: 'Level 6: PTM Crosstalk (3D Regulation)', value: 'level6' },
    { label: 'Level 7: PTM Hotspots (Sequence Clustering)', value: 'level7' },
    { label: 'Level 8: Domain-PTM Enrichment (Functional Density)', value: 'level8' },
    { label: 'Level 9: PTM Structural Enrichment Analysis (Disease Mechanism)', value: 'level9' },
    { label: 'Level 10: Pan-Family Evolutionary Mapping (HMM)', value: 'level10' },
    { label: 'Level 11: Structural Co-Pathogenicity', value: 'level11' },
    { label: 'Level 12: VUS Structural Prioritisation', value: 'level12' },
    { label: 'Level 13: Variant Convergence Triage', value: 'level13' },
    { label: 'Level 14: Conformational Differences (PDB vs PDB)', value: 'level14' }
  ];

  // Grouped by biological question (matching the module description doc), with levels
  // numbered continuously 1..14 across the whole list (not restarting per group).
  // The underlying `value` (level1, level2, ...) is untouched, so the rest of the
  // component keeps working exactly as before — only the dropdown's grouping/labels change.
  const groupedAnalysisModes = [
    {
      label: 'Structural analyses',
      code: 'STR',
      items: [
        { label: '1: Physical Path (Residue to Residue)', value: 'level1' },
        { label: '2: Structural Hubs (Critical Residues)', value: 'level2' },
        { label: '3: Structure Differences (PDB vs AlphaFold)', value: 'level5' },
        { label: '4: Conformational Differences (PDB vs PDB)', value: 'level14' }
      ]
    },
    {
      label: 'Post-translational modification analyses',
      code: 'PTM',
      items: [
        { label: '5: PTM Crosstalk (3D Regulation)', value: 'level6' },
        { label: '6: PTM Hotspots (Sequence Clustering)', value: 'level7' },
        { label: '7: Domain-PTM Enrichment (Functional Density)', value: 'level8' },
        { label: '8: PTM Structural Enrichment Analysis (Disease Mechanism)', value: 'level9' }
      ]
    },
    {
      label: 'Variant analyses',
      code: 'VAR',
      items: [
        { label: '9: PPI Interface Hotspots (Disease)', value: 'level3' },
        { label: '10: Functional Sites Overlap', value: 'level4' },
        { label: '11: Pan-Family Evolutionary Mapping (HMM)', value: 'level10' },
        { label: '12: Structural Co-Pathogenicity', value: 'level11' },
        { label: '13: VUS Structural Prioritisation', value: 'level12' },
        { label: '14: Variant Convergence Triage', value: 'level13' }
      ]
    }
  ];

   // Ego network drill-in (residue selected from SPM table or betweenness chart)
   const [egoResidue, setEgoResidue] = useState(null);
   const [egoLoading, setEgoLoading] = useState(false);
   const [egoData, setEgoData] = useState(null);
   const [analysisLoading, setAnalysisLoading] = useState(false);

  function openEgoNetwork(residue) {
    if (!residue || !result?.data) return;

    setEgoResidue(residue);
    setEgoLoading(false);
    setEgoData(null);

    const targetRow = result.data.find(
        (row) => row && row.col1 === String(residue) && row.col1 !== '__summary__'
    );

    if (!targetRow || !Array.isArray(targetRow.neighbors)) {
        setEgoData({ neighbors: [] });
        return;
    }

    const spmByResidue = new Map();
    result.data.forEach((row) => {
        if (row && row.col1 && row.col1 !== '__summary__') {
            spmByResidue.set(String(row.col1), {
                betweenness: Number(row.col3),
                isHub: row.col5 === 'Yes',
            });
        }
    });

    const neighbors = targetRow.neighbors.map((n) => {
        const spm = spmByResidue.get(n.position) || {};
        const summary = n.summary || {};
        return {
            position: n.position,
            distance: n.distance,
            betweenness: spm.betweenness,
            isHub: !!spm.isHub,
            amClass: n.am_class || null,
            annotations: {
                summary
            },
            rawLabel: null,
            features: null,
        };
    });

    setEgoData({ neighbors });
}

  const resolveInput = (input) => {
      if (!input) return null;
      const cleanInput = input.trim();

      if (cleanInput.includes('_')) {
          const parts = cleanInput.split('_');
          const prefix = parts[0].toUpperCase();
          const suffix = parts.slice(1).join('_');

          const found = geneMapData.find(g => g.GENE_SYMBOL === prefix);
          if (found) {
              return `${found.PROTEIN_AN}_${suffix}`;
          }
          return cleanInput;
      }

      const upperInput = cleanInput.toUpperCase();
      const found = geneMapData.find(g => g.GENE_SYMBOL === upperInput);
      return found ? found.PROTEIN_AN : cleanInput;
  };

  // 2. CSV Downloader
  const downloadCSV = () => {
      if (!result || !result.data || result.type !== 'table') return;

      const data = result.data;
      if (data.length === 0) return;

      const headers = [...new Set(data.flatMap(row => Object.keys(row)))];

      // Monta CSV
      const csvRows = [];
      csvRows.push(headers.join(','));

      for (const row of data) {
          const values = headers.map(header => {
              const escaped = ('' + row[header]).replace(/"/g, '\\"');
              return `"${escaped}"`;
          });
          csvRows.push(values.join(','));
      }

      const csvString = csvRows.join('\n');
      const blob = new Blob([csvString], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.setAttribute('hidden', '');
      a.setAttribute('href', url);
      a.setAttribute('download', `pathfinder_results_${mode}.csv`);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
  };

  const getMarkerRenderer = (listLength) => (item, index) => {
      if (index === 0) return <i className="pi pi-circle-fill text-danger" style={{ fontSize: '1.2rem' }}></i>;
      if (index === listLength - 1) return <i className="pi pi-check-circle text-success" style={{ fontSize: '1.2rem' }}></i>; // Fim
      return <i className="pi pi-circle text-secondary" style={{ fontSize: '0.8rem' }}></i>; // Meio
  };

  const customizedMarker = (item) => {
        return (
            <span className="flex w-2rem h-2rem align-items-center justify-content-center text-white border-circle z-1 shadow-1"
                style={{ backgroundColor: item.annotations?.has_clinvar ? '#EF4444' : '#3B82F6' }}>
                <i className={item.annotations?.has_clinvar ? "pi pi-exclamation-triangle" : "pi pi-circle-fill"} style={{fontSize: '0.8rem'}}></i>
            </span>
        );
    };

    const customizedContent = (item) => {
        return (
            <Card className="mb-3 shadow-none border-1 surface-border p-0">
                <div className="flex align-items-center justify-content-between mb-2">
                    <span className="font-bold text-lg text-900">{item.name}</span>
                    {item.annotations?.description && <Tag value={item.annotations.description} severity="info" />}
                </div>
                {item.annotations?.has_clinvar && <div className="text-red-500 text-sm"><i className="pi pi-exclamation-circle me-1"></i>Pathogenic Variant</div>}
                {item.annotations?.has_intra_contact && <div className="text-blue-500 text-sm"><i className="pi pi-link me-1"></i>Physical Contact</div>}
            </Card>
        );
    };

  // --- HANDLERS ---
const handleChange = (e) => {
  const { name, value } = e.target;

  let cleanValue = value;

  if (name === "pdbId") {
    cleanValue = value.replace(/\s+/g, "");

    if (!cleanValue.startsWith("AF-")) {
      cleanValue = cleanValue.toLowerCase();
    }
  }

  setFormData(prev => ({
    ...prev,
    [name]: cleanValue
  }));
};

  const handleCalculate = async (e) => {
  e.preventDefault();
  setLoading(true);
  setError(null);
  setResult(null);
  setPositions([]);
  setChain(undefined);

  // --- VALIDATION ---
  const validationError = validateForm();
  if (validationError) {
    setError(validationError);
    setLoading(false);
    return;
  }

  try {
    setSubmittedPdbId(formData.pdbId);
    setSubmittedPdbId2(formData.pdbId2);
    // Level 3 renders this model in Mol*; empty means the backend picked the
    // monomer, which the viewer resolves from `uniac`.
    setSubmittedAfId(mode === 'level5' ? (formData.afId || null) : null);

    // 1. Build payload
    const inputPayload = {
      mode,
      startResidue: resolveInput(formData.startResidue) || null,
      endResidue: resolveInput(formData.endResidue) || null,
      proteinId: formData.proteinId ? parseInt(formData.proteinId) : null,
      proteinA: resolveInput(formData.proteinA) || null,
      proteinB: resolveInput(formData.proteinB) || null,
      pdbId: formData.pdbId || null,
      pdbId2: mode === 'level14' ? (formData.pdbId2 || null) : undefined,  // omit key for other levels (avoids schema-unknown-field errors pre-deploy)
      // Level 3 only. Optional: null lets the backend default to the AlphaFold
      // monomer AF-<accession>.
      afId: mode === 'level5' ? (formData.afId || null) : undefined,
      distance: formData.distance || null,
      pathMode: mode === 'level1' ? formData.pathMode : null,
      contactScope: (mode === 'level1' || mode === 'level2') ? formData.contactScope : (mode === 'level11' ? (formData.copathScope || 'inter') : null),
      addLigand: (mode === 'level1' || mode === 'level2') ? formData.addLigand : null,
      variants: mode === 'level13' ? (formData.variants || null) : null,
      spmPercentile: Number(spmPercentile) || 10
    };

    // 2. Fetch
    const response = await fetch('https://api.atlantis.bioinfolab.sns.it/graphql', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        query: GRAPHQL_QUERY,
        variables: { input: inputPayload },
      }),
    });

    if (!response.ok) {
      throw new Error(`Server error: ${response.status} ${response.statusText}`);
    }

    const jsonResponse = await response.json();

    if (jsonResponse.errors) {
      throw new Error(jsonResponse.errors[0].message);
    }

    const data = jsonResponse.data?.graphAnalysis;
    if (!data) {
      throw new Error('No analysis data returned. Please verify your inputs.');
    }

    // 3. Format & set result
    // The backend answers with resultType 'text' when it declines to run (e.g.
    // an AlphaFold model that carries no intra-contacts). Surface its message.
    if (data.resultType === 'text') {
      throw new Error(data.text || 'The analysis returned no result.');
    }
    if (data.resultType === 'path') {
      await handlePathResult(data);
    } else {
      await handleTableResult(data);
    }
  } catch (err) {
    console.error(err);
    setError(err.message || 'Connection failed. Please check the backend.');
  } finally {
    setLoading(false);
  }
};

// --- VALIDATION (extracted) ---
const validateForm = () => {
  const rules = {
    level1: {
      fields: ['startResidue', 'endResidue', 'pdbId'],
      message: 'Please enter Source Residue, Target Residue, and Structure Identifier.',
    },
    level2: {
      fields: ['proteinA', 'pdbId'],
      message: 'Please enter Gene Symbol/Protein Accession and Structure Identifier.',
    },
    level5: {
      fields: ['proteinA', 'pdbId'],
      message: 'Please enter Gene Symbol/Protein Accession and Structure Identifier.',
    },
    level14: {
      fields: ['proteinA', 'pdbId', 'pdbId2'],
      message: 'Please enter Gene Symbol/Protein Accession and two PDB Structure Identifiers.',
    },
    level3: {
      fields: ['proteinA', 'proteinB', 'pdbId'],
      message: 'Please enter both Protein Accession numbers and a Structure Identifier',
    },
    level10: {
      fields: ['proteinA'],
      message: 'Please enter a Gene Symbol/Protein Accession.',
    },
    level12: {
      fields: ['proteinA', 'pdbId'],
      message: 'Please enter a Gene Symbol/Protein Accession and a Structure Identifier.',
    },
     level4: {
      fields: ['proteinA', 'pdbId'],
      message: 'Please enter Gene Symbol/Protein Accession and Structure Identifier.',
    },
    level6: { fields: ['proteinA'], message: 'Please enter Gene Symbol/Protein Accession.' },
    level7: { fields: ['proteinA'], message: 'Please enter Gene Symbol/Protein Accession.' },
    level8: { fields: ['proteinA'], message: 'Please enter Gene Symbol/Protein Accession.' },
    level9: {  fields: ['proteinA', 'pdbId'],
      message: 'Please enter Gene Symbol/Protein Accession and Structure Identifier.',
      },
    level11: { fields: ['proteinA'], message: 'Please enter Gene Symbol/Protein Accession.' },
  };

  const rule = rules[mode];
  if (!rule) return null;

  const missing = rule.fields.some((field) => !formData[field]);
  return missing ? rule.message : null;
};

// --- PATH RESULT HANDLER (Level 1) ---
const handlePathResult = async (data) => {
  const formattedResult = {
    type: 'path',
    totalDistance: data.totalDistance ? parseFloat(data.totalDistance.toFixed(2)) : 0,
    totalGeometricDistance: data.totalGeometricDistance
      ? parseFloat(data.totalGeometricDistance.toFixed(2))
      : null,
    pathMode: formData.pathMode,
    path: data.path,
  };

  const parsedNodes = data.path
    .map((node) => {
      const parts = node?.name?.split('_');
      const proteinUid = parts?.[0] || null;
      const pos = parts?.length > 1 ? parseInt(parts[1], 10) : null;
      return { proteinUid, pos };
    })
    .filter((item) => item.proteinUid && item.pos !== null);

  if (parsedNodes.length > 0) {
    // Same three-way routing the variants section uses, keyed by the shape of
    // the structure id:
    //   >= 2 hyphens  -> 'CO' complex: positions stay in UniProt numbering,
    //                    chain comes from complex_mapping.json
    //   AF-<16 digits> -> multimer: chain comes from mapa_alphafold.json
    //   AF-<acc>       -> monomer: chain A
    //   anything else  -> real PDB: positions and chains come from the API
    // resolveAlphaFoldChain already covers the first three, per protein.
    const hyphenCount = (String(formData.pdbId || '').match(/-/g) || []).length;
    const isAlphaFold = formData.pdbId?.toUpperCase().startsWith('AF-');
    const isComplexModel = hyphenCount >= 2;
    const isModel = isAlphaFold || isComplexModel;

    // Chain per protein, in the order the model lays them out. For a predicted
    // complex (AF-<accA>-<accB>) that order is the accession order in the id;
    // otherwise it is the order the proteins first appear in the path. A
    // single-protein path still gets 'A', as before — but an inter-protein
    // path no longer collapses every node onto chain A.
    const CHAIN_IDS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const uidsInPath = [...new Set(parsedNodes.map((n) => n.proteinUid))];
    const uidByUpper = new Map(uidsInPath.map((u) => [u.toUpperCase(), u]));
    const idAccessions = String(formData.pdbId || '').replace(/^AF-/i, '').split('-').filter(Boolean);

    const orderedUids = [];
    idAccessions.forEach((acc) => {
      const uid = uidByUpper.get(acc.toUpperCase());
      if (uid && !orderedUids.includes(uid)) orderedUids.push(uid);
    });
    uidsInPath.forEach((uid) => { if (!orderedUids.includes(uid)) orderedUids.push(uid); });

    const modelChainByUid = new Map(orderedUids.map((uid, i) => [uid, CHAIN_IDS[i] || 'A']));

    if (formData.pdbId && !isModel) {
      const groupedByProtein = parsedNodes.reduce((acc, item) => {
        if (!acc[item.proteinUid]) acc[item.proteinUid] = [];
        acc[item.proteinUid].push(item.pos);
        return acc;
      }, {});

      try {
        // mapPositionsAllChains, not structureMap: the path needs to know which
        // UniProt position produced each mapping (see the note on the query
        // above), and it needs every chain a position lives on, not the first.
        const maps = {};
        await Promise.all(Object.entries(groupedByProtein).map(async ([proteinUid, uniprotPositions]) => {
          try {
            maps[proteinUid] = await mapPositionsAllChains(uniprotPositions, formData.pdbId, proteinUid);
          } catch (err) {
            console.warn(`Position mapping failed for ${proteinUid}:`, err);
            maps[proteinUid] = {};
          }
        }));

        // One chain per protein: the chain most of its nodes share, and never a
        // chain already claimed by another protein — otherwise both sides of an
        // inter-protein path land on the same copy of a homodimer.
        const taken = new Set();
        const chainByUid = new Map();
        orderedUids.forEach((uid) => {
          const counts = new Map();
          parsedNodes
            .filter((n) => n.proteinUid === uid)
            .forEach((n) => {
              (maps[uid]?.[n.pos]?.chains || []).forEach((c) => counts.set(c, (counts.get(c) || 0) + 1));
            });
          const ranked = [...counts.entries()]
            .sort((a, b) => b[1] - a[1] || String(a[0]).localeCompare(String(b[0])))
            .map(([c]) => c);
          const picked = ranked.find((c) => !taken.has(c)) ?? ranked[0];
          if (picked) { taken.add(picked); chainByUid.set(uid, picked); }
        });

        // Path order is the point of a path, so nodes stay in path order here
        // instead of being regrouped per protein.
        const mappedNodes = parsedNodes.map((node) => {
          const entry = maps[node.proteinUid]?.[node.pos];
          if (!entry) return null;
          const mapped = parseInt(entry.mapped, 10);
          if (!Number.isInteger(mapped)) return null;
          const preferred = chainByUid.get(node.proteinUid);
          const chainId = (preferred && entry.chains.includes(preferred)) ? preferred : entry.chains[0];
          return chainId ? { pos: mapped, chain: chainId } : null;
        }).filter(Boolean);

        if (mappedNodes.length > 0) {
          console.info('Path mapped onto', formData.pdbId, '->',
            mappedNodes.map((n) => `${n.chain}/${n.pos}`).join('  '));
          setPositions(mappedNodes.map((n) => n.pos));
          setChain(mappedNodes.map((n) => n.chain));
        } else {
          console.warn('No path node could be mapped onto', formData.pdbId, '- falling back to raw positions.');
          setPositions(parsedNodes.map((n) => n.pos));
          setChain(parsedNodes.map((n) => modelChainByUid.get(n.proteinUid) || 'A'));
        }
      } catch (mapErr) {
        console.warn('Structure mapping error:', mapErr);
      }
    } else {
      // Model (AF monomer, AF multimer or CO complex): positions stay in
      // UniProt numbering and each protein sits on its own chain. Prefer
      // the same mapping the other levels use; the model order derived above is
      // only the fallback when the id isn't in the complex maps.
      const modelNodes = parsedNodes.map((item) => {
        const resolved = formData.pdbId ? resolveAlphaFoldChain(formData.pdbId, item.proteinUid) : null;
        const chainId = (resolved && resolved !== 'A')
          ? resolved
          : (modelChainByUid.get(item.proteinUid) || resolved || 'A');
        return { pos: item.pos, chain: chainId, uid: item.proteinUid };
      });
      console.info('Path on model', formData.pdbId, '->',
        modelNodes.map((n) => `${n.uid}:${n.chain}/${n.pos}`).join('  '));
      setPositions(modelNodes.map((n) => n.pos));
      setChain(modelNodes.map((n) => n.chain));
    }
  }

  setResult(formattedResult);
};

// --- TABLE RESULT HANDLER (Levels 2-12) ---
const handleTableResult = async (data) => {
  const formattedResult = {
    type: 'table',
    data: data.tableData,
    molstarData: [],
    enrichmentStats: data.enrichmentStats || null,
    provenance: data.text || null,
  };

  if (mode === 'level2') {
    formattedResult.molstarData = await mapLevel2MolstarData(data.tableData);
  }

  if (mode === 'level3') {
    if (!data.tableData || data.tableData.length === 0) {
      throw new Error('No analysis data returned. Please verify your inputs.');
    }
    formattedResult.molstarData = await mapLevel3MolstarData(data.tableData);
  }

  if (mode === 'level11') {
    formattedResult.molstarData = await mapLevel11MolstarData(data.tableData);
  }

  if (mode === 'level12') {
    formattedResult.molstarData = await mapLevel12MolstarData(data.tableData);
  }

  if (mode === 'level13') {
    formattedResult.molstarData = await mapLevel13MolstarData(data.tableData, data.enrichmentStats);
  }

  setResult(formattedResult);
};

// --- LEVEL 2 MOLSTAR MAPPING (extracted) ---
const mapLevel2MolstarData = async (tableData) => {
  if (!formData.pdbId || !Array.isArray(tableData) || tableData.length === 0) return tableData;

  const isAlphaFold = formData.pdbId.toUpperCase().startsWith('AF-');
  const proteinUid = tableData
  .find(r => {
    const val = r.col1?.trim();
    return (
      typeof val === 'string' &&
      val !== '__summary__' &&
      val.includes('_')
    );
  })
  ?.col1?.split('_')[0] || null;

  // Case 1: PDB structure → call structureMap API
  if (!isAlphaFold) {
    const validRows = tableData
      .map((node, originalIndex) => {
        const parts = node?.col1?.split('_');
        const val = parts?.length > 1 ? parseInt(parts[1], 10) : null;
        return Number.isInteger(val) ? { originalIndex, pos: val } : null;
      })
      .filter(Boolean);

    const positionsToMap = validRows
      .filter(r => !(typeof r.col1 === 'string' && /^[A-Z]{2,3}$/.test(r.col1.trim())))
      .map(r => r.pos);

    if (positionsToMap.length > 0 && proteinUid) {
      try {
        const mapResult = await structureMap(positionsToMap, formData.pdbId, proteinUid);
        if (mapResult?.mappedPositions) {
          const mappedByOriginalIndex = new Map();
          validRows.forEach((r, mappedIndex) => {
            mappedByOriginalIndex.set(r.originalIndex, {
              col1: mapResult.mappedPositions[mappedIndex]?.toString() ?? null,
              chain: mapResult.chains?.[mappedIndex] ?? 'A',
            });
          });

          return tableData.map((row, index) => {
            const mapped = mappedByOriginalIndex.get(index);
            if (!mapped || mapped.col1 === null) {
              // Ligando ou linha sem posição válida — mantém col1 original, sem chain
              return { ...row, chain: row.chain ?? null };
            }
            return { ...row, col1: mapped.col1, chain: mapped.chain };
          });
        }
      } catch (mapErr) {
        console.warn('Level 2 mapping error:', mapErr);
      }
    }
    return tableData;
  }

  // Case 2: AlphaFold structure
  const hyphenCount = (formData.pdbId.match(/-/g) || []).length;

  // Simple AF ID (e.g. "AF-P17612") → chain A
  if (hyphenCount === 1) {
    // mapa_alphafold complexes are single-hyphen stems but ARE complexes
    const chainSingle = /^AF-\d{16}$/i.test(formData.pdbId)
      ? resolveAlphaFoldChain(formData.pdbId, proteinUid)
      : 'A';
    return tableData.map((row) => ({
      ...row,
      col1: extractPosition(row.col1),
      chain: chainSingle,
    }));
  }

  // Complex AF ID (e.g. "AF-P17612-F1") → resolve chain from mapping
  if (hyphenCount >= 2) {
    const targetChain = resolveAlphaFoldChain(formData.pdbId, proteinUid);
    return tableData.map((row) => ({
      ...row,
      col1: extractPosition(row.col1),
      chain: targetChain,
    }));
  }

  return tableData;
};

// --- UTILITY: extract numeric position from "PROTEIN_123" format ---
const extractPosition = (col1) => {
  if (!col1) return col1;
  const parts = col1.split('_');
  return parts.length > 1 ? parseInt(parts[1], 10).toString() : col1;
};

// mapa_alphafold: uniprot -> [{ url, file, chain, partner, type }]. Complex
// stems here are single-hyphen (AF-<16 digits>), distinct from complexMapping.
const multimerMap = multimerData.default || multimerData;
const stemOf = (f) => (f || '').replace(/-model_v\d+\.cif$/, '');

const geneToAccession = (value) => {
  if (!value) return value;
  const found = geneMapData.find(g => g.GENE_SYMBOL === String(value).toUpperCase());
  return found ? found.PROTEIN_AN : value;
};

// --- UTILITY: resolve AlphaFold chain from complex mapping ---
const resolveAlphaFoldChain = (pdbId, proteinUid) => {
  const acc = geneToAccession(proteinUid);
  // mapa_alphafold complexes (AF-<16 digits> stem)
  if (/^AF-\d{16}$/i.test(pdbId) && acc && Array.isArray(multimerMap[acc])) {
    const rec = multimerMap[acc].find(r => stemOf(r.file).toUpperCase() === String(pdbId).toUpperCase());
    console.log(rec)
    if (rec && rec.chain) return rec.chain;
  }
  const fileKey = pdbId.toUpperCase() + '.cif';
  const fileChains = complexMapping[fileKey];

  if (fileChains && acc) {
    const foundChain = Object.keys(fileChains).find(
      (key) => fileChains[key] === acc
    );
    if (foundChain) return foundChain;
  }

  return 'A';
};

const mapLevel3MolstarData = async (tableData) => {
  if (!tableData || tableData.length === 0) return tableData;

  // 1) Explode: one item per (original row, structure in col6_list)
  const exploded = tableData.flatMap((row, idx) => {
    const structs = row.col6_list || (row.col6 ? [row.col6] : []);
    return structs.map((s) => ({
      ...row,
      _origIndex: idx,
      _structure: s,
    }));
  });

  // 2) Group exploded rows by structure to batch structureMap calls
  const byStructure = exploded.reduce((acc, r) => {
    if (!acc[r._structure]) acc[r._structure] = [];
    acc[r._structure].push(r);
    return acc;
  }, {});

  const inferProteinUid = (rows) => {
    for (const row of rows) {
      const val = row?.col1?.trim();
      if (typeof val === 'string' && val.includes('_')) {
        return val.split('_')[0];
      }
    }
    return null;
  };

  await Promise.all(Object.entries(byStructure).map(async ([structureId, group]) => {
    const isAlphaFold = structureId.toUpperCase().startsWith('AF-');
    const proteinUid  = inferProteinUid(group);
    if (!proteinUid) return;

    if (isAlphaFold) {
      const hyphenCount = (structureId.match(/-/g) || []).length;
      const targetChain = (hyphenCount >= 2 || /^AF-\d{16}$/i.test(structureId))
        ? resolveAlphaFoldChain(structureId, proteinUid)
        : 'A';
      group.forEach((row) => {
        row.col1_mapped = extractPosition(row.col1);
        row.chain       = targetChain;
        row.col6        = structureId;
      });
      return;
    }

    const positionsAll = group.map((row) => {
      const parts = row?.col1?.split('_');
      const p = parts?.length > 1 ? parseInt(parts[1], 10) : null;
      return Number.isInteger(p) ? p : null;
    });
    const validPositions = positionsAll.filter((p) => p != null);

    if (validPositions.length === 0) return;

    try {
      // Map by position, not by index. The row list repeats positions (many
      // variants per residue) while the API deduplicates and answers
      // chain-major, so the two lists never line up: reading by index pinned
      // one residue's coordinates — and therefore its enrichment colour — onto
      // another residue.
      const byPos = await mapPositionsAllChains(validPositions, structureId, proteinUid);
      group.forEach((row, i) => {
        const p = positionsAll[i];
        const m = p != null ? byPos[p] : null;
        if (!m) return;
        row.col1_mapped = m.mapped;
        row.chains      = m.chains;          // every chain holding this residue
        row.chain       = m.chains[0] || 'A';
        row.col6        = structureId;
      });
    } catch (err) {
      console.warn(`Level 3 mapping error for ${structureId}:`, err);
    }
  }));

  return exploded;
};

// --- LEVEL 11 MOLSTAR MAPPING ---
// Pre-maps every structure's residues for the QUERY protein the same way
// Levels 2/3 do (structureMap resolves PDB auth_seq_id + chain; AF uses
// UniProt numbering + resolved chain). Returns a per-structure lookup so the
// viewer never hard-codes chain/position.
const mapLevel11MolstarData = async (tableData) => {
  const metaRow = (tableData || []).find(r => r.kind === 'meta') || {};
  const targetAcc = metaRow.target_acc;
  const scope = metaRow.scope || 'inter';
  if (!targetAcc) return { scope, targetAcc, lookup: {} };

  const pnum = (p) => { const t = String(p ?? '').split('_').pop(); const n = parseInt(t, 10); return (String(n) === t) ? n : null; };
  const byStruct = {}; // structureId -> Set(uniprot pos)
  const add = (structs, pos) => { if (pos == null) return; (structs || []).forEach(st => { if (!st) return; (byStruct[st] = byStruct[st] || new Set()).add(pos); }); };

  if (scope === 'inter') {
    tableData.filter(r => r.kind === 'pair').forEach(r => add([r.structure].filter(Boolean), pnum(r.res1)));
  } else {
    tableData.filter(r => r.kind === 'edge').forEach(r => { add(r.structures || [], pnum(r.res1)); add(r.structures || [], pnum(r.res2)); });
  }

  const lookup = {};
  await Promise.all(Object.entries(byStruct).map(async ([structureId, posSet]) => {
    const positions = [...posSet];
    lookup[structureId] = {};
    const isAF = structureId.toUpperCase().startsWith('AF-');
    if (isAF) {
      const hyphenCount = (structureId.match(/-/g) || []).length;
      const chain = (hyphenCount >= 2 || /^AF-\d{16}$/i.test(structureId)) ? resolveAlphaFoldChain(structureId, targetAcc) : 'A';
      positions.forEach(p => { lookup[structureId][p] = { mapped: String(p), chain }; });
      return;
    }
    try {
      const mr = await structureMap(positions, structureId, targetAcc);
      if (mr?.mappedPositions) {
        positions.forEach((p, i) => { const mp = mr.mappedPositions[i]; if (mp != null) lookup[structureId][p] = { mapped: String(mp), chain: mr.chains?.[i] ?? 'A' }; });
      }
    } catch (e) { console.warn('Level 11 mapping error', structureId, e); }
  }));

  // INTER: also map the PARTNER residues (res2) per (structure, partner_acc)
  // so the viewer can highlight them in yellow with their own chain.
  const partnerLookup = {}; // structureId -> partnerAcc -> { uniprotPos: {mapped, chain} }
  if (scope === 'inter') {
    const partnerByStruct = {}; // `${structure}||${partnerAcc}` -> Set(pos)
    tableData.filter(r => r.kind === 'pair').forEach(r => {
      const p = pnum(r.res2);
      if (p == null || !r.structure || !r.partner_acc) return;
      const key = `${r.structure}||${r.partner_acc}`;
      (partnerByStruct[key] = partnerByStruct[key] || new Set()).add(p);
    });
    await Promise.all(Object.entries(partnerByStruct).map(async ([key, posSet]) => {
      const [structureId, partnerAcc] = key.split('||');
      const positions = [...posSet];
      if (!partnerLookup[structureId]) partnerLookup[structureId] = {};
      partnerLookup[structureId][partnerAcc] = {};
      const isAF = structureId.toUpperCase().startsWith('AF-');
      if (isAF) {
        const hyphenCount = (structureId.match(/-/g) || []).length;
        const chain = (hyphenCount >= 2 || /^AF-\d{16}$/i.test(structureId)) ? resolveAlphaFoldChain(structureId, partnerAcc) : 'A';
        positions.forEach(p => { partnerLookup[structureId][partnerAcc][p] = { mapped: String(p), chain }; });
        return;
      }
      try {
        const mr = await structureMap(positions, structureId, partnerAcc);
        if (mr?.mappedPositions) {
          positions.forEach((p, i) => { const mp = mr.mappedPositions[i]; if (mp != null) partnerLookup[structureId][partnerAcc][p] = { mapped: String(mp), chain: mr.chains?.[i] ?? 'A' }; });
        }
      } catch (e) { console.warn('Level 11 partner mapping error', structureId, partnerAcc, e); }
    }));
  }

  return { scope, targetAcc, lookup, partnerLookup };
};

const mapLevel12MolstarData = async (tableData) => {
  const metaRow = (tableData || []).find(r => r.kind === 'meta') || {};
  const targetAcc = metaRow.target_acc;
  const structureId = metaRow.structure;
  if (!targetAcc || !structureId) return { targetAcc, structureId: null, lookup: {} };
  const pnum = (x) => { const t = String(x ?? '').split('_').pop(); const n = parseInt(t, 10); return (String(n) === t) ? n : null; };
  const posSet = new Set();
  (tableData || []).filter(r => r.kind === 'vus').forEach(r => { const q = pnum(r.position); if (q != null) posSet.add(q); });
  const positions = [...posSet];
  const lookup = { [structureId]: {} };
  const isAF = structureId.toUpperCase().startsWith('AF-');
  if (isAF) {
    const hyphenCount = (structureId.match(/-/g) || []).length;
    const chain = (hyphenCount >= 2 || /^AF-\d{16}$/i.test(structureId)) ? resolveAlphaFoldChain(structureId, targetAcc) : 'A';
    positions.forEach(q => { lookup[structureId][q] = { mapped: String(q), chain }; });
  } else {
    try {
      const mr = await structureMap(positions, structureId, targetAcc);
      if (mr?.mappedPositions) {
        positions.forEach((q, i) => { const mp = mr.mappedPositions[i]; if (mp != null) lookup[structureId][q] = { mapped: String(mp), chain: mr.chains?.[i] ?? 'A' }; });
      }
    } catch (e) { console.warn('Level 12 mapping error', structureId, e); }
  }
  return { targetAcc, structureId, lookup };
};

const mapLevel13MolstarData = async (tableData, stats) => {
  // L13 rows have no 'kind'; the anchor position is col1 (a bare number).
  // structure + protein come from enrichmentStats (Level13Stats).
  const structureId = stats?.structure || null;
  const targetAcc = stats?.proteinA || null;
  if (!targetAcc || !structureId) return { targetAcc, structureId: null, lookup: {} };
  const posSet = new Set();
  (tableData || []).forEach(r => { const q = parseInt(String(r.col1), 10); if (!isNaN(q)) posSet.add(q); });
  const positions = [...posSet];
  const lookup = { [structureId]: {} };
  const isAF = structureId.toUpperCase().startsWith('AF-');
  if (isAF) {
    const hyphenCount = (structureId.match(/-/g) || []).length;
    const chain = (hyphenCount >= 2 || /^AF-\d{16}$/i.test(structureId)) ? resolveAlphaFoldChain(structureId, targetAcc) : 'A';
    positions.forEach(q => { lookup[structureId][q] = { mapped: String(q), chain }; });
  } else {
    try {
      const mr = await structureMap(positions, structureId, targetAcc);
      if (mr?.mappedPositions) {
        positions.forEach((q, i) => { const mp = mr.mappedPositions[i]; if (mp != null) lookup[structureId][q] = { mapped: String(mp), chain: mr.chains?.[i] ?? 'A' }; });
      }
    } catch (e) { console.warn('Level 13 mapping error', structureId, e); }
  }
  return { targetAcc, structureId, lookup };
};

const LEVEL3_CATEGORY_COLOR = {
  pathogenic:        '#ef4444',
  conflicting:       '#06b6d4',
  vus_am_pathogenic: '#f97316',
  uncertain:         '#9ca3af',
  benign:            '#22c55e',
};


const variantPathogenicityColor = (p) => {
  const s = String(p || '').toLowerCase().trim();
  if (s === 'pathogenic'
      || s === 'likely_pathogenic'
      || s === 'pathogenic/likely_pathogenic')                  return LEVEL3_CATEGORY_COLOR.pathogenic;
  if (s === 'conflicting_classifications_of_pathogenicity')     return LEVEL3_CATEGORY_COLOR.conflicting;
  if (s === 'uncertain_significance')                           return LEVEL3_CATEGORY_COLOR.uncertain;
  if (s === 'likely_benign' || s === 'benign')                  return LEVEL3_CATEGORY_COLOR.benign;
  return LEVEL3_CATEGORY_COLOR.uncertain;
};

const formatVariantLabel = (v) => {
  const aa = v?.aa_change || '';
  const pos = v?.protein_position || '';
  if (aa.includes('/')) {
    const [ref, alt] = aa.split('/');
    return `${ref}${pos}${alt}`;
  }
  return `${aa}${pos}`;
};

// Format a p-value for display (compact)
const formatPValue = (p) => {
  if (p == null) return 'n/a';
  return p < 0.001 ? p.toExponential(1) : p.toFixed(3);
};



  // --- RENDERERS ---
  const customizeMarker = (item, index) => {
    if (index === 0) return <i className="pi pi-circle-fill text-danger" style={{ fontSize: '1.2rem' }}></i>;
    if (result && index === result.path.length - 1) return <i className="pi pi-check-circle text-success" style={{ fontSize: '1.2rem' }}></i>;
    return <i className="pi pi-circle text-secondary" style={{ fontSize: '0.8rem' }}></i>;
  };

  const customizeContent = (item, index) => {
    const annotations = item.annotations || {};

    const isAttractor = (
      result?.pathMode &&
      result.pathMode !== 'geometric' &&
      annotations.is_functional === true
    );

    return (
      <div
        style={{
          borderLeft: isAttractor ? '4px solid #534AB7' : '4px solid transparent',
          paddingLeft: '8px',
          transition: 'border-color 0.2s',
          backgroundColor: isAttractor ? 'rgba(83, 74, 183, 0.04)' : 'transparent',
          borderRadius: '4px'
        }}
      >
        <Card className="mb-2 shadow-sm border-0" style={{ padding: '0.5rem' }}>
          <div className="d-flex flex-column">
            <span className="fw-bold text-dark mb-1">{item.name}</span>
            <div className="flex gap-2">
              {annotations.has_clinvar && (
                <><Tooltip target=".has_clinvar" /><i className="has_clinvar pi pi-shield p-1" style={{ color: '#708090' }} data-pr-tooltip="Associated with ClinVar Variant"></i></>
              )}
              {annotations.has_intra_contact && (
                <><Tooltip target=".has_intra_contact" /><i className="has_intra_contact pi pi-globe p-1" style={{ color: '#708090' }} data-pr-tooltip="Has Intra-protein Contact (PDB)"></i></>
              )}
              {annotations.has_af_intra_contact && (
                <><Tooltip target=".has_af_intra_contact" /><i className="has_af_intra_contact pi pi-star p-1" style={{ color: '#708090' }} data-pr-tooltip="Has Intra-protein Contact (AlphaFold)"></i></>
              )}
              {annotations.has_inter_contact && (
                <><Tooltip target=".has_inter_contact" /><i className="has_inter_contact pi pi-sitemap p-1" style={{ color: '#708090' }} data-pr-tooltip="Has Inter-protein Interaction (PDB)"></i></>
              )}
              {annotations.has_af_inter_contact && (
                <><Tooltip target=".has_af_inter_contact" /><i className="has_af_inter_contact pi pi-star-fill p-1" style={{ color: '#708090' }} data-pr-tooltip="Has Inter-protein Interaction (AlphaFold)"></i></>
              )}
              {annotations.has_ligand && (
                <><Tooltip target=".has_ligand" /><i className="has_ligand pi pi-ethereum p-1" style={{ color: '#708090' }} data-pr-tooltip="Binds to a Ligand"></i></>
              )}
              {annotations.is_functional && (
                  <><Tooltip target=".is_functional" /><i className="is_functional pi pi-cog p-1" style={{ color: '#708090' }} data-pr-tooltip="Functional Significance (PTM/Domain/Site/Ligand)"></i></>
              )}
              {annotations.has_site && (
                  <><Tooltip target=".has_site" /><i className="has_site pi pi-bullseye" style={{ color: '#4b2854' }} data-pr-tooltip="Has Active site/Site of interest "></i></>
              )}
            </div>
          </div>
        </Card>
      </div>
    );
  };

  const getStatusColor = (val) => {
      if (!val) return 'info';
      if (val.includes('Hub') || val.includes('Pathogenic') || val.includes('Strong')) return 'danger';
      if (val.includes('Core') || val.includes('Active')) return 'success';
      if (val.includes('Medium') || val.includes('VUS')) return 'warning';
      return 'info';
  };

  const renderDensityPlot = () => {
      if (!result || !result.data || result.data.length === 0) return null;

      let xData = [];
      let yData = [];
      let xLabel = "";

      // Level 8: Domain-PTM Enrichment (col1=Name, col4=Density%)
      if (mode === 'level8') {
          xData = result.data.map(r => r.col1);
          yData = result.data.map(r => parseFloat(r.col4.replace('%', '')));
          xLabel = "Domain";
      }

      else if (mode === 'level7') {
          xData = result.data.map(r => r.col1);
          yData = result.data.map(r => parseFloat(r.col3));
          xLabel = "Hotspot Center";
      }

      else if (mode === 'level12') {
          return null;
      } else {
          return null;
      }

      return (
        <Card className="mb-4 shadow-sm">
            <div className="d-flex align-items-center mb-3">
                <BarChart2 className="me-2 text-primary" />
                <h5 className="mb-0 text-primary">Density Distribution</h5>
            </div>
            <Plot
                data={[
                {
                    x: xData,
                    y: yData,
                    type: 'bar',
                    marker: { color: '#3B82F6' },
                },
                ]}
                layout={{ autosize: true,
                    title: 'Density / Enrichment Analysis',
                    xaxis: { title: xLabel, tickangle: -45,  automargin: true },
                    yaxis: { title: 'Density / Score',  automargin: true },
                    margin: { b: 100 }
                }}
                useResizeHandler={true}
                style={{ width: "100%", height: "400px" }}
                config={{ displayModeBar: 'hover', displaylogo: false, responsive: true }}
            />
        </Card>
      );
  };

  const loadExample = () => {


  // --- FORM VALIDATION FIXED ---
    // Level 1: Start + End + PDB
    if (mode === 'level1')  {
        setFormData({
            startResidue: 'UBA6_625',
            endResidue: 'UBA6_307',
            pdbId: '7pvn',
            pathMode: 'geometric',
            contactScope: 'intra',
            addLigand: true,
           });
    }

    if (mode === 'level2' )  {
        setFormData({
          proteinA: 'PRKACA',
            pdbId: 'AF-P17612',
            spmPercentile: 10,
            contactScope: 'intra',
            addLigand: false,
           });
    }

    if ( mode === 'level5')  {
        setFormData({
         proteinA: 'A0AVT1',
            pdbId: '7pvn',
            afId:''

           });
    }

    if (mode === 'level3') {
        setFormData({
            proteinA: 'GNAO1',
            proteinB: 'ADCY1',
            pdbId: 'AF-P09471-Q08828'
           });
    }

    if (mode === 'level7' ) {
        setFormData({
            proteinA: 'TP53'
           });
    }

     if (mode === 'level9') {
        setFormData({
            proteinA: 'RAF1',
            pdbId: 'AF-P04049'
           });
    }

    if (mode === 'level8') {
        setFormData({
            proteinA: 'Q8WZ42'
           });
    }

if (mode === 'level6') {
        setFormData({
            proteinA: 'GAPDH'
           });
    }
    if (mode === 'level4') {
        setFormData({
            proteinA: 'IDS',
            pdbId: '5fql'
           });
    }

    if (mode === 'level10') {
        setFormData({
            proteinA: 'KRAS'
           });
    }

    if (mode === 'level11') {
      setFormData({
            proteinA: 'BRCA1',
            contactScope: 'inter',
           });
    }

    if (mode === 'level12') {
        setFormData({
            proteinA: 'RAF1',
            pdbId: '6xgu'
           });
    }

    if (mode === 'level13') {
        setFormData({
            proteinA: 'P17612',
            variants: 'G201S,K214W,E171G,W197C,H14Q',
            pdbId: '3ama'
           });
    }

    if (mode === 'level14') {
        setFormData({
            proteinA: 'PRKACA',
            pdbId: '3ama',
            pdbId2: '4wb5'
           });
    }

};

  return (
    <>
      <Helmet><title>Atlantis | Pathfinder</title></Helmet>
      <Header />

      <section className="bg-light min-vh-100 px-5 py-10">
        <div className="container">
          <div className="row mb-5 text-center">
            <div className="col-12">
              <h1 className="display-5 fw-bold text-primary mb-3">
                <Activity className="me-3" size={40} />
                 Pathfinder - Structural Graph Analysis
                 <sup>
                    <a href="/help/#pathfinder" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                        <Button
                            icon="pi pi-info"
                            text
                            rounded
                            size="small"
                            className="btn-dialog"
                            tooltip="Learn Pathfinder"
                        />
                    </a>
                  </sup>
              </h1>
              <p className="lead text-500">Select an analysis level to probe protein structure, allosteric pathways, and hotspots.</p>
            </div>
          </div>

          <div className="row">
            {/* --- INPUT COLUMN --- */}
            <div className="col-lg-4">
              <Card className="shadow-sm h-100">
                <form onSubmit={handleCalculate} className="d-flex flex-column gap-3">

                  {/* Mode Selector */}
                  <div className="d-flex flex-column">
                    <label className="fw-bold small text-uppercase text-secondary mb-1">Analysis Mode</label>
                    <Dropdown
                        value={mode}
                        options={groupedAnalysisModes}
                        optionLabel="label"
                        optionValue="value"
                        optionGroupLabel="label"
                        optionGroupChildren="items"
                        onChange={(e) => {setMode(e.value); setResult(null); setError(null); setFormData({
                                    startResidue: '',
                                    endResidue: '',
                                    proteinId: '',
                                    proteinA: '',
                                    proteinB: '',
                                    pdbId: '',
                                    pdbId2: '',
                                    afId: '',
                                    distance: ''
                                  })
                                }}
                        placeholder="Select Analysis Mode"
                        className="w-100"
                    />
                  </div>
                  <Divider />

                {/* Level 1: Physical Path */}
                 {mode === 'level1' && (
   <>
     <div className="d-flex flex-column"><label className="fw-bold small text-secondary">Source</label><InputText name="startResidue" value={formData.startResidue} onChange={handleChange} placeholder="e.g. PRKACA_2/P17612_2" /></div>
     <div className="d-flex flex-column"><label className="fw-bold small text-secondary">Target</label><InputText name="endResidue" value={formData.endResidue} onChange={handleChange} placeholder="e.g. PRKACA_351" /></div>
     <div className="d-flex flex-column"><label className="fw-bold small text-secondary">Structure Identifier</label><InputText name="pdbId" value={formData.pdbId} onChange={handleChange} placeholder="e.g. 3ama/AF-P17612" /></div>

     {/* ============ NOVO: Path Mode Selector ============ */}
     <div className="d-flex flex-column">
       <label className="fw-bold small text-secondary mb-2">Path Weighting</label>
       <div className="d-flex gap-2" role="group" aria-label="Path weighting mode">
         <input
           type="radio"
           className="btn-check"
           name="pathMode"
           id="pathMode-geometric"
           value="geometric"
           checked={formData.pathMode === 'geometric'}
           onChange={handleChange}
         />
         <label className="btn btn-outline-secondary btn-sm" htmlFor="pathMode-geometric">
           Geometric
         </label>

         <input
           type="radio"
           className="btn-check"
           name="pathMode"
           id="pathMode-functional"
           value="functional"
           checked={formData.pathMode === 'functional'}
           onChange={handleChange}
         />
         <label className="btn btn-outline-secondary btn-sm" htmlFor="pathMode-functional">
           Functional
         </label>
       </div>
       <small className="mt-1" style={{fontSize: '0.75rem'}}>
         {formData.pathMode === 'geometric' && ''}
         {formData.pathMode === 'functional' && ''}
       </small>
     </div>

     {/* ============ Contact Scope + Add Ligand ============ */}
     <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap">
       <div className="d-flex flex-column">
         <label className="fw-bold small text-secondary mb-2">Contact scope</label>
         <SelectButton
           value={formData.contactScope}
           onChange={(e) =>
             e.value && setFormData((prev) => ({ ...prev, contactScope: e.value }))
           }
           options={[
             { label: 'Intra', value: 'intra' },
             { label: 'Inter', value: 'inter' },
             { label: 'Both',  value: 'both'  },
           ]}
         />
       </div>

       <div className="d-flex flex-column">
         <label className="fw-bold small text-secondary mb-2">Ligand</label>
         <SelectButton
           value={formData.addLigand}
           onChange={(e) =>
             e.value !== null && setFormData((prev) => ({ ...prev, addLigand: e.value }))
           }
           options={[
             { label: 'Off', value: false },
             { label: 'On',  value: true  },
           ]}
         />
       </div>
     </div>

     <div className="mt-2 p-3 bg-light border text-justify rounded small">
       <h6 className="fw-bold text-secondary mb-2"><Activity size={14} className="me-1"/>Methodology:</h6>
       <span className="text-500">
          Calculates the shortest weighted physical path (Dijkstra) between two endpoints (residue or ligand), using an all-atom <strong>Euclidean distance threshold of 8 &Aring;</strong> (C&beta;; C&alpha; for Glycine) as edge weights.
          {formData.pathMode !== 'geometric' && (
            <> In <strong>{formData.pathMode}</strong> mode, edges adjacent to functionally annotated residues (PTMs, active sites, ligand contacts, ClinVar variants) receive reduced cost.</>
          )}
           <br/>
           <strong> Contact scope:</strong> chooses the search network — <em>Intra</em> (single-chain folding), <em>Inter</em> (cross-chain interfaces, enabling paths between subunits), or <em>Both</em>.
           <br/>
           <strong> Ligand:</strong> includes/remove ligand contacts and ligand nodes, enabling residue–ligand and ligand–ligand routes.
          {' '}
        </span>
     </div>
   </>
 )}

 {/* Level 2: Structural Hubs */}
{mode === 'level2' && (
   <>
     <div className="mb-2">
       <label className="fw-bold small text-secondary">Gene Symbol/Protein Accession</label>
       <InputText name="proteinA" value={formData.proteinA} onChange={handleChange} placeholder="e.g. PRKACA/P17612" className="w-100 mb-2"/>
       <div className="d-flex flex-column mb-2"><label className="fw-bold small text-secondary">Identifier</label><InputText name="pdbId" value={formData.pdbId} onChange={handleChange} placeholder="e.g. 3ama" /></div>
      <label className="fw-bold small text-secondary"><span>Top-N% </span> </label>
       <Dropdown className="w-100"  value={spmPercentile} onChange={(e) => {
             setSpmPercentile(e.value);
             setResult(null);
             setError(null);
             setEgoResidue(null);
             setEgoData(null);
             setPositions([]);
             setChain(undefined);
       }}
             options={[
                   { label: 'Top 5%', value: 5 },
                   { label: 'Top 10%', value: 10 },
                   { label: 'Top 20%', value: 20 },
                   { label: 'Top 30%', value: 30 },
                   ]}
                  placeholder="Choose a cutoff"
             />

     </div>

     {/* ============ Contact Scope + Add Ligand ============ */}
     <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap">
       <div className="d-flex flex-column">
         <label className="fw-bold small text-secondary mb-2">Contact scope</label>
         <SelectButton
           value={formData.contactScope}
           onChange={(e) =>
             e.value && setFormData((prev) => ({ ...prev, contactScope: e.value }))
           }
           options={[
             { label: 'Intra', value: 'intra' },
             { label: 'Inter', value: 'inter' },
             { label: 'Both',  value: 'both'  },
           ]}
         />
       </div>

       <div className="d-flex flex-column">
         <label className="fw-bold small text-secondary mb-2">Ligand</label>
         <SelectButton
           value={formData.addLigand}
           onChange={(e) =>
             e.value !== null && setFormData((prev) => ({ ...prev, addLigand: e.value }))
           }
           options={[
             { label: 'Off', value: false },
             { label: 'On',  value: true  },
           ]}
         />
       </div>
     </div>
    <div className="mt-3 p-3 bg-light border rounded small">
        <h6 className="fw-bold text-secondary mb-2"><Activity size={14} className="me-1"/>Methodology:</h6>
       <span className="text-500 d-block mb-2">
           Computes <strong>Degree</strong> (high-valency hubs) and <strong>Betweenness Centrality</strong> (bottlenecks) to identify evolutionarily conserved structural cores within the 3D contact network.
           <br/>
           <strong> Contact scope:</strong> chooses the search network — <em>Intra</em> (single-chain folding), <em>Inter</em> (cross-chain interfaces, enabling paths between subunits), or <em>Both</em>.
           <br/>
           <strong> Ligand:</strong> includes/remove ligand contacts and ligand nodes.
       </span>
     </div>
   </>
 )}

 {/* Level 3: PPI Hotspots */}
 {mode === 'level3' && (
   <>
     <div className="d-flex flex-column"><label className="fw-bold small text-secondary">Gene Symbol A/Protein A</label><InputText name="proteinA" value={formData.proteinA} onChange={handleChange} placeholder="e.g. LRRK2/Q5S007" /></div>
     <div className="d-flex flex-column"><label className="fw-bold small text-secondary">Gene Symbol B/Protein B</label><InputText name="proteinB" value={formData.proteinB} onChange={handleChange} placeholder="e.g. RAB12/Q6IQ22" /></div>
      <div className="d-flex flex-column mb-2"><label className="fw-bold small text-secondary">Identifier</label>
       <InputText name="pdbId" value={formData.pdbId} onChange={handleChange} placeholder="e.g. 1ca2" />
     </div>
     <div className="mt-3 p-3 bg-light border rounded small">
       <h6 className="fw-bold text-secondary mb-2"><Activity size={14} className="me-1"/>Methodology:</h6>
       <span className="text-500">Identify residues at the protein–protein interface and characterise them across three complementary dimensions: pathogenic variant burden, 3D neighbourhood density, and interaction promiscuity.</span>
     </div>
   </>
 )}

{/* Level 4: Ligand-Variant Overlap */}
 {mode === 'level4' && (
   <>
     <div className="d-flex flex-column">
       <label className="fw-bold small text-secondary">Gene Symbol/Protein Accession</label>
       <InputText name="proteinA" value={formData.proteinA} onChange={handleChange} placeholder="e.g. CA2"  />
     </div>
     <div className="d-flex flex-column mb-2"><label className="fw-bold small text-secondary">Identifier</label>
       <InputText name="pdbId" value={formData.pdbId} onChange={handleChange} placeholder="e.g. 1ca2" />
     </div>

    <div className="mt-3 p-3 bg-light border rounded test-justify small">
                       <h6 className="fw-bold text-secondary  mb-2"><Activity size={14} className="me-1"/>Methodology:</h6>
                      <span className="text-500">
                           Identifies variants on UniProt-annotated binding and active
                         sites and on their <strong>1st-shell allosteric neighbours</strong>
                         (&le;&nbsp;8&nbsp;Å) within the structure you pin above.
                         Binding sites come from UniProt-curated
                         annotations (ligands and cofactors). Active sites use UniProt
                         catalytic annotations (label <em>"Active site"</em> or catalytic
                         intermediates).
                        </span>
                        </div>
   </>
 )}

 {/* Level 5: Structure Differences */}
 {mode === 'level5' && (
   <>
    <div className="d-flex flex-column">
       <label className="fw-bold small text-secondary">Gene Symbol/Protein Accession</label>
       <InputText name="proteinA" value={formData.proteinA} onChange={handleChange} placeholder="e.g. PRKACA/P17612" className="w-100 mb-2"/>
    </div>

    <div className="d-flex flex-column"><label className="fw-bold small text-secondary">PDB ID</label><InputText name="pdbId" value={formData.pdbId} onChange={handleChange} placeholder="e.g. 3l9l" /></div>
    <div className="d-flex flex-column mt-2">
       <label className="fw-bold small text-secondary">
         AlphaFold model <span className="fw-normal">(optional)</span>
       </label>
       <InputText name="afId" value={formData.afId} onChange={handleChange} placeholder="Monomer (default)" />
       <small className="text-secondary small mt-1">
         Leave empty to compare against the AlphaFold monomer. Give a multimer id
         (e.g. AF-P07550-P63092/ AF-0000000210698901) to compare against a specific
         predicted complex.
       </small>
    </div>
    <div className="mt-2 p-3 bg-light border rounded small">
       <h6 className="fw-bold text-secondary mb-2"><Activity size={14} className="me-1"/>Methodology:</h6>
       <span className="text-500">Evaluates topological consensus by comparing intra-contacts and pLDDT scores between experimental Crystal (PDB) and predictive AlphaFold models.</span>
     </div>
   </>
 )}

 {/* Level 14: Conformational Differences (PDB vs PDB) */}
 {mode === 'level14' && (
   <>
     <div className="d-flex flex-column">
        <label className="fw-bold small text-secondary">Gene Symbol/Protein Accession</label>
        <InputText name="proteinA" value={formData.proteinA} onChange={handleChange} placeholder="e.g. PRKACA/P17612" className="w-100 mb-2"/>
     </div>
     <div className="d-flex flex-column"><label className="fw-bold small text-secondary">PDB ID &mdash; structure A</label><InputText name="pdbId" value={formData.pdbId} onChange={handleChange} placeholder="e.g. 3ama" /></div>
     <div className="d-flex flex-column mt-2"><label className="fw-bold small text-secondary">PDB ID &mdash; structure B</label><InputText name="pdbId2" value={formData.pdbId2} onChange={handleChange} placeholder="e.g. 4wb5" /></div>
     <div className="mt-3 p-3 bg-light border rounded small">
        <h6 className="fw-bold text-secondary mb-2"><Activity size={14} className="me-1"/>Methodology:</h6>
        <span className="text-500">Compares the intra-contact topology of <strong>two experimental structures</strong> of the same protein. Unlike the PDB-vs-AlphaFold comparison, both sides are ground truth, so contact rewiring reflects genuine <strong>conformational change</strong> between states (e.g. apo/holo, active/inactive) rather than prediction error. Only residues resolved in <strong>both</strong> structures are compared.</span>
      </div>
    </>
 )}


 {/* Level 6: 3D PTM Crosstalk */}
 {mode === 'level6' && (
   <>
     <div className="d-flex flex-column"><label className="fw-bold small text-secondary">Gene Symbol/Protein Accession</label><InputText name="proteinA" value={formData.proteinA} onChange={handleChange} placeholder="e.g. TP53/P04637" /></div>
     <div className="mt-3 p-3 bg-light border rounded small">
       <h6 className="fw-bold text-secondary mb-2"><Activity size={14} className="me-1"/>Methodology:</h6>
       <span className="text-500">Maps distinct PTMs sharing spatial proximity (≤ 8.0 Å) despite linear separation, revealing 3D network-motif cliques that act as macromolecular Logic Gates (AND/OR) for allosteric crosstalk.</span>
     </div>
   </>
 )}

 {/* Level 7: PTM Hotspots */}
 {mode === 'level7' && (
   <>
     <div className="d-flex flex-column">
         <label className="fw-bold small text-secondary">Gene Symbol/Protein Accession</label>
         <InputText name="proteinA" value={formData.proteinA} onChange={handleChange} placeholder="e.g. TP53/P04637" />
     </div>
     <div className="mt-3 p-3 bg-light border rounded small">
       <h6 className="fw-bold text-secondary mb-2"><Activity size={14} className="me-1"/>Methodology:</h6>
       <p className="text-500 mb-2">
           Identifies dense regulatory hotspots using a 31-residue sliding window, validated via a 1000× permutation null and Benjamini-Hochberg/Permutation FDR correction.
       </p>
      </div>
   </>
 )}

 {/* Level 8: Domain-PTM Enrichment */}
 {mode === 'level8' && (
   <>
     <div className="d-flex flex-column"><label className="fw-bold small text-secondary">Gene Symbol/Protein Accession</label><InputText name="proteinA" value={formData.proteinA} onChange={handleChange} placeholder="e.g. TP53/P04637" /></div>
     <div className="mt-3 p-3 bg-light border rounded small">
       <h6 className="fw-bold text-secondary mb-2"><Activity size={14} className="me-1"/>Methodology:</h6>
       <span className="text-500">Calculates localized Modification Propensity within domains annotated by <strong>UniProt, Pfam, and InterPro</strong>, identifying structural modules under disproportionate regulatory control.</span>
     </div>
   </>
 )}

 {/* Level 9: PTM-Variant Disruption */}
 {mode === 'level9' && (
   <>
     <div className="d-flex flex-column"><label className="fw-bold small text-secondary">Gene Symbol/Protein Accession</label><InputText name="proteinA" value={formData.proteinA} onChange={handleChange} placeholder="e.g. TP53/P04637" /></div>
        <div className="d-flex flex-column mb-2"><label className="fw-bold small text-secondary">Identifier</label>
       <InputText name="pdbId" value={formData.pdbId} onChange={handleChange} placeholder="e.g. 1ca2" />
     </div>
     <div className="mt-3 p-3 bg-light border rounded small">
       <h6 className="fw-bold text-secondary mb-2"><Activity size={14} className="me-1"/>Methodology:</h6>
       <span className="text-500">Checks if Variants <strong>directly abolish</strong> a PTM site (0.0 &Aring;) or cause Steric/Electrostatic Disruption (<strong>&lt; 8.0 &Aring;</strong>) in the 3D structure, effectively silencing a regulatory switch.</span>
     </div>
   </>
 )}

 {/* Level 10: Pan-Family Evolutionary Hotspots */}
 {mode === 'level10' && (
    <>
     <div className="d-flex flex-column"><label className="fw-bold small text-secondary">Gene Symbol/Protein Accession</label><InputText name="proteinA" value={formData.proteinA} onChange={handleChange} placeholder="e.g. TP53/P04637" /></div>

     <div className="mt-3 p-3 bg-light border rounded small">
       <h6 className="fw-bold text-secondary mb-2"><Activity size={14} className="me-1"/>Methodology:</h6>
       <span className="text-500">
          Identifies conserved HMM positions that repeatedly accumulate pathogenic variants, PTMs and functional features across homologous proteins.
       </span>
      </div>
   </>
 )}

 {/* Level 11: Structural Co-Pathogenicity */}
 {mode === 'level11' && (
      <>
        <div className="d-flex flex-column"><label className="fw-bold small text-secondary">Gene Symbol/Protein Accession</label><InputText name="proteinA" value={formData.proteinA} onChange={handleChange} placeholder="e.g. TP53/P04637" /></div>
        <div className="d-flex flex-column mt-3">
          <label className="fw-bold small text-secondary mb-2">Co-pathogenicity scope</label>
          <SelectButton
            value={formData.copathScope || 'inter'}
            onChange={(e) => e.value && setFormData((prev) => ({ ...prev, copathScope: e.value }))}
            options={[
              { label: 'Inter', value: 'inter' },
              { label: 'Intra', value: 'intra' },
            ]}
          />
        </div>
        <div className="mt-3 p-3 bg-light border rounded small">
          <h6 className="fw-bold text-secondary mb-2">
            <Activity size={14} className="me-1" />
            Methodology:
          </h6>

          <span className="text-500">
            {formData.copathScope === "inter"
              ? "Identifies pathogenic residue pairs across protein–protein interfaces."
              : "Identifies spatial clusters of pathogenic residues within the same protein."}
          </span>
        </div>
      </>
 )}
 {/* Level 12: VUS Resolver */}
 {mode === 'level12' && (
        <>
          <div className="d-flex flex-column"><label className="fw-bold small text-secondary">Gene Symbol/Protein Accession</label><InputText name="proteinA" value={formData.proteinA} onChange={handleChange} placeholder="e.g. PRKACA/P17612" /></div>
          <div className="d-flex flex-column mt-2"><label className="fw-bold small text-secondary">Identifier</label><InputText name="pdbId" value={formData.pdbId} onChange={handleChange} placeholder="e.g. 3ama / AF-P17612" /></div>

            <div className="mt-3 p-3 bg-light border rounded small">
                    <h6 className="fw-bold text-secondary mb-2">
                        <Activity size={14} className="me-1"/>Methodology:
                    </h6>
                    <span className="text-500">
                        Prioritises every ClinVar <strong>VUS</strong> (uncertain / conflicting) on the protein by its 3D structural environment within <strong>8.0 &Aring;</strong> (intra + inter), reporting two independent axes side by side: an <strong>observed</strong> axis (nearby ClinVar pathogenic residues) and a <strong>predicted</strong> axis (AlphaMissense-weighted neighbourhood density).
                    </span>
                </div>
        </>
      )}
 {mode === 'level13' && (
        <>
          <div className="d-flex flex-column"><label className="fw-bold small text-secondary">Gene Symbol/Protein Accession</label><InputText name="proteinA" value={formData.proteinA} onChange={handleChange} placeholder="e.g. PRKACA/P17612" /></div>
          <div className="d-flex flex-column mt-2"><label className="fw-bold small text-secondary">Variants</label><InputText name="variants" value={formData.variants} onChange={handleChange} placeholder="e.g. G201S,K214N,E171G,W197C,H143Q" /></div>
          <div className="d-flex flex-column mt-2"><label className="fw-bold small text-secondary">Structure Identifier <span className="fw-normal">(optional, defaults to AlphaFold)</span></label><InputText name="pdbId" value={formData.pdbId} onChange={handleChange} placeholder="e.g. 3ama / AF-P17612" /></div>

            <div className="mt-3 p-3 bg-light border rounded small">
                    <h6 className="fw-bold text-secondary mb-2">
                        <Activity size={14} className="me-1"/>Methodology:
                    </h6>
                    <span className="text-500">
                        Cross-references your variant list against everything in the graph and reports up to <strong>eight independent lines of evidence</strong> per variant — AlphaMissense (variant &amp; residue), functional site, PTM proximity, observed pathogenic neighbourhood, PPI interface, evolutionary recurrence in homologs, network dynamics and network centrality.
                    </span>
                </div>
        </>
      )}
                          <Button
                            label="Example"
                            icon="pi pi-info-circle"
                            severity="secondary"
                            className="w-100 mt-2"
                            onClick={loadExample}
                            type="button"
                        />
                  <Button label="Execute Analysis" icon="pi pi-bolt" loading={loading} className="w-100" type="submit"/>
                </form>
              </Card>
            </div>

            {/* --- RESULTS COLUMN --- */}
            <div className="col-lg-8">
              {error && <Message severity="error" text={error} className="w-100 mb-3" />}

              {!result && !loading && !error && (
                <div className="text-center p-5 text-500 border rounded bg-white h-100 d-flex flex-column justify-content-center align-items-center">
                  <Layers size={64} className="opacity-25 mb-3" />
                  <p>Analysis results will appear here.</p>
                </div>
              )}

           {/* --- RENDER: PATH/TIMELINE (Level 1 ) --- */}
            {result && result.type === 'path' && (
                <>
                    <Card className="shadow-sm animate__animated animate__fadeIn">

                        <div className="d-flex justify-content-between align-items-center mb-4">
                        <h4 className="mb-0 text-primary">Pathway Topology</h4>
                        <small className="text-capitalize">
                            {formData.pathMode} weighting
                        </small>
                        <div className="d-flex gap-2 align-items-center">
                            <Tag
                                value={`Cost: ${result.totalDistance?.toFixed(2)}`}
                                severity="warning"
                                icon="pi pi-compass"
                            />
                            <Tag
                                value={`${result.totalGeometricDistance?.toFixed(2)} Å`}
                                severity="info"
                                icon="pi pi-arrows-h"
                            />
                        </div>

                    </div>

                    {result.pathMode !== 'geometric' && (
                          <small className="d-block mb-3" style={{fontSize: '0.8rem'}}>
                            <span style={{
                              display: 'inline-block',
                              width: '3px',
                              height: '12px',
                              backgroundColor: '#534AB7',
                              verticalAlign: 'middle',
                              marginRight: '6px'
                            }}/>
                            Residues marked with a colored bar exerted attraction on the path due to their functional annotations.
                          </small>
                        )}

                        <Timeline value={result.path} align="alternate" marker={customizeMarker} content={customizeContent} />
                         <div className="p-3 border rounded bg-light mt-3 mb-3">
                        <p className="mb-2 small">
                            <strong><i className="pi pi-info-circle me-1 text-primary"></i>How to read this path:</strong>
                            </p>
                            <ul className="mb-0 small">
                                <li>
                                    <strong>Short paths (&lt;20 Å, 2–3 hops)</strong> indicate strong mechanical coupling — perturbations at one end likely propagate to the other.
                                </li>
                                <li>
                                    <strong>Long paths (&gt;40 Å)</strong> suggest weaker or indirect allosteric coupling.
                                </li>
                                <li>
                                    Intermediate residues with <i className="pi pi-shield" style={{fontSize:'0.8rem'}}></i> (ClinVar) or <i className="pi pi-cog" style={{fontSize:'0.8rem'}}></i> (functional) icons indicate the path traverses known functional sites — strengthening the biological plausibility of this allosteric connection.
                                </li>
                            </ul>
                        </div>

                        <div className="d-flex align-items-center">
                            <h5 className="mb-0 text-primary mb-4 mt-5">< Route className="me-2"/>Structure Path</h5>
                        </div>
                        <div className="viewer-box mb-2">
                            <MolstarViewerPath
                                pdbId={submittedPdbId}
                                level="1"
                                positions={positions}
                                chain={chain}
                            />
                        </div>
                         <div className="d-flex">
                          <div className="burble-contact"></div>
                          <p className="fs--2 text-justify">Contact position</p>
                        </div>
                    </Card>
                </>
            )}

              {/* --- RENDER: TABLE (Level 2 - Hubs & Centrality) --- */}
              {result && mode === 'level2' && (
                     <Card className="shadow-sm animate__animated animate__fadeIn mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                           <h4 className="text-primary m-0">
                               <Network className="me-2"/>Structural Hubs/SPM
                           </h4>
                           <Button
                               label="Download CSV"
                               icon="pi pi-download"
                               severity="secondary"
                               outlined
                               size="small"
                               onClick={downloadCSV}
                           />
                       </div>
                        {/* Context / methodology block */}
                       <div className="card card-spm bg-light border-0 p-3 mb-4 text-start">

                         <h5 className="mb-3">
                                     <i className="pi pi-info-circle me-2 text-primary" />
                                   What is Shortest Path Mapping?
                         </h5>

                                                <p className="mb-3 small">
                                                  SPM ranks every residue by <strong>degree</strong> and
                                                   <strong> betweenness centrality</strong> —
                                                  how many shortest paths in the contact graph pass through it.
                                                  The top-N% residues are then classified as{' '}
                                                  <strong className="text-warning-emphasis">SPM hubs</strong>, and the top-N% edges as{' '}
                                                  <strong className="text-primary">SPM edges</strong>.
                                                </p>

                                                <p className="mb-2">
                                                  <Tag
                                                    value="SPM hubs"
                                                    severity="warning"
                                                    icon="pi pi-star-fill"
                                                    rounded
                                                  />
                                                  <span className="small text-secondary ms-2">
                                                    Residues that sit on many structural communication paths —
                                                    likely <em>allosteric bottlenecks</em> or folding cores.
                                                  </span>
                                                </p>

                                                <p>
                                                  <Tag
                                                    value="SPM edges"
                                                    severity="info"
                                                    icon="pi pi-link"
                                                    rounded
                                                  />
                                                  <span className="small text-secondary ms-2">
                                                    Contacts carrying high path traffic — breaking one of
                                                    them disconnects distant parts of the structure.
                                                  </span>
                                                </p>

                                              </div>

                                              {(result.data && result.data.length > 0) ? (<>
                                              <div className="mt-4 animate__animated animate__fadeIn">

                                                        {/* SPM profile summary + table */}
                                                        <div className="mb-4">
                                                            <SpmProfileTablePath
                                                                tableData={result.data || []}
                                                                percentile={spmPercentile}
                                                                onSelectResidue={openEgoNetwork}
                                                                loading={analysisLoading && mode === 'level2'}
                                                            />
                                                        </div>

                                                        {/* Ego network drill-in */}
                                                        {egoResidue && (
                                                            <div className="mt-4 mb-4 animate__animated animate__fadeIn">
                                                                {egoLoading ? (
                                                                    <Card className="text-center py-5">
                                                                        <i className="pi pi-spin pi-spinner text-primary" style={{ fontSize: '2rem' }} />
                                                                        <p className="mt-3 mb-0 text-secondary">
                                                                            Loading ego network for {egoResidue}…
                                                                        </p>
                                                                    </Card>
                                                                ) : egoData && egoData.neighbors && egoData.neighbors.length > 0 ? (
                                                                    <EgoNetworkPlot
                                                                        center={egoResidue}
                                                                        neighbors={egoData.neighbors}
                                                                        percentile={spmPercentile}
                                                                        onClose={() => {
                                                                            setEgoResidue(null);
                                                                            setEgoData(null);
                                                                        }}
                                                                        title="Residue neighborhood"
                                                                    />
                                                                ) : (
                                                                    <Message
                                                                        severity="info"
                                                                        className="w-full"
                                                                        text={`No neighbors within 8 Å of ${egoResidue}.`}
                                                                    />
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>

                                                       {/* Per-residue charts — individual cards (Level 3/4 style), below the SPM table */}
                                                       <div className="row g-3 mb-3">
                                                         <div className="col-12 col-md-6">
                                                           <div className="p-3 border rounded bg-white h-100">
                                                             <h6 className="mb-3 text-primary"><i className="pi pi-chart-bar me-2" />Contacts per residue · by SPM hub</h6>
                                                             <GroupedResidueBarChart
                                                               data={(result.data || []).filter((r) => r && r.col1 !== '__summary__').map((r) => ({ position: r.col1, value: Number(r.col2), highlightValue: Number(r.col8) }))}
                                                               percentile={spmPercentile} title="" yAxisLabel="# of contacts" allLabel="Total"
                                                               highlightLabel={`Top ${spmPercentile}% hubs`} baseColor="#9dd9d2" highlightColor="#1e9e8a" onResidueClick={openEgoNetwork} />
                                                           </div>
                                                         </div>
                                                         <div className="col-12 col-md-6">
                                                           <div className="p-3 border rounded bg-white h-100">
                                                             <h6 className="mb-3 text-primary"><i className="pi pi-chart-bar me-2" />Contacts per residue · by SPM edge</h6>
                                                             <GroupedResidueBarChart
                                                               data={(result.data || []).filter((r) => r && r.col1 !== '__summary__').map((r) => ({ position: r.col1, value: Number(r.col2), highlightValue: Number(r.col7) }))}
                                                               percentile={spmPercentile} title="" yAxisLabel="# of contacts" allLabel="Total"
                                                               highlightLabel={`Top ${spmPercentile}% SPM edges`} baseColor="#b5d4f4" highlightColor="#185fa5" onResidueClick={openEgoNetwork} />
                                                           </div>
                                                         </div>
                                                       </div>
                                                        <div className="row g-3 mb-3">
                                                          <div className='col-12 col-md-6'>
                                                            <div className="p-3 border rounded bg-white h-100">
                                                              <h6 className="mb-3 text-primary"><i className="pi pi-share-alt me-2" />Betweenness centrality</h6>
                                                              <BetweennessBarChart
                                                                data={(result.data || []).filter((r) => r && r.col1 !== '__summary__').map((r) => ({ position: r.col1, betweenness: Number(r.col3), degree: Number(r.col2), isHub: r.col5 === 'Yes' }))}
                                                                percentile={spmPercentile} title="" onResidueClick={openEgoNetwork} />
                                                            </div>
                                                          </div>
                                                          {(result.data || []).some((r) => r && r.scope === 'both') && (
                                                            <div className="col-12 col-md-6">
                                                              <div className="p-3 border rounded bg-white h-100">
                                                                <h6 className="mb-3 text-primary"><i className="pi pi-arrows-h me-2" />Inter-driven betweenness (Δ = both − intra)</h6>
                                                                <BetweennessBarChart
                                                                  data={(result.data || []).filter((r) => r && r.col1 !== '__summary__' && r.btw_diff != null).map((r) => ({ position: r.col1, betweenness: Number(r.btw_diff), degree: Number(r.col2), isHub: r.col5 === 'Yes' }))}
                                                                  percentile={spmPercentile} title="" onResidueClick={openEgoNetwork} />

                                                              </div>
                                                            </div>
                                                          )}
                                                        </div>

                         <div className="d-flex align-items-center">
                            <h5 className="mb-0 text-primary mb-4 mt-2"><Grip  className="me-2"/>Structure Hubs</h5>
                        </div>
                        <Dropdown
                            value={centralityMetric}
                            options={[
                                {label: 'Degree', value: 'col2'},
                                {label: 'Betweenness', value: 'col3'},
                                {label: 'Hubs', value: 'col5'},
                            ]}
                            onChange={(e) => setCentralityMetric(e.value)}
                            placeholder="Select Annotation"
                            style={{ width: '100%' }}
                            className="structure-dropdown mb-3"
                        />
                        <div className="viewer-box mb-2">
                            <MolstarViewerPath
                                pdbId={submittedPdbId}
                                level="2"
                                datatable={result.molstarData || result.data}
                                centralityMetric={centralityMetric}
                            />
                        </div>
                         {result && result.data && centralityMetric !== 'col5' && (
                            <div className="mt-3 p-3 border rounded bg-light">
                                <div className="d-flex justify-content-between mb-1 small fw-bold text-secondary">
                                  <span>Min: {Math.min(...result.data.slice(1).map(d =>
                                  centralityMetric === 'col2' ? d.col2 : d.col3
                                )).toFixed(2)}</span>
                                   <span className="text-center text-uppercase">
                                        Intensity Spectrum ({centralityMetric === 'col2' ? 'Degree Centrality' : 'Betweenness Centrality'})
                                    </span>
                                   <span>Max: {Math.max(...result.data.slice(1).map(d =>
                                      centralityMetric === 'col2' ? d.col2 : d.col3
                                    )).toFixed(2)}</span>

                                </div>
                                <div style={{
                                    height: '12px',
                                    background: 'linear-gradient(to right, #3B82F6 0%, #FBBF24 50%, #EF4444 100%)',
                                    borderRadius: '6px'
                                }}></div>
                              <div className="d-flex justify-content-between mt-2 small text-500">
                                    <div className="text-start" style={{ width: '45%' }}>
                                        {centralityMetric === 'col2'
                                            ? "Blue: Low connectivity; peripheral residue."
                                            : "Blue: Minimal contribution to global topology."}
                                    </div>
                                    <div className="text-end" style={{ width: '45%' }}>
                                        {centralityMetric === 'col2'
                                            ? "Red: High-occupancy structural hub."
                                            : "Red: Critical allosteric bottleneck."}
                                    </div>
                                </div>
                            </div>
                        )}
                        {/* Legend for the categorical 'Hubs' mode (no min/max — it's a binary classification). */}
                        {result && result.data && centralityMetric === 'col5' && (
                            <div className="mt-3 p-3 border rounded bg-light">
                                <div className="d-flex justify-content-between align-items-center mb-2 small fw-bold text-secondary">
                                    <span className="text-center text-uppercase w-100">
                                        SPM Hubs ({result.data.filter(d => d.col5 === 'Yes').length} of {result.data.length} residues)
                                    </span>
                                </div>
                                <div className="d-flex align-items-center gap-3 small">
                                    <div className="d-flex align-items-center gap-2">
                                        <span style={{
                                            display: 'inline-block',
                                            width: '14px',
                                            height: '14px',
                                            background: '#E53935',
                                            borderRadius: '50%',
                                            border: '1px solid #b71c1c'
                                        }}></span>
                                        <span className="text-500">Hub residue (top {spmPercentile}% by betweenness, with ball-and-stick overlay)</span>
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                        <span style={{
                                            display: 'inline-block',
                                            width: '14px',
                                            height: '14px',
                                            background: '#EEEEEE',
                                            borderRadius: '50%',
                                            border: '1px solid #cccccc'
                                        }}></span>
                                        <span className="text-500">Non-hub residue (cartoon only)</span>
                                    </div>
                                </div>
                            </div>
                        )}
                     </>) : (
                       <Message severity="info" className="w-100 mt-3" text="No hubs found for this structure." />
                     )}
                     </Card>

              )}

              {/* --- RENDER: TABLE (Level 3 - PPI Hotspots) --- */}
             {result && mode === 'level3' && (
              <Card className="shadow-sm animate__animated animate__fadeIn">

                  {/* -------- Header -------- */}
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                  <h4 className="text-danger mb-0">
                                    <AlertTriangle className="me-2"/>PPI Interface Hotspots
                                  </h4>

                                <div className="d-flex align-items-center gap-3">
                           <div className="d-flex align-items-center gap-2">
                               <Checkbox
                                   inputId="pathogenicOnly"
                                   onChange={e => setShowOnlyPathogenic(e.checked)}
                                   checked={showOnlyPathogenic}
                               />
                               <label
                                   htmlFor="pathogenicOnly"
                                   className="cursor-pointer small fw-bold text-secondary user-select-none mb-0 d-flex align-items-center"
                                   style={{ height: '100%' }}
                               >
                                   Show Only Pathogenic Variants
                               </label>
                           </div>

                           <Button
                               label="Download CSV"
                               icon="pi pi-download"
                               severity="secondary"
                               outlined
                               size="small"
                               onClick={downloadCSV}
                           />
                       </div>
                   </div>
                        <div className="card card-spm bg-light border-0 p-3 mb-4 text-start">
    <h5 className="mb-3">
      <i className="pi pi-info-circle me-2 text-primary" />
      What is PPI Interface Hotspots analysis?
    </h5>
    <p className="mb-3 small">
      We identify residues at the <strong>protein–protein interface</strong> and
      characterise them across <strong>three complementary dimensions</strong>:
      <strong> pathogenic variant burden</strong>,{' '}
      <strong>3D neighbourhood density</strong>, and{' '}
      <strong>interaction promiscuity</strong>.
    </p>
    <p className="mb-2">
      <Tag value="Fisher banner" severity="danger" icon="pi pi-chart-bar" rounded />
      <span className="small text-secondary ms-2">
        Two-sided Fisher exact test — among ClinVar-classified residues,
        does the pathogenic-vs-benign composition differ at interface positions
        relative to the rest of the structure? Direction and magnitude are read
        from the odds ratio and its 95% CI.
      </span>
    </p>
    <p className="mb-2">
      <Tag value="Density" severity="warning" icon="pi pi-bolt" rounded />
      <span className="small text-secondary ms-2">
        Pathogenic neighbours / total neighbours within 8&nbsp;Å.
      </span>
    </p>
    <p className="mb-2">
      <Tag value="3D Neighborhood Enrichment" severity="warning" icon="pi pi-bolt" rounded />
      <span className="small text-secondary ms-2">
        Per-residue density relative to the interface mean
        (&ge;&nbsp;4&times; strong hotspot, &ge;&nbsp;2&times; notable,
        &lt;&nbsp;2&times; unremarkable). Highlights residues whose 3D
        neighbourhood is disproportionately patho­genic.
      </span>
    </p>
    <p className="mb-0">
      <Tag value="Promiscuity" severity="info" icon="pi pi-share-alt" rounded />
      <span className="small text-secondary ms-2">
        Number of distinct partner proteins each residue contacts across all
        structures — distinguishes <em>hub</em> residues from interface-specific ones.
      </span>
    </p>
</div>


    {/* ─────────────── DATA TABLE ─────────────── */}

      <ColumnToggleDataTable value={
                          showOnlyPathogenic
                            ? result.data.filter(r => {
                                const label = String(r.variant_pathogenicity || "")
                                  .toLowerCase()
                                  .replace(/[_\s]/g, "");

                                return label.includes("pathogenic");
                              })
                            : result.data
                        } className="nobreak" paginator rows={5} stripedRows showGridlines filterDisplay="menu" tableStyle={{ minWidth: '66rem' }} responsiveLayout="scroll">
                        <Column
                          field="col1"
                          header="Residue A"
                          sortable
                          filter
                          dataType="text"
                          filterPlaceholder="Search by Residue A"
                        />
                         <Column
        field="col2"
        header="Variant A"
        sortable
        filter
        dataType="text"
        filterPlaceholder="Search by Variant A"
      />

      <Column
        field="variant_pathogenicity"
        header="ClinVar Clinical Significance"
        sortable
        filter
        dataType="text"
        filterPlaceholder="Search by ClinVar Clinical Significance"
        body={(row) => {
          const cat   = row.variant_pathogenicity || 'uncertain';
          const color = variantPathogenicityColor(cat);
          return (
            <Tag
              value={row.variant_pathogenicity.trim()}
              style={{
                justifyContent: 'flex-start',
                backgroundColor: color,
                color: '#ffffff',
              }}
            />
          );
        }}
      />

      <Column
        field="col3"
        header="AlphaMissense Class"
        sortable
        filter
        dataType="text"
        filterPlaceholder="Search by AlphaMissense Class"
      />
      <Column
        field="col4"
        header="Interacts with (B)"
        sortable
        filter
        dataType="text"
        filterPlaceholder="Search by Interacts with (B)"
      />
      <Column
        field="col5"
        header="Dist (Å)"
        sortable
        filter
        dataType="numeric"
        filterElement={renderNumericFilter}
        filterPlaceholder="Search by Dist (Å)"
      />
      <Column
        field="density"
        header="Density"
        sortable
        filter
        dataType="numeric"
        filterElement={renderNumericFilter}
        filterPlaceholder="Search by Density"
        body={(row) => {
          const path = row.pathogenic_neighbours;
          const tot  = row.total_neighbours;
          if (tot == null || path == null) return '—';
          return <span>{path}/{tot}</span>;
        }}
      />
      <Column
        field="enrichment_score"
        header="Enrichment Score"
        sortable
        filter
        dataType="numeric"
        filterElement={renderNumericFilter}
        filterPlaceholder="Search by Enrichment Score"
        body={(row) => {
          const e = row.enrichment_score;
          if (e == null) return '—';
          const sev = e >= 4 ? 'danger' : e >= 2 ? 'warning' : 'secondary';
          return <Tag value={`${Number(e).toFixed(1)}x Enriched`} severity={sev} />;
        }}
      />
      <Column
        field="promiscuity_score"
        header="Promiscuity Score"
        sortable
        filter
        dataType="numeric"
        filterElement={renderNumericFilter}
        filterPlaceholder="Search by Promiscuity Score"
      />
      <Column
        field="col6"
        header="Source Structure"
        sortable
        filter
        dataType="text"
        filterPlaceholder="Search by Source Structure"
        body={(row) => (
          <Tag value={row.col6} severity={row.col6 === 'AlphaFold' ? 'warning' : 'success'} />
        )}
      />
                    </ColumnToggleDataTable>

       {/* ─────────────── STRUCTURE SECTION (full-width viewer) ─────────────── */}
    <div className="d-flex align-items-center mt-4 mb-3">
      <h5 className="mb-0 text-primary">
        <Route className="me-2"/>Interface Structure
      </h5>
    </div>

    {/* Horizontal control bar — sits on top of the viewer */}
    <div className="mb-3">
      <div className="row g-3 align-items-end">

        {/* Viewer-only pathogenicity filter (independent from the table buttons) */}
        <div className="col-md-3 mb-3">
          <label htmlFor="level3-viewer-filters" className="form-label fw-bold text-secondary mb-1">
            Show in structure
          </label>
          <MultiSelect
            id="level3-viewer-filters"
            value={level3ViewerFilters}
            onChange={(e) => setLevel3ViewerFilters(e.value || [])}
            options={[
              { label: 'Pathogenic',                    value: 'pathogenic'                                     },
              { label: 'Likely pathogenic',             value: 'likely_pathogenic'                              },
              { label: 'Pathogenic / Likely pathogenic', value: 'pathogenic/likely_pathogenic'                  },
              { label: 'Conflicting',                   value: 'conflicting_classifications_of_pathogenicity'   },
              { label: 'Uncertain significance (VUS)',  value: 'uncertain_significance'                         },
              { label: 'Likely benign',                 value: 'likely_benign'                                  },
              { label: 'Benign',                        value: 'benign'                                         },
            ]}
            placeholder="Select categories"
            display="chip"
            className="w-full"
            style={{ width: '100%' }}
          />
        </div>

        {/* View-mode toggle */}
        <div className="col-md-6 mb-3">
          <label className="form-label fw-bold text-secondary mb-1">
            View mode
          </label>
          <SelectButton
            value={level3ViewMode}
            onChange={(e) => e.value && setLevel3ViewMode(e.value)}
            options={[
              { label: 'Variants',   value: 'variants'   },
              { label: 'Enrichment', value: 'enrichment' },
              { label: 'Promiscuity', value: 'promiscuity' },
            ]}
            className="fs--2"
          />
        </div>

      </div>
    </div>

    {/* Viewer — full width */}
    <div className="viewer-box mb-2" style={{ minHeight: '500px' }}>
      {(() => {


        const rowsForStructure = (result.molstarData || []).filter(r =>
          r.col6 === submittedPdbId &&
          (level3ViewerFilters.length === 0
            || level3ViewerFilters.includes(
                 String(r.variant_pathogenicity || '').toLowerCase().trim()
               ))
        );


        const focusedRows = level3SelectedRow
          ? rowsForStructure.filter(r =>
              r.col1 === level3SelectedRow.col1 &&
              r.col4 === level3SelectedRow.col4
            )
          : rowsForStructure;

        if (level3ViewMode === 'variants') {

          // Expand to every chain carrying the residue: a homo-oligomer holds
          // it in each subunit and Mol* selects chains one at a time.
          const validRows = focusedRows.filter(r => r.col1_mapped);
          const positionsArr = [];
          const chainsArr    = [];
          validRows.forEach(r => {
            (r.chains && r.chains.length ? r.chains : [r.chain || 'A']).forEach(ch => {
              positionsArr.push(r.col1_mapped);
              chainsArr.push(ch);
            });
          });

          return (
            <MolstarViewerPath
              pdbId={submittedPdbId}
              level="3"
              positions={positionsArr}
              chain={chainsArr}
            />
          );
        }

        if (level3ViewMode === 'promiscuity') {
          // Dedup by position — promiscuity_score is residue-level
          const seen = new Set();
          const promiscuityTable = [];
          for (const r of focusedRows) {
            if (!r.col1_mapped || seen.has(r.col1_mapped)) continue;
            seen.add(r.col1_mapped);
            (r.chains && r.chains.length ? r.chains : [r.chain || 'A']).forEach(ch => {
              promiscuityTable.push({
                col1: r.col1_mapped,
                col2: r.promiscuity_score ?? 0,
                chain: ch,
              });
            });
          }
          return (
            <MolstarViewerPath
              pdbId={submittedPdbId}
              level="2"
              datatable={promiscuityTable}
              centralityMetric="col2"
            />
          );
        }

        const enrichmentTable = focusedRows
          .filter(r => r.col1_mapped)
          .flatMap(r => (r.chains && r.chains.length ? r.chains : [r.chain || 'A'])
            .map(ch => ({
              col1: r.col1_mapped,
              col2: r.enrichment_score ?? 0,
              chain: ch,
            })));

        return (
          <MolstarViewerPath
            pdbId={submittedPdbId}
            level="2"
            datatable={enrichmentTable}
            centralityMetric="col2"
          />
        );
      })()}
    </div>

    {/* Enrichment-mode legend */}
                                        {level3ViewMode === 'enrichment' && (
                                        <div className="mt-3 p-3 border rounded bg-light mb-4">
                                <div className="d-flex justify-content-between mb-1 small fw-bold text-secondary">
                                  <span className="text-secondary">Min: <strong>0</strong></span>
                                   <span className="text-center text-uppercase">
                                        Enrichment Score (local variant density)
                                    </span>
                                   <span className="text-secondary">
                                                Max: <strong>
                                                  {Math.max(0, ...(result.molstarData || []).map(r => r.enrichment_score ?? 0))}
                                                </strong>
                                              </span>

                                </div>
                                <div style={{
                                    height: '12px',
                                    background: 'linear-gradient(to right, #3B82F6 0%, #FBBF24 50%, #EF4444 100%)',
                                    borderRadius: '6px'
                                }}></div>
                              <div className="d-flex justify-content-between mt-2 small text-500">
                                    <div className="text-start" style={{ width: '45%' }}>
                                        Blue: Low density of pathogenic variants in the residue's 3D neighbourhood.
                                    </div>
                                    <div className="text-end" style={{ width: '45%' }}>
                                        Red: High density — clustered pathogenic variants forming a structural hotspot.
                                    </div>
                                </div>
                            </div>

                                        )}
                                         {level3ViewMode === 'promiscuity' && (
                                        <div className="mt-3 p-3 border rounded bg-light mb-4">
                                <div className="d-flex justify-content-between mb-1 small fw-bold text-secondary">
                                  <span className="text-secondary">Min: <strong>1</strong></span>
                                   <span className="text-center text-uppercase">
                                        Promiscuity Score (partner protein count)
                                    </span>
                                   <span className="text-secondary">
                                                Max: <strong>
                                                  {Math.max(1, ...(result.molstarData || []).map(r => r.promiscuity_score ?? 0))}
                                                </strong>
                                              </span>
                                </div>
                                <div style={{
                                    height: '12px',
                                    background: 'linear-gradient(to right, #3B82F6 0%, #FBBF24 50%, #EF4444 100%)',
                                    borderRadius: '6px'
                                }}></div>
                              <div className="d-flex justify-content-between mt-2 small text-500">
                                    <div className="text-start" style={{ width: '45%' }}>
                                        Blue: Specific interface — residue touches one partner only.
                                    </div>
                                    <div className="text-end" style={{ width: '45%' }}>
                                        Red: Hub residue — contacts multiple partners across complexes.
                                    </div>
                                </div>
                            </div>
                                        )}
                                         {level3ViewMode === 'variants' && (

                                         <div className="d-flex mb-4">
                                      <div className="burble-variant"></div>
                                      <p className="fs--2 text-justify">Variant</p>
                                    </div>
                         )}

{/* ───────────── BLOCK 2: Interface enrichment (transparent 2x2) ───────────── */}
                                    {(() => {
                                      const st = result.enrichmentStats?.structuresData?.interface_enrichment;
                                      if (!st) {
                                        return (
                                          <Message
                                            className="mb-3 w-100"
                                            content={
                                              <div className="small bg-light p-3 border rounded text-500">
                                                <strong>Disease variants detected at the binding interface.</strong>
                                                <br />
                                                <span>Enrichment statistics are not available for this structure.</span>
                                              </div>
                                            }
                                          />
                                        );
                                      }

                                      const num = (color = '#212529') => ({ fontSize: '1.7rem', fontWeight: 700, color, lineHeight: 1.1 });
                                      const fmtP = (p) => p == null ? '—' : (p < 0.001 ? p.toExponential(2) : p.toFixed(4));
                                      const pAnalytic = fmtP(st.p_value_greater);
                                      const pSig = st.p_value_greater != null && st.p_value_greater < 0.05;
                                      // Badge reflects the confidence interval, not the p-value: an
                                      // enrichment is called robust only when the whole 95% CI sits
                                      // above 1 (OR > 1 excluded of no-effect). A CI that crosses 1 is
                                      // inconclusive even if the two-sided p < 0.05, which happens at
                                      // small N. This keeps the badge, OR and CI telling one story.
                                      const ciRobust = st.ci_low != null && st.ci_low > 1;
                                      const orStr = st.odds_ratio_is_infinite ? '∞' : (st.odds_ratio == null ? '—' : st.odds_ratio);
                                      const ciStr = (st.ci_low == null) ? '—'
                                        : `${st.ci_low} – ${st.ci_high_is_infinite ? '∞' : st.ci_high}`;
                                      const foldStr = st.fold_enrichment == null ? '—' : st.fold_enrichment;
                                      const iPct = st.interface_rate != null ? (st.interface_rate * 100).toFixed(0) : null;
                                      const bPct = st.background_rate != null ? (st.background_rate * 100).toFixed(0) : null;

                                      return (
                                        <div className="p-3 mb-2 text-start">
                                          <div className="d-flex align-items-center justify-content-between mb-4">
                                            <h4 className="mb-0">
                                              <i className="pi pi-chart-bar me-2 text-primary" /> Interface enrichment: pathogenic vs benign
                                            </h4>
                                            <Tag
                                              value={ciRobust ? "Robust enrichment" : "Inconclusive"}
                                              severity={ciRobust ? "success" : "warning"}
                                            />
                                          </div>

                                          <div className="row g-2 mb-2">
                                            <div className="col-md-4 col-6">
                                              <div className="card p-3 border-start border-3 border-secondary h-100">
                                                <div className="text-secondary small mb-1">Fold enrichment</div>
                                                <div className="fw-bold" style={num('#3B82F6')}>
                                                  {foldStr}{st.fold_enrichment != null && <span style={{ fontSize: '1.1rem' }}>×</span>}
                                                </div>
                                                <div className="text-secondary" style={{ fontSize: '0.7rem' }}>
                                                  {iPct != null ? `${iPct}% vs ${bPct}% pathogenic` : ''}
                                                </div>
                                              </div>
                                            </div>
                                            <div className="col-md-4 col-6">
                                              <div className="card p-3 border-start border-3 border-secondary h-100">
                                                <div className="text-secondary small mb-1">Odds ratio</div>
                                                <div className="fw-bold" style={num('#6c757d')}>{orStr}</div>
                                                <div className="text-secondary" style={{ fontSize: '0.7rem' }}>95% CI {ciStr}</div>
                                              </div>
                                            </div>
                                            <div className="col-md-4 col-6">
                                              <div className="card p-3 border-start border-3 border-secondary h-100">
                                                <div className="text-secondary small mb-1">P-value (Fisher)</div>
                                                <div className="fw-bold" style={num(pSig ? '#22c55e' : '#212529')}>{pAnalytic}</div>
                                                <div className="text-secondary" style={{ fontSize: '0.7rem' }}>two-sided</div>
                                              </div>
                                            </div>
                                          </div>

                                          <div className="small text-500 mt-2">
                                            Among ClinVar-classified residues on structure {st.structure}, the interface has
                                            {' '}{st.a} pathogenic / {st.b} benign ({iPct}% pathogenic), vs {st.c} pathogenic / {st.d} benign
                                            {' '}({bPct}%) in the rest of the resolved protein.
                                          </div>

                                          {st.note && (
                                            <p className="text-warning fs--2 mb-0 mt-1">
                                              <i className="pi pi-info-circle me-1" />{st.note}
                                            </p>
                                          )}
                                        </div>
                                      );
                                    })()}

{/* ───────────── BLOCK 3: Seção dos 4 Gráficos do Level 3 ───────────── */}
{submittedPdbId !== null && (() => {
    // Per-structure plots: pull from structuresData[submittedPdbId]
    const sd = result.enrichmentStats?.structuresData?.[submittedPdbId];
    const proximityData   = sd?.proximity_plot_data   || [];
    const enrichmentData  = sd?.enrichment_plot_data  || [];
    const promiscuityData = sd?.promiscuity_plot_data || [];

    // Monotonic revision number — when submittedPdbId changes, this changes
    // and tells react-plotly.js to redraw the plot WITHOUT unmounting it.
    // Using `key` to force remount caused a createRoot() conflict because the
    // Plotly DOM container is already registered.
    const plotRevision = (sd?.proximity_plot_data?.length ?? 0)
                       + (sd?.enrichment_plot_data?.length ?? 0)
                       + (sd?.promiscuity_plot_data?.length ?? 0)
                       + String(submittedPdbId).length;

    // Plot 1 (stacked): count variants per position by category, filtered
    // to the currently-selected structure so it stays consistent with the
    // other per-structure plots.
    const seenVariants = new Set();
    const byPosition = {};
    (result.data || [])
      .filter(row => row.col6 === submittedPdbId)
      .forEach(row => {
        const pos = row.col1;
        const key = `${pos}::${row.col2}`;
        if (seenVariants.has(key)) return;
        seenVariants.add(key);
        if (!byPosition[pos]) {
          byPosition[pos] = { pathogenic: 0, conflicting: 0, vus_am_pathogenic: 0, uncertain: 0, benign: 0 };
        }
        // Map row.variant_pathogenicity (or fallback) to category bucket
        const p = String(row.variant_pathogenicity || '').toLowerCase();
        const am = String(row.col3 || '').toLowerCase();
        if (p === 'pathogenic' || p === 'likely_pathogenic' || p === 'pathogenic/likely_pathogenic') {
          byPosition[pos].pathogenic += 1;
        } else if (p === 'conflicting_classifications_of_pathogenicity') {
          byPosition[pos].conflicting += 1;
        } else if (p === 'uncertain_significance') {
          if (am === 'likely_pathogenic') byPosition[pos].vus_am_pathogenic += 1;
          else byPosition[pos].uncertain += 1;
        } else if (p === 'likely_benign' || p === 'benign') {
          byPosition[pos].benign += 1;
        }
      });

    const stackedPositions = Object.keys(byPosition).sort((a, b) => {
      const sA = Object.values(byPosition[a]).reduce((s, n) => s + n, 0);
      const sB = Object.values(byPosition[b]).reduce((s, n) => s + n, 0);
      return sB - sA;
    });

    const CATS = [
      { key: 'pathogenic',        label: 'Pathogenic',             color: '#ef4444' },
      { key: 'conflicting',       label: 'Conflicting',            color: '#06b6d4' },
      { key: 'vus_am_pathogenic', label: 'VUS + AM likely-pathogenic',   color: '#f97316' },
      { key: 'uncertain',         label: 'Uncertain (VUS)',        color: '#9ca3af' },
      { key: 'benign',            label: 'Benign / Likely-benign', color: '#22c55e' },
    ];

    const stackedTraces = CATS
      .filter(c => stackedPositions.some(p => byPosition[p][c.key] > 0))
      .map(c => ({
        x: stackedPositions,
        y: stackedPositions.map(p => byPosition[p][c.key]),
        name: c.label,
        type: 'bar',
        marker: { color: c.color },
      }));

    const commonLayout = {
      autosize: true,
      height: 360,
      margin: { l: 50, r: 20, t: 40, b: 80 },
      font: { size: 11 }
    };

    const plotConfig = {
      displayModeBar: 'hover',
      responsive: true,
      displaylogo: false,
      modeBarButtonsToRemove: [
        'zoom2d', 'pan2d', 'select2d', 'lasso2d',
        'zoomIn2d', 'zoomOut2d', 'autoScale2d', 'resetScale2d',
        'toggleSpikelines', 'hoverClosestCartesian', 'hoverCompareCartesian'
      ],
    };

    return (
    <div className="row g-4 mt-2">
        {/* Plot 1: Variant composition per position */}
        <div className="col-md-6">
          <div className="p-3 border rounded bg-white">
            <h6 className="mb-2 text-primary">
              <i className="pi pi-chart-bar me-2" />
              Variant composition by position
            </h6>
            <Plot
              revision={plotRevision}
              data={stackedTraces}
              layout={{ ...commonLayout,
                 font: { size: 9 },
                barmode: 'stack',
                xaxis: { tickangle: -45, title: { text: '' } },
                yaxis: { title: { text: 'Variant count' }, dtick: 1 },
                legend: {
                  orientation: 'h',
                  y: -0.45,
                  x: 0,
                  xanchor: 'left',
                  traceorder: 'normal',
                  font: { size: 9 },
                  itemwidth: 60,
                },
                showlegend: true,
              }}
              config={plotConfig}
              style={{ width: '100%' }}
              useResizeHandler
            />
          </div>
        </div>

        {/* Plot 2: Interface Proximity Distribution */}
        <div className="col-md-6">
            <div className="p-3 border rounded bg-white">
                <h6 className="mb-2 text-primary">
                    <i className="pi pi-chart-bar me-2" /> Interface Proximity Distribution
                </h6>
                <Plot
                    revision={plotRevision}
                    data={[{
                        y: proximityData,
                        type: 'box',
                        name: 'Distance',
                        boxpoints: 'all',
                        jitter: 0.3,
                        pointpos: 0,
                        marker: { color: '#10B981', size: 5, opacity: 0.6 },
                        line: { color: '#047857' },
                        fillcolor: 'rgba(16, 185, 129, 0.2)',
                        hovertemplate: '%{y:.2f} Å<extra></extra>',
                    }]}
                    layout={{ height: 360,
                        margin: { t: 10, b: 40, l: 50, r: 10 },
                        xaxis: { showticklabels: false },
                        yaxis: { title: 'Distance (Å)' },
                        font: { size: 11 },
                        showlegend: false,
                    }}
                    config={{ displayModeBar: 'hover', displaylogo: false, responsive: true }}
                    style={{ width: '100%' }}
                    useResizeHandler
                />
            </div>
        </div>

        {/* Plot 3: Enrichment */}
        <div className="col-md-6">
            <div className="p-3 border rounded bg-white">
                <h6 className="mb-2 text-primary">
                    <i className="pi pi-bolt me-2" /> 3D Neighborhood Enrichment Score
                </h6>
                <Plot
                    revision={plotRevision}
                    data={(() => {
                        const filtered = enrichmentData.filter(d => d.score > 0);
                        // Split into 3 traces so each category becomes its own legend entry.
                        const strong  = filtered.filter(d => d.score >= 4);
                        const notable = filtered.filter(d => d.score >= 2 && d.score < 4);
                        const low     = filtered.filter(d => d.score < 2);
                        return [
                            {
                                x: strong.map(d => d.pos),
                                y: strong.map(d => d.score),
                                type: 'bar',
                                name: 'Strong hotspot (≥4×)',
                                marker: { color: '#ef4444' },
                            },
                            {
                                x: notable.map(d => d.pos),
                                y: notable.map(d => d.score),
                                type: 'bar',
                                name: 'Notable (≥2×)',
                                marker: { color: '#f97316' },
                            },
                            {
                                x: low.map(d => d.pos),
                                y: low.map(d => d.score),
                                type: 'bar',
                                name: 'Unremarkable (<2×)',
                                marker: { color: '#9ca3af' },
                            },
                        ];
                    })()}
                    layout={{ height: 360,
                        margin: { t: 10, b: 100, l: 50, r: 10 },
                        xaxis: { title: 'Residue Position', type: 'category' },
                        yaxis: { title: 'Enrichment Ratio' },
                        font: { size: 9 },
                        legend: { orientation: 'h', y: -0.5, x: 0, xanchor: 'left', font: { size: 10 } },
                        showlegend: true,
                    }}
                    config={{ displayModeBar: 'hover', displaylogo: false, responsive: true }}
                    style={{ width: '100%' }}
                    useResizeHandler
                />
            </div>
        </div>

        {/* Plot 4: Promiscuity Score */}
        <div className="col-md-6">
            <div className="p-3 border rounded bg-white">
                <h6 className="mb-2 text-primary">
                    <i className="pi pi-share-alt me-2" /> Interface Promiscuity Score
                </h6>
                <Plot
                    revision={plotRevision}
                    data={(() => {
                        // Categorize residues by partner count
                        let specific = 0, low = 0, multi = 0, hub = 0;
                        (promiscuityData || []).forEach(v => {
                            const n = parseInt(v, 10);
                            if (isNaN(n) || n < 1) return;
                            if (n === 1)        specific += 1;
                            else if (n <= 3)    low += 1;
                            else if (n <= 10)   multi += 1;
                            else                hub += 1;
                        });
                        return [
                            {
                                x: ['Specific (1)'],
                                y: [specific],
                                type: 'bar',
                                name: 'Specific (1 partner)',
                                marker: { color: '#9ca3af' },
                            },
                            {
                                x: ['Low (2–3)'],
                                y: [low],
                                type: 'bar',
                                name: 'Low-promiscuity (2–3 partners)',
                                marker: { color: '#3B82F6' },
                            },
                            {
                                x: ['Multi (4–10)'],
                                y: [multi],
                                type: 'bar',
                                name: 'Multi-partner (4–10 partners)',
                                marker: { color: '#f97316' },
                            },
                            {
                                x: ['Hub (>10)'],
                                y: [hub],
                                type: 'bar',
                                name: 'Hub (>10 partners)',
                                marker: { color: '#ef4444' },
                            },
                        ];
                    })()}
                    layout={{ height: 360,
                        margin: { t: 10, b: 100, l: 50, r: 10 },
                        xaxis: { title: '' },
                        yaxis: { title: 'Residue count', dtick: 1 },
                        font: { size: 10 },
                        legend: { orientation: 'h', y: -0.4, x: 0, xanchor: 'left', font: { size: 10 } },
                        showlegend: true,
                    }}
                    config={{ displayModeBar: 'hover', displaylogo: false, responsive: true }}
                    style={{ width: '100%' }}
                    useResizeHandler
                />
            </div>
        </div>
    </div>
    );
})()}
  </Card>
)}

              {/* --- RENDER: TABLE (Level 4 - Ligand-Variant Overlap) --- */}
              {result && mode === 'level4' && (
                 <Card className="shadow-sm animate__animated animate__fadeIn">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                       <h4 className="text-danger mb-0"><AlertTriangle className="me-2"/>Functional Sites Overlap</h4>
                       <div className="d-flex align-items-center gap-3">
                           <div className="d-flex align-items-center gap-2">
                               <Checkbox
                                   inputId="pathogenicOnly"
                                   onChange={e => setShowOnlyPathogenicLevel4(e.checked)}
                                   checked={showOnlyPathogenicLevel4}
                               />
                               <label
                                   htmlFor="pathogenicOnly"
                                   className="cursor-pointer small fw-bold text-secondary user-select-none mb-0 d-flex align-items-center"
                                   style={{ height: '100%' }}
                               >
                                   Show Only Pathogenic Variants
                               </label>
                           </div>

                           <Button
                               label="Download CSV"
                               icon="pi pi-download"
                               severity="secondary"
                               outlined
                               size="small"
                               onClick={downloadCSV}
                           />
                       </div>
                   </div>
                        {/* Context  */}
                       <div className="card card-spm bg-light border-0 p-3 mb-4 text-start">
    <h5 className="mb-3">
      <i className="pi pi-info-circle me-2 text-primary" />
      What is Functional Sites Overlap analysis?
    </h5>
    <p className="mb-3 small">
      We identify variants on functional-site residues —{' '}
      <strong>binding sites</strong> (UniProt-annotated
      ligands and cofactors) and <strong>active sites</strong> (UniProt
      catalytic-site annotations, including catalytic intermediates) — and their{' '}
      <strong>1st-shell allosteric neighbours</strong> within 8&nbsp;Å through 3D
      intra-protein contacts in the graph.
    </p>
    <p className="mb-2">
      <Tag value="Fisher banners" severity="danger" icon="pi pi-chart-bar" rounded />
      <span className="small text-secondary ms-2">
        Two two-sided Fisher exact tests — one for binding sites and one for active
        sites — comparing the pathogenic composition at the functional-site interface
        (direct&nbsp;+&nbsp;1st-shell) vs. the rest of the resolved structure.
      </span>
    </p>
    <p className="mb-2">
      <Tag value="3D Neighborhood Enrichment" severity="warning" icon="pi pi-bolt" rounded />
      <span className="small text-secondary ms-2">
        Per-residue density (pathogenic neighbours / total neighbours within
        8&nbsp;Å) relative to the protein-wide mean, centred on each variant
        residue in the table. Shown separately for binding and active sites —
        variants with no 3D neighbours simply do not appear in the plot.
      </span>
    </p>
    <p className="mb-2">
      <Tag value="Multi-feature residues" severity="warning" icon="pi pi-th-large" rounded />
      <span className="small text-secondary ms-2">
        For each variant-bearing residue we count how many of these{' '}
        <strong>four functional layers</strong> co-localise on it:{' '}
        <em>(1) variant present, (2) PTM annotated, (3) binding site,
        (4) active site</em>. Residues where multiple layers converge are
        biologically rich hotspots — perturbing them likely affects multiple
        mechanisms simultaneously. Only possible because the graph stores all
        four annotations on the same residue.
      </span>
    </p>
    <p className="mb-2">
      <Tag value="Cascade severity" severity="info" icon="pi pi-sitemap" rounded />
      <span className="small text-secondary ms-2">
        The count itself (1–4).{' '}
        <strong>1</strong> = variant only.{' '}
        <strong>2</strong> = variant + one feature (e.g. PTM).{' '}
        <strong>3</strong> = variant + two features (e.g. PTM&nbsp;+&nbsp;binding).{' '}
        <strong>4</strong> = all four converge — the strongest mechanistic
        hypotheses ("<em>domino-effect</em>" cases). Severity-stratified
        enrichment is a direct way to show that <em>graph integration</em>{' '}
        reveals signal single-source tabular filters miss.
      </span>
    </p>
    <p className="mb-2">
      <Tag value="Cascade flow (Sankey)" severity="info" icon="pi pi-arrows-h" rounded />
      <span className="small text-secondary ms-2">
        When at least 10 severity&nbsp;&ge;&nbsp;3 cases are found, a Sankey
        diagram visualises the flow{' '}
        <em>Variant pathogenicity → PTM status → Site type → Site detail</em>{' '}
        so dominant mechanistic narratives in the protein become visible at
        a glance.
      </span>
    </p>
    <p className="mb-2">
      <Tag value="Variant Cluster Synergy" severity="success" icon="pi pi-objects-column" rounded />
      <span className="small text-secondary ms-2">
        Flips the perspective from <em>per-variant</em> to <em>per-site</em>:
        for every functional site in the pinned structure, lists how many
        ClinVar variants individually classified as VUS / benign /
        likely-benign / conflicting cluster within 8&nbsp;Å. A horizontal
        stacked bar — sites ranked by relevance — highlights in{' '}
        <span className="fw-bold" style={{ color: '#DC2626' }}>red</span>{' '}
        the subset of those "innocuous" variants that AlphaMissense flags
        as <em>ambiguous</em> or <em>likely_pathogenic</em>, a candidate
        signature of compound-effect pathogenicity invisible to single-variant
        filtering.
      </span>
    </p>
    <p className="mb-0">
      <Tag value="Allosteric bridge residues" severity="warning" icon="pi pi-share-alt" rounded />
      <span className="small text-secondary ms-2">
        Variant-bearing residues that are neither a PTM nor a functional site themselves,
        yet lie within 8&nbsp;Å (intra-chain) of <strong>both</strong> a PTM{' '}
        <strong>and</strong> a binding / active site, structurally coupling the two.
        Reported as candidate allosteric relays and kept separate from cascade severity,
        as spatial proximity is necessary but not sufficient for functional coupling.
      </span>
    </p>
</div>

                    {/* ───────── DataTable ───────── */}
                    <ColumnToggleDataTable value={
                          showOnlyPathogenicLevel4
                            ? result.data.filter(r => {
                                const label = String(r.variant_pathogenicity || "")
                                  .toLowerCase()
                                  .replace(/[_\s]/g, "");
                                return label.includes("pathogenic");
                              })
                            : result.data
                        } className="mb-3 nobreak" paginator rows={5} stripedRows showGridlines filterDisplay="menu" tableStyle={{ minWidth: '66rem' }} responsiveLayout="scroll">
                        <Column field="col1" header="Residue" sortable filter dataType="text" filterPlaceholder="Search by Residue" />
                        <Column field="col2" header="Variant" sortable filter dataType="text" filterPlaceholder="Search by Variant" />
                        <Column
                          field="variant_pathogenicity"
                          header="ClinVar Clinical Significance"
                          sortable filter dataType="text"
                          filterPlaceholder="Search by Clinical Significance"
                          body={(row) => {
                            const cat   = row.variant_pathogenicity || 'uncertain';
                            const color = variantPathogenicityColor(cat);
                            return (
                              <Tag
                                value={(row.variant_pathogenicity || '').trim()}
                                style={{ justifyContent: 'flex-start', backgroundColor: color, color: '#ffffff' }}
                              />
                            );
                          }}
                        />
                        <Column field="col3" header="AlphaMissense Class" sortable filter dataType="text" filterPlaceholder="Search by AlphaMissense Class" />
                        <Column
                          field="site_type"
                          header="Site type"
                          sortable filter dataType="text"
                          filterPlaceholder="binding or active"
                          body={(row) => (
                            <Tag
                              value={row.site_type}
                              style={{ backgroundColor: row.site_type === 'binding' ? '#3B82F6' : '#8B5CF6', color: '#fff' }}
                            />
                          )}
                        />
                        <Column field="col4" header="Site detail" sortable filter dataType="text" filterPlaceholder="Search by Site detail" />
                        <Column
                          field="hop"
                          header="Hop"
                          sortable filter dataType="numeric"
                          filterElement={renderNumericFilter}
                          filterPlaceholder="0 or 1"
                          body={(row) => (
                            <Tag
                              value={row.hop === 0 ? 'Direct' : '1st shell'}
                              severity={row.hop === 0 ? 'danger' : 'warning'}
                            />
                          )}
                        />
                        <Column
                          field="col5"
                          header="Dist (Å)"
                          sortable filter dataType="numeric"
                          filterElement={renderNumericFilter}
                          filterPlaceholder="Search by Dist (Å)"
                          body={(row) => row.col5 != null ? Number(row.col5).toFixed(2) : '—'}
                        />
                        <Column
                          field="has_ptm"
                          header="PTM"
                          sortable filter dataType="boolean"
                          body={(row) => row.has_ptm
                            ? <Tag value="Yes" severity="warning" />
                            : <span className="text-secondary">—</span>}
                        />
                        <Column
                          field="cascade_severity"
                          header="Cascade severity"
                          sortable filter dataType="numeric"
                          filterElement={renderNumericFilter}
                          filterPlaceholder="1 to 4"
                          body={(row) => {
                            const sev = row.cascade_severity;
                            const sevMap = {
                              1: { sev: 'secondary', label: '1 layer' },
                              2: { sev: 'info',      label: '2 layers' },
                              3: { sev: 'warning',   label: '3 layers' },
                              4: { sev: 'danger',    label: '4 layers' },
                            };
                            const m = sevMap[sev] || sevMap[1];
                            return <Tag value={m.label} severity={m.sev} />;
                          }}
                        />
                        <Column field="col6" header="Source" sortable filter dataType="text" filterPlaceholder="Search by Source"
                          body={(row) => (
                            <Tag value={row.col6} severity={row.col6 === 'UniProt' ? 'info' : 'success'} />
                          )}
                        />
                    </ColumnToggleDataTable>

                    {/* ───────── Site Structure (Mol* viewer) ───────── */}
                    {mode === 'level4' && result?.data?.length > 0
                      && (result?.enrichmentStats?.pinnedStructure || formData.pdbId) && (() => {
                      const structureId = result?.enrichmentStats?.pinnedStructure || formData.pdbId;
                      const sd = result.enrichmentStats?.structuresData?.[submittedPdbIdLevel4] || {};
                      const enrMap = {};
                      (sd.enrichment_plot_data || []).forEach(e => { enrMap[e.pos] = e.score; });
                      const maxEnr = Math.max(0, ...Object.values(enrMap));
                      return (
                        <>
                          <div className="d-flex align-items-center mt-4 mb-3">
                            <h5 className="mb-0 text-primary"><Route className="me-2" />Site Structure</h5>
                          </div>

                         {/* control bar */}

                          <div className="lvl4-ctl d-flex flex-nowrap align-items-end gap-2" style={{ marginBottom: '8rem' }}>
                            <div className="flex-shrink-0">
                              <label className="form-label fw-bold text-secondary mb-1 d-block">Colour by</label>
                              <SelectButton
                                value={level4ViewerMode}
                                onChange={(e) => e.value && setLevel4ViewerMode(e.value)}
                                options={[
                                  { label: 'Variants',   value: 'variants' },
                                  { label: 'Enrichment', value: 'enrichment' },
                                  { label: 'Severity',   value: 'severity' },
                                ]}
                              />
                            </div>
                            <div className="flex-shrink-0">
                              <label className="form-label fw-bold text-secondary mb-1  d-block">Site type</label>
                              <SelectButton
                                value={level4ViewerSite}
                                onChange={(e) => e.value && setLevel4ViewerSite(e.value)}
                                options={[
                                  { label: 'Both',    value: 'both' },
                                  { label: 'Binding', value: 'binding' },
                                  { label: 'Active',  value: 'active' },
                                ]}
                              />
                            </div>
                            <div className="col-md-3">
                              <label className="form-label fw-bold text-secondary mb-1 d-block">Show in structure</label>
                              <MultiSelect
                                value={level4ViewerFilters}
                                onChange={(e) => setLevel4ViewerFilters(e.value || [])}
                                options={[
                                  { label: 'Pathogenic',                     value: 'pathogenic' },
                                  { label: 'Likely pathogenic',              value: 'likely_pathogenic' },
                                  { label: 'Pathogenic / Likely pathogenic', value: 'pathogenic/likely_pathogenic' },
                                  { label: 'Conflicting',                    value: 'conflicting_classifications_of_pathogenicity' },
                                  { label: 'Uncertain (VUS)',                value: 'uncertain_significance' },
                                  { label: 'Likely benign',                  value: 'likely_benign' },
                                  { label: 'Benign',                         value: 'benign' },
                                ]}
                                placeholder="All categories"
                                display="chip"
                                className="w-100"
                                style={{ width: '100%' }}
                              />
                            </div>
                          </div>

                          {/* Viewer — full width */}
                          <div className="viewer-box mb-2" style={{ minHeight: '500px' }}>
                            {level4Mol.loading && (
                              <div className="text-secondary small text-center py-5">
                                <i className="pi pi-spin pi-spinner me-2" />
                                Mapping residues onto {structureId}…
                              </div>
                            )}
                            {!level4Mol.loading && level4Mol.ready && (() => {
                              const byPos = level4Mol.byPos;
                              const seen = new Set();
                              const items = [];
                              (result.data || []).forEach(r => {
                                if (level4ViewerSite !== 'both' && r.site_type !== level4ViewerSite) return;
                                if (level4ViewerFilters.length > 0
                                    && !level4ViewerFilters.includes(String(r.variant_pathogenicity || '').toLowerCase().trim())) return;
                                const parts = String(r.col1 || '').split('_');
                                const num = parts.length > 1 ? parseInt(parts[1], 10) : NaN;
                                if (!Number.isInteger(num)) return;
                                const m = byPos[num];
                                if (!m?.mapped || seen.has(r.col1)) return;
                                seen.add(r.col1);
                                // One item per chain: Mol* selects chains separately.
                                (m.chains || ['A']).forEach(ch => {
                                  items.push({ mapped: m.mapped, chain: ch, severity: r.cascade_severity || 1, enrichment: enrMap[r.col1] ?? 0 });
                                });
                              });
                              if (items.length === 0) {
                                return (
                                  <div className="text-secondary small text-center py-5">
                                    No mappable residues for this structure / filter.
                                  </div>
                                );
                              }
                              if (level4ViewerMode === 'variants') {
                                return (
                                  <MolstarViewerPath
                                    pdbId={structureId}
                                    level="3"
                                    positions={items.map(i => i.mapped)}
                                    chain={items.map(i => i.chain)}
                                  />
                                );
                              }
                              const key = level4ViewerMode === 'enrichment' ? 'enrichment' : 'severity';
                              const datatable = items.map(i => ({ col1: i.mapped, col2: i[key] ?? 0, chain: i.chain }));
                              return (
                                <MolstarViewerPath
                                  pdbId={structureId}
                                  level="2"
                                  datatable={datatable}
                                  centralityMetric="col2"
                                />
                              );
                            })()}
                          </div>

                          {/* Legend (linear gradient — as in Level 3) */}
                          {level4ViewerMode === 'enrichment' && (
                            <div className="mt-2 p-3 border rounded bg-light mb-4">
                              <div className="d-flex justify-content-between mb-1 small fw-bold text-secondary">
                                <span>Min: <strong>0</strong></span>
                                <span className="text-center text-uppercase">3D Neighborhood Enrichment</span>
                                <span>Max: <strong>{maxEnr}</strong></span>
                              </div>
                              <div style={{ height: '12px', background: 'linear-gradient(to right, #3B82F6 0%, #FBBF24 50%, #EF4444 100%)', borderRadius: '6px' }} />
                              <div className="d-flex justify-content-between mt-2 small text-500">
                                <div className="text-start" style={{ width: '45%' }}>Blue: low pathogenic-variant density in the residue's 3D neighbourhood.</div>
                                <div className="text-end" style={{ width: '45%' }}>Red: high density — clustered pathogenic variants forming a structural hotspot.</div>
                              </div>
                            </div>
                          )}
                          {level4ViewerMode === 'severity' && (
                            <div className="mt-2 p-3 border rounded bg-light mb-4">
                              <div className="d-flex justify-content-between mb-1 small fw-bold text-secondary">
                                <span>Min: <strong>1</strong></span>
                                <span className="text-center text-uppercase">Cascade severity (converging functional layers)</span>
                                <span>Max: <strong>4</strong></span>
                              </div>
                              <div style={{ height: '12px', background: 'linear-gradient(to right, #3B82F6 0%, #FBBF24 50%, #EF4444 100%)', borderRadius: '6px' }} />
                              <div className="d-flex justify-content-between mt-2 small text-500">
                                <div className="text-start" style={{ width: '45%' }}>Blue: variant only (severity 1).</div>
                                <div className="text-end" style={{ width: '45%' }}>Red: variant + PTM + binding + active converge (severity 4).</div>
                              </div>
                            </div>
                          )}
                          {level4ViewerMode === 'variants' && (
                            <div className="d-flex mb-4">
                              <div className="burble-variant"></div>
                              <p className="fs--2 text-justify">Variant residues highlighted on the structure.</p>
                            </div>
                          )}
                        </>
                      );
                    })()}

                    {/* ───────── 2 Fisher banners side by side ───────── */}
                    {result.enrichmentStats && (() => {
                      const renderFisher = (block, title) => {
                        if (!block) {
                          return (
                            <div className="p-3 mb-2 text-start border rounded bg-white h-100">
                              <h5 className="mb-2 text-secondary">{title}</h5>
                              <span className="text-muted small">No data available.</span>
                            </div>
                          );
                        }
                        const pSig = block.pValue != null && block.pValue < 0.05;
                        // Badge from the CI (robust only if the whole 95% CI is above 1),
                        // consistent with L3/L9. A CI crossing 1 is inconclusive even if p < 0.05.
                        const ciRobust = block.ciLow != null && block.ciLow > 1;
                        const num = (color = '#212529') => ({ fontSize: '1.7rem', fontWeight: 700, color, lineHeight: 1.1 });
                        const orStr = block.oddsRatioIsInfinite ? '∞' : (block.oddsRatio == null ? '—' : block.oddsRatio);
                        const ciStr = (block.ciLow == null) ? '—'
                          : `${block.ciLow} – ${block.ciHighIsInfinite ? '∞' : block.ciHigh}`;
                        const site_b = Math.max(0, (block.interfaceTotal || 0) - (block.observed || 0));      // site benign
                        const bg_d = Math.max(0, (block.backgroundTotal || 0) - (block.backgroundPathogenic || 0)); // rest benign
                        const iPct = block.interfaceTotal > 0 ? Math.round(100 * block.observed / block.interfaceTotal) : 0;
                        const bPct = block.backgroundTotal > 0 ? Math.round(100 * block.backgroundPathogenic / block.backgroundTotal) : 0;
                        return (
                          <div className="p-3 mb-2 text-start border rounded bg-white h-100">
                            <div className="d-flex align-items-center justify-content-between mb-4">
                              <h4 className="mb-0">
                                <i className="pi pi-chart-bar me-2 text-primary" /> {title}
                              </h4>
                              <Tag
                                value={ciRobust ? 'Robust enrichment' : 'Inconclusive'}
                                severity={ciRobust ? 'success' : 'warning'}
                              />
                            </div>
                            <div className="row g-2 mb-2">
                              <div className="col-md-4 col-6">
                                <div className="card p-3 border-start border-3 border-secondary h-100 text-center">
                                  <div className="text-secondary small mb-1">Fold enrichment</div>
                                  <div className="fw-bold" style={num('#3B82F6')}>
                                    {block.foldEnrichment}<span style={{ fontSize: '1.1rem' }}>×</span>
                                  </div>
                                  <div className="text-secondary" style={{ fontSize: '0.7rem' }}>
                                    {iPct}% vs {bPct}% pathogenic
                                  </div>
                                </div>
                              </div>
                              <div className="col-md-4 col-6">
                                <div className="card p-3 border-start border-3 border-secondary h-100 text-center">
                                  <div className="text-secondary small mb-1">Odds ratio</div>
                                  <div className="fw-bold" style={num('#6c757d')}>{orStr}</div>
                                  <div className="text-secondary" style={{ fontSize: '0.7rem' }}>95% CI {ciStr}</div>
                                </div>
                              </div>
                              <div className="col-md-4 col-6">
                                <div className="card p-3 border-start border-3 border-secondary h-100 text-center">
                                  <div className="text-secondary small mb-1">P-value (Fisher)</div>
                                  <div className="fw-bold" style={num(pSig ? '#22c55e' : '#212529')}>
                                    {block.pValue < 0.001 ? block.pValue.toExponential(2) : block.pValue.toFixed(4)}
                                  </div>
                                  <div className="text-secondary" style={{ fontSize: '0.7rem' }}>two-sided</div>
                                </div>
                              </div>
                            </div>
                            {block.backgroundTotal > 0 && (
                              <div className="text-500 mt-2" style={{ fontSize: '0.72rem' }}>
                                Among ClinVar-classified residues{block.structure ? ` on structure ${block.structure}` : ''}, the site has
                                {' '}{block.observed} pathogenic / {site_b} benign ({iPct}% pathogenic), vs {block.backgroundPathogenic} pathogenic / {bg_d} benign
                                {' '}({bPct}%) in the rest of the resolved structure.
                              </div>
                            )}
                          </div>
                        );
                      };
                      return (
                        <div className="row g-3 mb-3">
                          <div className="col-md-6">
                            {renderFisher(result.enrichmentStats.bindingFisher, 'Binding sites')}
                          </div>
                          <div className="col-md-6">
                            {renderFisher(result.enrichmentStats.activeFisher, 'Active sites')}
                          </div>
                        </div>
                      );
                    })()}

                    {/* ───────── 5 plots (+ optional Sankey) ───────── */}
                    {submittedPdbIdLevel4 && (() => {
                      const sd = result.enrichmentStats?.structuresData?.[submittedPdbIdLevel4];
                      if (!sd) return null;

                      const proximityData    = sd.proximity_plot_data    || [];
                      const enrichmentData   = sd.enrichment_plot_data   || [];
                      const multifeatureData = sd.multifeature_plot_data || {};
                      const cascadeData      = sd.cascade_plot_data      || {};
                      const sankeyData       = sd.sankey_plot_data       || null;

                      // Plot revision token (forces redraw on structure change)
                      const plotRevision = (proximityData?.length ?? 0)
                                         + (enrichmentData?.length ?? 0)
                                         + Object.keys(multifeatureData).length
                                         + Object.keys(cascadeData).length
                                         + String(submittedPdbIdLevel4).length;

                      const BINDING_COLOR = '#3B82F6';
                      const ACTIVE_COLOR  = '#8B5CF6';

                      // ── Variant composition by position — stacked by ClinVar category
                      const VARIANT_CATS = [
                        { key: 'pathogenic',         label: 'Pathogenic',                color: '#ef4444' },
                        { key: 'conflicting',        label: 'Conflicting',               color: '#06b6d4' },
                        { key: 'vus_am_pathogenic',  label: 'VUS + AM likely-pathogenic', color: '#f97316' },
                        { key: 'uncertain',          label: 'Uncertain (VUS)',           color: '#9ca3af' },
                        { key: 'benign',             label: 'Benign / Likely-benign',    color: '#22c55e' },
                      ];
                      const categoriseRow = (row) => {
                        const p  = String(row.variant_pathogenicity || '').toLowerCase();
                        const am = String(row.col3 || '').toLowerCase();
                        if (p === 'pathogenic' || p === 'likely_pathogenic' || p === 'pathogenic/likely_pathogenic') return 'pathogenic';
                        if (p === 'conflicting_classifications_of_pathogenicity') return 'conflicting';
                        if (p === 'uncertain_significance') return am === 'likely_pathogenic' ? 'vus_am_pathogenic' : 'uncertain';
                        if (p === 'likely_benign' || p === 'benign') return 'benign';
                        return 'uncertain';
                      };

                      const buildVariantBuckets = (siteType) => {
                        const seen = new Set();
                        const byPos = {};
                        (result.data || []).forEach(r => {
                          // include all buckets: direct UniProt-annotated sites + PDB/AF hop-1 neighbours
                          if (r.site_type !== siteType) return;
                          const dedupKey = `${r.col1}::${r.col2}::${siteType}`;
                          if (seen.has(dedupKey)) return;
                          seen.add(dedupKey);
                          const cat = categoriseRow(r);
                          if (!byPos[r.col1]) {
                            byPos[r.col1] = { pathogenic: 0, conflicting: 0, vus_am_pathogenic: 0, uncertain: 0, benign: 0 };
                          }
                          byPos[r.col1][cat] += 1;
                        });
                        // Sort positions by total count desc (most prominent first)
                        const positions = Object.keys(byPos).sort((a, b) => {
                          const sA = Object.values(byPos[a]).reduce((s, n) => s + n, 0);
                          const sB = Object.values(byPos[b]).reduce((s, n) => s + n, 0);
                          return sB - sA;
                        });
                        return { positions, byPos };
                      };

                      const bindingBuckets = buildVariantBuckets('binding');
                      const activeBuckets  = buildVariantBuckets('active');

                      const buildVariantTraces = (bucket) => {
                        return VARIANT_CATS.map(cat => ({
                          x: bucket.positions,
                          y: bucket.positions.map(p => bucket.byPos[p][cat.key] || 0),
                          type: 'bar',
                          name: cat.label,
                          marker: { color: cat.color },
                        }));
                      };

                      // sevLabels is reused by the cascade-severity computation below.
                      const sevLabels = ['1', '2', '3', '4'];

                      // ── Plot 4: Multi-feature residues
                      const FEATURE_COMBOS = [
                        { key: 'PTM',                    color: '#f59e0b' },
                        { key: 'Binding',                color: '#3B82F6' },
                        { key: 'Active',                 color: '#8B5CF6' },
                        { key: 'PTM + Binding',          color: '#14b8a6' },
                        { key: 'PTM + Active',           color: '#ec4899' },
                        { key: 'Binding + Active',       color: '#6366f1' },
                        { key: 'PTM + Binding + Active', color: '#dc2626' },
                      ];
                      const multiFeatureByPos = (() => {
                        const byPos = {};
                        (result.data || []).forEach(r => {
                          // include all buckets: direct UniProt-annotated sites + PDB/AF hop-1 neighbours
                          const pos = r.col1;
                          if (!byPos[pos]) byPos[pos] = { ptm: false, binding: false, active: false };
                          if (r.has_ptm) byPos[pos].ptm = true;
                          if (Number(r.hop) === 0 && r.site_type === 'binding') byPos[pos].binding = true;
                          if (Number(r.hop) === 0 && r.site_type === 'active')  byPos[pos].active  = true;
                        });
                        return byPos;
                      })();
                      const comboLabel = (f) => {
                        const parts = [];
                        if (f.ptm)     parts.push('PTM');
                        if (f.binding) parts.push('Binding');
                        if (f.active)  parts.push('Active');
                        return parts.join(' + ');
                      };
                      // layers bin = 1 + (#features); equals backend cascade_severity.
                      const multiFeatureCounts = {};
                      Object.values(multiFeatureByPos).forEach(f => {
                        const nFeat = (f.ptm ? 1 : 0) + (f.binding ? 1 : 0) + (f.active ? 1 : 0);
                        if (nFeat < 1) return;
                        const layers = nFeat + 1;
                        const label = comboLabel(f);
                        multiFeatureCounts[layers] = multiFeatureCounts[layers] || {};
                        multiFeatureCounts[layers][label] =
                          (multiFeatureCounts[layers][label] || 0) + 1;
                      });
                      const multiFeatureLayers = ['2', '3', '4'].filter(
                        s => multiFeatureCounts[s]
                          && Object.values(multiFeatureCounts[s]).reduce((a, b) => a + b, 0) > 0
                      );
                      const multiTraces = FEATURE_COMBOS.map(c => ({
                        x: multiFeatureLayers.map(s => `${s} layers`),
                        y: multiFeatureLayers.map(s => (multiFeatureCounts[s]?.[c.key]) || 0),
                        name: c.key,
                        type: 'bar',
                        marker: { color: c.color },
                        hovertemplate: `<b>${c.key}</b><br>%{x}: %{y} residue(s)<extra></extra>`,
                      }));

                      // ── Plot 5: Cascade severity as a pathogenicity-enrichment
                      const CASCADE_DISPLAY_CATS = [
                        { key: 'pathogenic',  label: 'Pathogenic',             color: '#ef4444' },
                        { key: 'conflicting', label: 'Conflicting',            color: '#06b6d4' },
                        { key: 'uncertain',   label: 'VUS / Uncertain',        color: '#9ca3af' },
                        { key: 'benign',      label: 'Benign / Likely-benign', color: '#22c55e' },
                      ];
                      const cascadeCatOf = (r) => {
                        const p = String(r.variant_pathogenicity || '').toLowerCase();
                        if (p === 'pathogenic' || p === 'likely_pathogenic'
                            || p === 'pathogenic/likely_pathogenic') return 'pathogenic';
                        if (p === 'conflicting_classifications_of_pathogenicity') return 'conflicting';
                        if (p === 'likely_benign' || p === 'benign') return 'benign';
                        return 'uncertain';
                      };
                      const cascadeBySev = (() => {
                        const seenVar = new Set();
                        const bySev = {};
                        (result.data || []).forEach(r => {
                          // include all buckets: direct UniProt-annotated sites + PDB/AF hop-1 neighbours
                          const vKey = `${r.col1}::${r.col2}`;
                          if (seenVar.has(vKey)) return;
                          seenVar.add(vKey);
                          const sev = r.cascade_severity || 1;
                          const cat = cascadeCatOf(r);
                          bySev[sev] = bySev[sev] || {};
                          bySev[sev][cat] = (bySev[sev][cat] || 0) + 1;
                        });
                        return bySev;
                      })();
                      const cascadeSevPresent = sevLabels.filter(
                        s => cascadeBySev[s]
                          && Object.values(cascadeBySev[s]).reduce((a, b) => a + b, 0) > 0
                      );
                      const cascadeSevTotals = {};
                      cascadeSevPresent.forEach(s => {
                        cascadeSevTotals[s] = Object.values(cascadeBySev[s])
                          .reduce((a, b) => a + b, 0);
                      });
                      const cascadePctTraces = CASCADE_DISPLAY_CATS.map(c => ({
                        x: cascadeSevPresent.map(s => `Severity ${s}`),
                        y: cascadeSevPresent.map(s =>
                          cascadeSevTotals[s]
                            ? 100 * (cascadeBySev[s][c.key] || 0) / cascadeSevTotals[s]
                            : 0),
                        customdata: cascadeSevPresent.map(s =>
                          [cascadeBySev[s][c.key] || 0, cascadeSevTotals[s]]),
                        name: c.label,
                        type: 'bar',
                        marker: { color: c.color },
                        hovertemplate:
                          `<b>%{x}</b><br>${c.label}: %{y:.1f}%`
                          + `<br>(%{customdata[0]} of %{customdata[1]} variants)<extra></extra>`,
                      }));
                      const cascadeNAnnotations = cascadeSevPresent.map(s => ({
                        x: `Severity ${s}`,
                        y: 100,
                        yanchor: 'bottom',
                        text: `n=${cascadeSevTotals[s]}`,
                        showarrow: false,
                        font: { size: 10, color: '#475569' },
                      }));
                      // ── Cochran–Armitage trend test: does the pathogenic fraction
                      // rise monotonically with severity? scores = severity number,
                      // successes = pathogenic variants, one row per distinct variant.
                      // One-sided (H1: increasing). Turns the "ladder" into evidence.
                      const cascadeTrend = (() => {
                        const erf = (x) => {
                          const sign = x < 0 ? -1 : 1;
                          const ax = Math.abs(x);
                          const t = 1 / (1 + 0.3275911 * ax);
                          const y = 1 - (((((1.061405429 * t - 1.453152027) * t)
                            + 1.421413741) * t - 0.284496736) * t + 0.254829592)
                            * t * Math.exp(-ax * ax);
                          return sign * y;
                        };
                        const Phi = (z) => 0.5 * (1 + erf(z / Math.SQRT2));
                        const groups = cascadeSevPresent.map(s => ({
                          t: Number(s),
                          n: cascadeSevTotals[s],
                          x: cascadeBySev[s].pathogenic || 0,
                        })).filter(g => g.n > 0);
                        if (groups.length < 2) return null;
                        const N = groups.reduce((a, g) => a + g.n, 0);
                        const R = groups.reduce((a, g) => a + g.x, 0);
                        if (R === 0 || R === N) return null;
                        const T1 = groups.reduce((a, g) => a + g.n * g.t, 0);
                        const T2 = groups.reduce((a, g) => a + g.n * g.t * g.t, 0);
                        const Sxt = groups.reduce((a, g) => a + g.x * g.t, 0);
                        const stat = N * Sxt - R * T1;
                        const varT = (R * (N - R) / N) * (N * T2 - T1 * T1);
                        if (varT <= 0) return null;
                        const z = stat / Math.sqrt(varT);
                        const pOne = 1 - Phi(z);
                        return { z, pOne, N, R, k: groups.length };
                      })();

                      return (
                        <div className="row g-3 mb-3 mt-2">

                          {/* ─────────── Row: Variant composition split by site type ─────────── */}
                          <div className="col-md-6">
                            <div className="p-3 border rounded bg-white">
                              <h6 className="mb-2 text-primary">
                                <i className="pi pi-chart-bar me-2" /> Variant composition — Binding sites
                              </h6>
                              {bindingBuckets.positions.length === 0 ? (
                                <div className="text-secondary small text-center py-4">
                                  No binding-site variants in this structure.
                                </div>
                              ) : (
                                <Plot
                                  revision={plotRevision}
                                  data={buildVariantTraces(bindingBuckets)}
                                  layout={{ height: 360,
                                    barmode: 'stack',
                                    margin: { t: 10, b: 110, l: 50, r: 10 },
                                    xaxis: { title: 'Residue position', type: 'category' },
                                    yaxis: { title: 'Variant count', dtick: 1 },
                                    legend: { orientation: 'h', y: -0.5, x: 0, xanchor: 'left', font: { size: 9 } },
                                    font: { size: 10 },
                                  }}
                                  config={{ displayModeBar: 'hover', displaylogo: false, responsive: true }}
                                  style={{ width: '100%' }}
                                  useResizeHandler
                                />
                              )}
                            </div>
                          </div>

                          <div className="col-md-6">
                            <div className="p-3 border rounded bg-white">
                              <h6 className="mb-2 text-primary">
                                <i className="pi pi-chart-bar me-2" /> Variant composition — Active sites
                              </h6>
                              {activeBuckets.positions.length === 0 ? (
                                <div className="text-secondary small text-center py-4">
                                  No active-site variants in this structure.
                                </div>
                              ) : (
                                <Plot
                                  revision={plotRevision}
                                  data={buildVariantTraces(activeBuckets)}
                                  layout={{ height: 360,
                                    barmode: 'stack',
                                    margin: { t: 10, b: 110, l: 50, r: 10 },
                                    xaxis: { title: 'Residue position', type: 'category' },
                                    yaxis: { title: 'Variant count', dtick: 1 },
                                    legend: { orientation: 'h', y: -0.5, x: 0, xanchor: 'left', font: { size: 9 } },
                                    font: { size: 10 },
                                  }}
                                  config={{ displayModeBar: 'hover', displaylogo: false, responsive: true }}
                                  style={{ width: '100%' }}
                                  useResizeHandler
                                />
                              )}
                            </div>
                          </div>

                          {/* ─────────── Row: 3D Enrichment split by site type ─────────── */}
                          <div className="col-md-6">
                            <div className="p-3 border rounded bg-white">
                              <h6 className="mb-2 text-primary">
                                <i className="pi pi-bolt me-2" /> 3D Neighborhood Enrichment — Binding sites
                              </h6>
                              {(() => {
                                const data = enrichmentData
                                  .filter(d => d.site_type === 'binding' && d.score > 0);
                                if (data.length === 0) {
                                  return (
                                    <div className="text-secondary small text-center py-4">
                                      No binding-site enrichment data in this structure.
                                    </div>
                                  );
                                }
                                return (
                                  <Plot
                                    revision={plotRevision}
                                    data={[{
                                      x: data.map(d => d.pos),
                                      y: data.map(d => d.score),
                                      type: 'bar',
                                      name: 'Binding site',
                                      marker: { color: BINDING_COLOR },
                                    }]}
                                    layout={{ height: 360,
                                      margin: { t: 10, b: 100, l: 50, r: 10 },
                                      xaxis: { title: 'Residue position', type: 'category' },
                                      yaxis: { title: 'Enrichment ratio' },
                                      font: { size: 9 },
                                    }}
                                    config={{ displayModeBar: 'hover', displaylogo: false, responsive: true }}
                                    style={{ width: '100%' }}
                                    useResizeHandler
                                  />
                                );
                              })()}
                            </div>
                          </div>

                          <div className="col-md-6">
                            <div className="p-3 border rounded bg-white">
                              <h6 className="mb-2 text-primary">
                                <i className="pi pi-bolt me-2" /> 3D Neighborhood Enrichment — Active sites
                              </h6>
                              {(() => {
                                const data = enrichmentData
                                  .filter(d => d.site_type === 'active' && d.score > 0);
                                if (data.length === 0) {
                                  return (
                                    <div className="text-secondary small text-center py-4">
                                      No active-site enrichment data in this structure.
                                    </div>
                                  );
                                }
                                return (
                                  <Plot
                                    revision={plotRevision}
                                    data={[{
                                      x: data.map(d => d.pos),
                                      y: data.map(d => d.score),
                                      type: 'bar',
                                      name: 'Active site',
                                      marker: { color: ACTIVE_COLOR },
                                    }]}
                                    layout={{ height: 360,
                                      margin: { t: 10, b: 100, l: 50, r: 10 },
                                      xaxis: { title: 'Residue position', type: 'category' },
                                      yaxis: { title: 'Enrichment ratio' },
                                      font: { size: 9 },
                                    }}
                                    config={{ displayModeBar: 'hover', displaylogo: false, responsive: true }}
                                    style={{ width: '100%' }}
                                    useResizeHandler
                                  />
                                );
                              })()}
                            </div>
                          </div>

                          {/* ─────────── Row: Proximity + Multi-feature ─────────── */}
                          <div className="col-md-6">
                            <div className="p-3 border rounded bg-white">
                              <h6 className="mb-2 text-primary">
                                <i className="pi pi-chart-bar me-2" /> Interface Proximity Distribution
                              </h6>
                              <Plot
                                revision={plotRevision}
                                data={[
                                  {
                                    y: proximityData.filter(d => d.site_type === 'binding').map(d => d.distance),
                                    type: 'box',
                                    name: 'Binding site',
                                    boxpoints: 'all',
                                    jitter: 0.3,
                                    marker: { color: BINDING_COLOR, size: 5, opacity: 0.6 },
                                  },
                                  {
                                    y: proximityData.filter(d => d.site_type === 'active').map(d => d.distance),
                                    type: 'box',
                                    name: 'Active site',
                                    boxpoints: 'all',
                                    jitter: 0.3,
                                    marker: { color: ACTIVE_COLOR, size: 5, opacity: 0.6 },
                                  },
                                ]}
                                layout={{ height: 360,
                                  margin: { t: 10, b: 60, l: 50, r: 10 },
                                  yaxis: { title: 'Distance (Å)' },
                                  font: { size: 10 },
                                  legend: { orientation: 'h', y: -0.2, x: 0 },
                                }}
                                config={{ displayModeBar: 'hover', displaylogo: false, responsive: true }}
                                style={{ width: '100%' }}
                                useResizeHandler
                              />
                            </div>
                          </div>

                          <div className="col-md-6">
                            <div className="p-3 border rounded bg-white">
                              <h6 className="mb-2 text-primary">
                                <i className="pi pi-th-large me-2" /> Multi-feature residues
                              </h6>
                              <Plot
                                revision={plotRevision}
                                data={multiTraces}
                                layout={{ height: 360,
                                  barmode: 'stack',
                                  margin: { t: 10, b: 100, l: 50, r: 10 },
                                  xaxis: { title: 'Functional layers converging on residue' },
                                  yaxis: { title: 'Residues (deduplicated by position)' },
                                  legend: { orientation: 'h', y: -0.4, x: 0, xanchor: 'left', font: { size: 10 } },
                                  font: { size: 10 },
                                }}
                                config={{ displayModeBar: 'hover', displaylogo: false, responsive: true }}
                                style={{ width: '100%' }}
                                useResizeHandler
                              />
                            </div>
                          </div>

                          {/* ─────────── Row: Cascade severity + optional Sankey ─────────── */}
                          <div className="col-md-6">
                            <div className="p-3 border rounded bg-white">
                              <h6 className="mb-2 text-primary">
                                <i className="pi pi-sitemap me-2" /> Cascade severity — pathogenicity enrichment
                              </h6>
                              <Plot
                                revision={plotRevision}
                                data={cascadePctTraces}
                                layout={{ height: 360,
                                  barmode: 'stack',
                                  margin: { t: 20, b: 100, l: 50, r: 10 },
                                  xaxis: { title: 'Severity (number of converging functional layers)' },
                                  yaxis: { title: '% of variants', range: [0, 100], ticksuffix: '%' },
                                  annotations: cascadeNAnnotations,
                                  legend: { orientation: 'h', y: -0.4, x: 0, xanchor: 'left', font: { size: 10 } },
                                  font: { size: 10 },
                                }}
                                config={{ displayModeBar: 'hover', displaylogo: false, responsive: true }}
                                style={{ width: '100%' }}
                                useResizeHandler
                              />
                              {cascadeTrend ? (
                                <div className={`small mt-2 mb-0 ${cascadeTrend.pOne < 0.05 ? 'text-success' : 'text-secondary'}`}>
                                  <i className={`pi ${cascadeTrend.pOne < 0.05 ? 'pi-check-circle' : 'pi-info-circle'} me-1`} />
                                  Cochran–Armitage trend: Z = {cascadeTrend.z.toFixed(2)},{' '}
                                  p = {cascadeTrend.pOne < 0.001 ? cascadeTrend.pOne.toExponential(2) : cascadeTrend.pOne.toFixed(4)}{' '}
                                  (one-sided; pathogenic fraction rising with severity · {cascadeTrend.k} bins · N = {cascadeTrend.N})
                                </div>
                              ) : (
                                <div className="small mt-2 mb-0 text-secondary">
                                  <i className="pi pi-info-circle me-1" />
                                  Trend test needs ≥ 2 severity bins containing variants.
                                </div>
                              )}
                            </div>
                          </div>

                          {sankeyData && sankeyData.nodes && sankeyData.nodes.length > 0 && (
                            <div className="col-md-6">
                              <div className="p-3 border rounded bg-white">
                                <h6 className="mb-2 text-primary">
                                  <i className="pi pi-arrows-h me-2" /> Cascade flow (Sankey)
                                </h6>
                                <Plot
                                  revision={plotRevision}
                                  data={[{
                                    type: 'sankey',
                                    orientation: 'h',
                                    node: {
                                      pad: 15,
                                      thickness: 18,
                                      line: { color: '#cbd5e1', width: 0.5 },
                                      label: sankeyData.nodes,
                                      color: sankeyData.nodes.map(() => '#8B5CF6'),
                                    },
                                    link: {
                                      source: sankeyData.source,
                                      target: sankeyData.target,
                                      value:  sankeyData.value,
                                    },
                                  }]}
                                  layout={{ height: 360,
                                    margin: { t: 10, b: 10, l: 10, r: 10 },
                                    font: { size: 10 },
                                  }}
                                  config={{ displayModeBar: 'hover', displaylogo: false, responsive: true }}
                                  style={{ width: '100%' }}
                                  useResizeHandler
                                />
                              </div>
                            </div>
                          )}

                      {/* ─────────── Variant Cluster Synergy ─────────── */}
                        {(() => {
                          const clusters = result?.enrichmentStats?.variantClusterSynergy || [];
                          const pinned   = result?.enrichmentStats?.pinnedStructure || submittedPdbIdLevel4;
                          if (!clusters || clusters.length === 0) return null;

                          const BINDING_COLOR = '#3B82F6';
                          const ACTIVE_COLOR  = '#8B5CF6';

                          const bubbleData = ['binding', 'active'].map((stype) => {
                            const subset = clusters.filter((c) => c.site_type === stype);
                            return {
                              type: 'scatter',
                              mode: 'markers',
                              name: stype === 'binding' ? 'Binding site' : 'Active site',
                              x: subset.map((c) => c.site_position),
                              y: subset.map((c) => c.n_innocuous_variants),
                              customdata: subset.map((c) => [
                                c.target_name || '—',
                                c.n_innocuous_variants,
                                c.n_am_concerning,
                                c.avg_distance,
                              ]),
                              hovertemplate:
                                '<b>%{x}</b><br>' +
                                'Target: %{customdata[0]}<br>' +
                                'Innocuous variants: %{customdata[1]}<br>' +
                                'AM concerning: %{customdata[2]}<br>' +
                                'Mean distance: %{customdata[3]} Å' +
                                '<extra></extra>',
                              marker: {
                                size: subset.map((c) =>
                                  Math.max(10, 10 + 3 * (c.n_am_concerning || 0))
                                ),
                                color: stype === 'binding' ? BINDING_COLOR : ACTIVE_COLOR,
                                opacity: 0.75,
                                line: { width: 1, color: '#fff' },
                              },
                            };
                          });

                          // Flatten variants_detail to a compact string for the table body.
                          const renderTopVariants = (row) => {
                            const details = row.variants_detail || [];
                            const top = details.slice(0, 5)
                              .map((v) => `${v.pos}:${v.aa_change}`)
                              .join(', ');
                            const more = details.length > 5 ? ` … (+${details.length - 5})` : '';
                            return <span className="text-secondary small">{top}{more}</span>;
                          };

                          return (
                            <div className="col-12">
                              <div className="p-3 border rounded bg-white">
                                <h6 className="mb-2 text-primary">
  <i className="pi pi-objects-column me-2" />
  Variant Cluster Synergy
</h6>

<Plot
  revision={plotRevision}
  data={[
    {
      type: 'bar',
      orientation: 'h',
      name: 'AM concerning (ambiguous / likely_pathogenic)',
      y: clusters.map((c) => `${c.site_position} — ${c.target_name || '—'}`),
      x: clusters.map((c) => c.n_am_concerning),
      marker: { color: '#DC2626' },
      hovertemplate:
        '<b>%{y}</b><br>' +
        'AM concerning variants: %{x}' +
        '<extra></extra>',
    },
    {
      type: 'bar',
      orientation: 'h',
      name: 'Other innocuous variants',
      y: clusters.map((c) => `${c.site_position} — ${c.target_name || '—'}`),
      x: clusters.map((c) => c.n_innocuous_variants - c.n_am_concerning),
      marker: { color: '#94A3B8' },
      hovertemplate:
        '<b>%{y}</b><br>' +
        'Innocuous (no AM signal): %{x}' +
        '<extra></extra>',
    },
  ]}
  layout={{ autosize: true,
    height: Math.max(220, 32 * clusters.length + 80),
    barmode: 'stack',
    margin: { l: 200, r: 20, t: 10, b: 50 },
    xaxis: {
      title: 'Variants in cluster',
      rangemode: 'tozero',
    },
    yaxis: {
      autorange: 'reversed',
      automargin: true,
    },
    legend: { orientation: 'h', y: -0.2 },
    showlegend: true,
    hovermode: 'closest',
  }}
  config={{ displayModeBar: 'hover', displaylogo: false, responsive: true }}
  style={{ width: '100%' }}
  useResizeHandler
/>

                                {/* ─── Cluster table  ─── */}
                                <ColumnToggleDataTable value={clusters} className="nobreak" paginator rows={5} stripedRows showGridlines filterDisplay="menu" tableStyle={{ minWidth: '66rem' }} responsiveLayout="scroll">
                                  <Column
                                    field="site_position"
                                    header="Site position"
                                    sortable filter dataType="text"
                                    filterPlaceholder="Search position"
                                  />
                                  <Column
                                    field="site_type"
                                    header="Site type"
                                    sortable filter dataType="text"
                                    filterPlaceholder="binding or active"
                                    body={(row) => (
                                      <Tag
                                        value={row.site_type}
                                        style={{
                                          backgroundColor:
                                            row.site_type === 'binding' ? BINDING_COLOR : ACTIVE_COLOR,
                                          color: '#fff',
                                        }}
                                      />
                                    )}
                                  />
                                  <Column
                                    field="target_name"
                                    header="Target"
                                    sortable filter dataType="text"
                                    filterPlaceholder="Search target"
                                    body={(row) => row.target_name || '—'}
                                  />
                                  <Column
                                    field="n_innocuous_variants"
                                    header="# variants"
                                    sortable dataType="numeric"
                                    style={{ textAlign: 'right' }}
                                  />
                                  <Column
                                    field="n_am_concerning"
                                    header="# AM concerning"
                                    sortable dataType="numeric"
                                    style={{ textAlign: 'right' }}
                                    body={(row) => (
                                      <span
                                        className={
                                          row.n_am_concerning > 0
                                            ? 'text-danger fw-bold'
                                            : 'text-secondary'
                                        }
                                      >
                                        {row.n_am_concerning}
                                      </span>
                                    )}
                                  />
                                  <Column
                                    field="avg_distance"
                                    header="Avg distance (Å)"
                                    sortable dataType="numeric"
                                    style={{ textAlign: 'right' }}
                                  />
                                  <Column
                                    field="variants_detail"
                                    header="Top variants"
                                    body={renderTopVariants}
                                  />
                                </ColumnToggleDataTable>
                              </div>
                            </div>
                          );
                        })()}

                        {/* ─────────── Allosteric bridge residues (hypothesis) ─────────── */}
                        {(() => {
                          const bridges = result?.enrichmentStats?.allostericBridges || [];
                          if (!bridges || bridges.length === 0) return null;

                          const pathClass = (p) => {
                            const s = String(p || '').toLowerCase();
                            if (s.includes('pathogenic') && !s.includes('benign')) return 'text-danger fw-bold';
                            if (s.includes('benign')) return 'text-success';
                            if (s.includes('conflicting')) return 'text-info';
                            return 'text-secondary';
                          };

                          const siteDistByPos = (() => {
                            const m = {};
                            (result.data || []).forEach(r => {
                              const d = Number(r.col5);
                              if (!(d > 0)) return;
                              if (r.site_type !== 'binding' && r.site_type !== 'active') return;
                              const slot = m[r.col1] || (m[r.col1] = { binding: null, active: null });
                              if (slot[r.site_type] == null || d < slot[r.site_type]) slot[r.site_type] = d;
                            });
                            return m;
                          })();
                          const enrichedBridges = bridges.map(b => {
                            const sd = siteDistByPos[b.position] || { binding: null, active: null };
                            const binding_dist = b.binding_dist ?? sd.binding;
                            const active_dist  = b.active_dist  ?? sd.active;
                            const near_site_type = b.near_site_type
                              || (binding_dist != null && active_dist != null ? 'binding+active'
                                  : binding_dist != null ? 'binding'
                                  : active_dist != null ? 'active' : '—');
                            return { ...b, binding_dist, active_dist, near_site_type };
                          });
                          const bridgesSorted = [...enrichedBridges].sort(
                            (a, b) => ((a.ptm_dist || 0) + (a.site_dist ?? a.binding_dist ?? a.active_dist ?? 0))
                                    - ((b.ptm_dist || 0) + (b.site_dist ?? b.binding_dist ?? b.active_dist ?? 0))
                          );
                          const bridgeX = bridgesSorted.map(b => `${b.position} ${b.variant}`);
                          const bridgeHover = bridgesSorted.map(b => [b.pathogenicity || '—', b.am_class || '—', b.near_site_type || '—']);
                          const bridgeHasSplit = bridgesSorted.some(
                            b => b.binding_dist != null || b.active_dist != null
                          );
                          const bridgeBars = bridgeHasSplit
                            ? [
                                {
                                  type: 'bar', name: 'nearest PTM',
                                  x: bridgeX, y: bridgesSorted.map(b => b.ptm_dist),
                                  customdata: bridgeHover, marker: { color: '#f59e0b' },
                                  hovertemplate: '<b>%{x}</b><br>PTM: %{y} Å<br>near: %{customdata[2]}<br>ClinVar: %{customdata[0]}<br>AM: %{customdata[1]}<extra></extra>',
                                },
                                {
                                  type: 'bar', name: 'nearest binding site',
                                  x: bridgeX, y: bridgesSorted.map(b => (b.binding_dist ?? null)),
                                  customdata: bridgeHover, marker: { color: '#3B82F6' },
                                  hovertemplate: '<b>%{x}</b><br>Binding: %{y} Å<extra></extra>',
                                },
                                {
                                  type: 'bar', name: 'nearest active site',
                                  x: bridgeX, y: bridgesSorted.map(b => (b.active_dist ?? null)),
                                  customdata: bridgeHover, marker: { color: '#8B5CF6' },
                                  hovertemplate: '<b>%{x}</b><br>Active: %{y} Å<extra></extra>',
                                },
                              ]
                            : [
                                {
                                  type: 'bar', name: 'nearest PTM',
                                  x: bridgeX, y: bridgesSorted.map(b => b.ptm_dist),
                                  customdata: bridgeHover, marker: { color: '#f59e0b' },
                                  hovertemplate: '<b>%{x}</b><br>PTM: %{y} Å<br>ClinVar: %{customdata[0]}<br>AM: %{customdata[1]}<extra></extra>',
                                },
                                {
                                  type: 'bar', name: 'nearest site',
                                  x: bridgeX, y: bridgesSorted.map(b => b.site_dist),
                                  customdata: bridgeHover, marker: { color: '#3B82F6' },
                                  hovertemplate: '<b>%{x}</b><br>Site: %{y} Å<br>ClinVar: %{customdata[0]}<br>AM: %{customdata[1]}<extra></extra>',
                                },
                              ];

                          return (
                            <div className="col-12">
                              <div className="p-3 border rounded bg-white">
                                <h6 className="mb-2 text-primary">
                                  <i className="pi pi-share-alt me-2" />
                                  Allosteric bridge residues
                                  <Tag value={String(enrichedBridges.length)} severity="info" rounded className="ms-2" />
                                </h6>
                                <Plot
                                  data={bridgeBars}
                                  layout={{ height: 360,
                                    barmode: 'group',
                                    margin: { t: 10, b: 110, l: 50, r: 10 },
                                    xaxis: { automargin: true, tickangle: -45 },
                                    yaxis: { title: 'Distance (Å)', rangemode: 'tozero' },
                                    legend: { orientation: 'h', y: -0.35, x: 0 },
                                    font: { size: 10 },
                                    hovermode: 'closest',
                                  }}
                                  config={{ displayModeBar: 'hover', displaylogo: false, responsive: true }}
                                  style={{ width: '100%' }}
                                  useResizeHandler
                                />
                                <ColumnToggleDataTable value={enrichedBridges} className="nobreak" paginator rows={5} stripedRows showGridlines filterDisplay="menu" tableStyle={{ minWidth: '66rem' }} responsiveLayout="scroll">
                                  <Column
                                    field="position"
                                    header="Position"
                                    sortable filter dataType="numeric"
                                    filterPlaceholder="Position"
                                    style={{ textAlign: 'right' }}
                                  />
                                  <Column
                                    field="variant"
                                    header="Variant"
                                    sortable filter dataType="text"
                                    filterPlaceholder="Search variant"
                                  />
                                  <Column
                                    field="pathogenicity"
                                    header="ClinVar"
                                    sortable filter dataType="text"
                                    filterPlaceholder="Search"
                                    body={(row) => (
                                      <span className={pathClass(row.pathogenicity)}>
                                        {row.pathogenicity || '—'}
                                      </span>
                                    )}
                                  />
                                  <Column
                                    field="am_class"
                                    header="AlphaMissense"
                                    sortable filter dataType="text"
                                    filterPlaceholder="Search"
                                    body={(row) => row.am_class || '—'}
                                  />
                                  <Column
                                    field="ptm_dist"
                                    header="PTM (Å)"
                                    sortable dataType="numeric"
                                    style={{ textAlign: 'right' }}
                                  />
                                  {!bridgeHasSplit && (
                                  <Column
                                    field="site_dist"
                                    header="Site (Å)"
                                    sortable dataType="numeric"
                                    style={{ textAlign: 'right' }}
                                  />
                                  )}
                                  {bridgeHasSplit && (
                                  <Column
                                    field="binding_dist"
                                    header="Binding (Å)"
                                    sortable dataType="numeric"
                                    style={{ textAlign: 'right' }}
                                    body={(row) => (row.binding_dist ?? '—')}
                                  />
                                  )}
                                  {bridgeHasSplit && (
                                  <Column
                                    field="active_dist"
                                    header="Active (Å)"
                                    sortable dataType="numeric"
                                    style={{ textAlign: 'right' }}
                                    body={(row) => (row.active_dist ?? '—')}
                                  />
                                  )}
                                  {bridgeHasSplit && (
                                  <Column
                                    field="near_site_type"
                                    header="Near"
                                    sortable filter dataType="text"
                                    filterPlaceholder="Search"
                                    body={(row) => {
                                      const t = row.near_site_type || '';
                                      const color = t === 'binding' ? '#3B82F6'
                                        : t === 'active' ? '#8B5CF6' : '#DC2626';
                                      return <span className="fw-bold" style={{ color }}>{t || '—'}</span>;
                                    }}
                                  />
                                  )}
                                </ColumnToggleDataTable>
                              </div>
                            </div>
                          );
                        })()}

                        </div>
                      );
                    })()}

                 </Card>
              )}

              {/* --- RENDER: TABLE (Level 5 - Structure differences) --- */}
              {result && mode === 'level5' && (
                  <>
                      <Card className="shadow-sm animate__animated animate__fadeIn">
                          <div className="d-flex justify-content-between align-items-center mb-3">
                              <h4 className="text-primary mb-3"><Activity className="me-2"/>Structure differences</h4>
                              <Button
                                  label="Download CSV"
                                  icon="pi pi-download"
                                  severity="secondary"
                                  outlined
                                  size="small"
                                  onClick={downloadCSV}
                              />
                          </div>

                       <div className="card card-spm bg-light border-0 p-3 mb-4 text-start">
                           <h5 className="mb-3">
                               <i className="pi pi-info-circle me-2 text-primary" />
                               Residues where PDB and AlphaFold disagree in connectivity
                           </h5>
                           <p className="mb-3 small">
                               For every residue we compare its <strong>contact neighbourhood</strong> in the
                               experimental crystal (PDB) and in the AlphaFold model. Intra-chain residue–residue
                               contacts are read from the graph at <strong>&lt;&nbsp;8&nbsp;Å</strong> (PDB) and
                               <strong>&nbsp;&lt;&nbsp;8&nbsp;Å</strong> (AlphaFold); for each residue we keep the
                               <em> set</em> of partner positions in each structure — <em>P</em> (PDB) and
                               <em> A</em> (AlphaFold). Neighbourhood agreement is summarised
                               by the Jaccard index <em>J&nbsp;=&nbsp;|P&nbsp;∩&nbsp;A|&nbsp;/&nbsp;|P&nbsp;∪&nbsp;A|</em>,
                               and disagreement by the <em>rewiring&nbsp;=&nbsp;1&nbsp;−&nbsp;J</em>. Each residue is
                               assigned to one class:
                           </p>
                           <p className="mb-2">
                               <Tag value="Rigid core" icon="pi pi-lock" rounded style={{ backgroundColor: '#1e3a8a', color: '#fff' }} />
                               <span className="small text-secondary ms-2">
                                   <strong>Consensus</strong> neighbourhood — partners are largely shared
                                   (<em>J&nbsp;≥&nbsp;0.60</em>). The stable, immovable scaffold both structures agree on.
                               </span>
                           </p>
                           <p className="mb-2">
                               <Tag value="Hinge" icon="pi pi-sync" rounded style={{ backgroundColor: '#7c3aed', color: '#fff' }} />
                               <span className="small text-secondary ms-2">
                                   <strong>Rewired</strong> neighbourhood at <strong>preserved degree</strong>: the
                                   residue keeps a similar number of contacts (both&nbsp;≥&nbsp;3,
                                   |Δdegree|&nbsp;≤&nbsp;2) but exchanges most partners (<em>J&nbsp;&lt;&nbsp;0.40</em>) —
                                   the signature of an allosteric hinge or conformational switch.
                               </span>
                           </p>
                           <p className="mb-2">
                               <Tag value="Transient" icon="pi pi-bolt" rounded style={{ backgroundColor: '#f59e0b', color: '#fff' }} />
                               <span className="small text-secondary ms-2">
                                   Remaining residues (<em>0.40&nbsp;≤&nbsp;J&nbsp;&lt;&nbsp;0.60</em>): the
                                   neighbourhood is partly conserved and partly rewired. The
                                   <em> consensus</em> and <em>transient</em> columns show which of the two
                                   prevails for a given residue.
                               </span>
                           </p>
                           <p className="mb-0 small text-secondary">
                               <i className="pi pi-exclamation-circle me-1" />
                               Thresholds are configurable server-side. Differences are most trustworthy where AlphaFold
                               is confident (<strong>pLDDT&nbsp;&gt;&nbsp;70</strong>); low-confidence regions
                               (<strong>pLDDT&nbsp;&lt;&nbsp;50</strong>) are intrinsically disordered and their
                               disagreement is expected rather than informative.
                           </p>
                       </div>

                          <ColumnToggleDataTable value={result.data} sortField="rewiring" sortOrder={-1} className="mb-3 nobreak" paginator rows={5} stripedRows showGridlines filterDisplay="menu" tableStyle={{ minWidth: '66rem' }} responsiveLayout="scroll">
                              <Column field="col1" header="Residue" sortable></Column>
                              <Column field="structure_class" header="Class" sortable filter dataType="text"
                                  filterPlaceholder="rigid / hinge / transient"
                                  body={(row) => {
                                      const map = {
                                          rigid:     { bg: '#1e3a8a', label: 'Rigid core' },
                                          hinge:     { bg: '#7c3aed', label: 'Hinge' },
                                          transient: { bg: '#f59e0b', label: 'Transient' },
                                      };
                                      const m = map[row.structure_class] || { bg: '#9ca3af', label: row.structure_class || '—' };
                                      return <Tag value={m.label} style={{ backgroundColor: m.bg, color: '#fff' }} />;
                                  }}
                              ></Column>
                              <Column field="col2" header="PDB contacts" sortable></Column>
                              <Column field="col3" header="AF contacts" sortable></Column>
                              <Column field="consensus" header="Consensus" sortable dataType="numeric" style={{ textAlign: 'right' }}></Column>
                              <Column field="transient" header="Transient" sortable dataType="numeric" style={{ textAlign: 'right' }}></Column>
                              <Column field="rewiring" header="Rewiring" sortable dataType="numeric" style={{ textAlign: 'right' }}
                                  body={(row) => `${Math.round((row.rewiring ?? 0) * 100)}%`}
                              ></Column>
                              <Column field="clustering_delta" header="Block Δ" sortable dataType="numeric" style={{ textAlign: 'right' }}
                                  body={(row) => {
                                      const d = row.clustering_delta;
                                      if (d === null || d === undefined) return <span className="text-muted">—</span>;
                                      const color = d > 0.15 ? '#b45309' : (d < -0.15 ? '#1e3a8a' : '#6b7280');
                                      return <span style={{ color, fontWeight: 600 }}>{d > 0 ? '+' : ''}{Number(d).toFixed(2)}</span>;
                                  }}
                              ></Column>
                               <Column field="col5" header="pLDDT (Conf.)" sortable body={(row)=> {
                                  const val = parseFloat(row.col5);

                                  // Lógica de Cores Oficial do AlphaFold
                                  let color = '#ff7675';       // < 50 (Very Low)
                                  let textColor = '#fff';
                                  if (val > 90) {
                                      color = '#0984e3';       // > 90 (Very High)
                                  } else if (val > 70) {
                                      color = '#74b9ff';       // > 70 (Confident)
                                  } else if (val > 50) {
                                      color = '#ffeaa7';       // > 50 (Low)
                                      textColor = '#000';
                                  }

                                  return (
                                      <span className="badge rounded-pill" style={{backgroundColor: color, color: textColor, minWidth: '60px'}}>
                                          {val?.toFixed(1)}
                                      </span>
                                  );
                              }}></Column>

                          </ColumnToggleDataTable>
                          <div className="mt-2 small text-500 border-top pt-2 mb-4">
                              <div className="mb-2">
                                  <strong>Class:</strong>
                                  <span className="ms-2" style={{color:'#1e3a8a'}}>■ Rigid core (shared contacts)</span>
                                  <span className="ms-2" style={{color:'#7c3aed'}}>■ Hinge (rewired — allosteric)</span>
                                  <span className="ms-2" style={{color:'#f59e0b'}}>■ Transient (PDB/AF-only — flexible)</span>
                              </div>
                              <div className="mb-2">
                                  <strong>Block Δ</strong> (PDB − AF local clustering):
                                  <span className="ms-2" style={{color:'#b45309'}}>■ &gt; +0.15 anchoring block lost in AF</span>
                                  <span className="ms-2" style={{color:'#1e3a8a'}}>■ &lt; −0.15 extra cohesion in AF (often crystal gaps)</span>
                                  <span className="ms-2" style={{color:'#6b7280'}}>■ ±0.15 block preserved</span>
                              </div>
                              <strong>pLDDT Legend:</strong>
                              <span className="ms-2" style={{color:'#0984e3'}}>■ &gt;90 (Rigid)</span>
                              <span className="ms-2" style={{color:'#74b9ff'}}>■ 70-90 (Confident)</span>
                              <span className="ms-2" style={{color:'#f1c40f'}}>■ 50-70 (Flexible)</span>
                              <span className="ms-2" style={{color:'#ff7675'}}>■ &lt;50 (Disordered/IDR)</span>
                          </div>

                          {/* ───── Structure (single viewer: pLDDT / class + superposition) ───── */}
                          <div className="d-flex align-items-center">
                              <h5 className="mb-0 text-primary mb-3"><Grip className="me-2"/>Structure</h5>
                          </div>
                          <div className="d-flex flex-wrap align-items-end gap-3" style={{ marginBottom: '8rem' }}>
                              <div>
                                  <label className="form-label fw-bold text-secondary mb-1 d-block">Colour by</label>
                                  <SelectButton
                                      value={level5ColorBy}
                                      onChange={(e) => e.value && setLevel5ColorBy(e.value)}
                                      options={[
                                          { label: 'pLDDT', value: 'plddt' },
                                          { label: 'Class', value: 'class' },
                                      ]}
                                  />
                              </div>
                              <div>
                                  <label className="form-label fw-bold text-secondary mb-1 d-block">PDB overlay</label>
                                  <Button
                                      label={level5Superpose ? 'Hide superposition' : 'Show superposition'}
                                      icon={level5Superpose ? 'pi pi-eye-slash' : 'pi pi-clone'}
                                      severity={level5Superpose ? 'primary' : 'secondary'}
                                      outlined
                                      size="small"
                                      onClick={() => setLevel5Superpose(v => !v)}
                                  />
                              </div>
                          </div>
                          <div className="viewer-box">
                              <MolstarViewerPath
                                  key={`l5-${submittedPdbId}-${submittedAfId || 'monomer'}`}
                                  level="5"
                                  colorBy={level5ColorBy}
                                  superpose={level5Superpose}
                                  pdbId={submittedPdbId}
                                  afId={submittedAfId}
                                  uniac={result.data && result.data.length > 0 ? result.data[0].col1.split('_')[0] : null}
                                  datatable={(() => {
                                      const uniacL5 = (result.data || [])
                                          .find(r => (r?.col1 || '').includes('_'))?.col1?.split('_')[0] || null;
                                      // P07550 is not chain A in every predicted
                                      // complex; hardcoding 'A' mis-coloured multimers.
                                      const chainL5 = submittedAfId
                                          ? resolveAlphaFoldChain(submittedAfId, uniacL5)
                                          : 'A';
                                      return (result.data || []).map(r => ({
                                          col1: (r.col1 || '').split('_')[1],
                                          chain: chainL5,
                                          structure_class: r.structure_class,
                                      }));
                                  })()}
                              />
                          </div>
                          {level5ColorBy === 'class' ? (
                              <div className="mt-2 p-2 border rounded bg-light small text-500 mb-4 d-flex flex-wrap gap-3">
                                  <span><span style={{display:'inline-block',width:'12px',height:'12px',background:'#1e3a8a',borderRadius:'3px',marginRight:'6px'}}></span>Rigid core (consensus contacts)</span>
                                  <span><span style={{display:'inline-block',width:'12px',height:'12px',background:'#7c3aed',borderRadius:'3px',marginRight:'6px'}}></span>Hinge (rewired — allosteric)</span>
                                  <span><span style={{display:'inline-block',width:'12px',height:'12px',background:'#f59e0b',borderRadius:'3px',marginRight:'6px'}}></span>Transient (PDB/AF-only)</span>
                              </div>
                          ) : (
                              <div className="mt-2 p-2 border rounded bg-light small text-500 mb-4 d-flex flex-wrap gap-3">
                                  <span style={{color:'#0984e3'}}>■ &gt;90 (Very high)</span>
                                  <span style={{color:'#74b9ff'}}>■ 70–90 (Confident)</span>
                                  <span style={{color:'#f1c40f'}}>■ 50–70 (Low)</span>
                                  <span style={{color:'#ff7675'}}>■ &lt;50 (Very low / IDR)</span>
                              </div>
                          )}

                          {/* ───── Class / rewiring plots (publication style) ───── */}
                          {(() => {
                            const PF = { family: 'Inter, Helvetica, Arial, sans-serif', size: 12, color: '#374151' };
                            const counts = { rigid: 0, hinge: 0, transient: 0 };
                            (result.data || []).forEach(r => { if (counts[r.structure_class] !== undefined) counts[r.structure_class]++; });
                            const total = (counts.rigid + counts.hinge + counts.transient) || 1;
                            const pct = (n) => `${Math.round((n / total) * 100)}%`;
                            const CLS = [
                              { key: 'rigid',     name: 'Rigid core', color: '#1e3a8a' },
                              { key: 'hinge',     name: 'Hinge',      color: '#7c3aed' },
                              { key: 'transient', name: 'Transient',  color: '#f59e0b' },
                            ];
                            const scatterTraces = CLS.map(c => {
                              const xs = [], ys = [], ts = [], sz = [], cd = [];
                              (result.data || []).forEach(r => {
                                if (r.structure_class !== c.key) return;
                                const y = parseFloat(r.col5);
                                const d = (r.clustering_delta ?? 0);
                                xs.push(r.rewiring); ys.push(isNaN(y) ? null : y); ts.push(r.col1);
                                sz.push(6 + Math.max(0, d) * 18); cd.push(d);
                              });
                              return {
                                type: 'scatter', mode: 'markers', name: c.name,
                                x: xs, y: ys, text: ts, customdata: cd,
                                hovertemplate: `<b>%{text}</b><br>${c.name}<br>rewiring %{x:.2f} \u00b7 pLDDT %{y:.0f}<br>block \u0394 %{customdata:.2f}<extra></extra>`,
                                marker: { color: c.color, size: sz, opacity: 0.7, line: { width: 0.5, color: '#ffffff' } },
                              };
                            });
                            return (
                              <div className="row mb-2">
                                <div className="col-md-5 mb-3">
                                  <div className="p-3 border rounded bg-white h-100">
                                    <h6 className="mb-3 text-primary"><i className="pi pi-chart-bar me-2" />Residue class composition</h6>
                                    <Plot
                                      data={[{
                                        type: 'bar',
                                        x: CLS.map(c => c.name),
                                        y: CLS.map(c => counts[c.key]),
                                        marker: { color: CLS.map(c => c.color) },
                                        width: 0.6,
                                        text: CLS.map(c => `${counts[c.key]}<br>${pct(counts[c.key])}`),
                                        textposition: 'outside',
                                        textfont: PF,
                                        cliponaxis: false,
                                        hovertemplate: '%{x}: %{y} residues<extra></extra>',
                                      }]}
                                      layout={{ height: 330,
                                        margin: { t: 25, r: 12, b: 40, l: 52 },
                                        font: PF,
                                        bargap: 0.4,
                                        xaxis: { fixedrange: true, tickfont: PF },
                                        yaxis: { title: { text: 'Residues' }, gridcolor: '#eef2f7', zeroline: false, fixedrange: true, rangemode: 'tozero' },
                                        showlegend: false,
                                      }}
                                      config={{ displayModeBar: 'hover', displaylogo: false, responsive: true }}
                                      style={{ width: '100%' }} useResizeHandler
                                    />
                                  </div>
                                </div>
                                <div className="col-md-7 mb-3">
                                  <div className="p-3 border rounded bg-white h-100">
                                    <h6 className="mb-1 text-primary"><i className="pi pi-chart-bar me-2" />Rewiring vs pLDDT</h6>
                                    <div className="small text-500 mb-2">Marker size ∝ block-coupling Δ (larger = local anchoring block lost in AlphaFold).</div>
                                    <Plot
                                      data={scatterTraces}
                                      layout={{ height: 330,
                                        margin: { t: 16, r: 16, b: 46, l: 52 },
                                        font: PF,
                                        xaxis: { title: { text: 'Rewiring  (1 \u2212 Jaccard)' }, range: [-0.02, 1.02], gridcolor: '#eef2f7', zeroline: false, fixedrange: true },
                                        yaxis: { title: { text: 'pLDDT' }, range: [0, 100], gridcolor: '#eef2f7', zeroline: false, fixedrange: true },
                                        shapes: [
                                          { type: 'rect', xref: 'x', yref: 'y', x0: 0.5, x1: 1.02, y0: 70, y1: 100, fillcolor: '#ef4444', opacity: 0.06, line: { width: 0 }, layer: 'below' },
                                          { type: 'line', x0: -0.02, x1: 1.02, y0: 70, y1: 70, line: { color: '#cbd5e1', width: 1, dash: 'dot' } },
                                        ],
                                        annotations: [
                                          { x: 0.99, y: 98, xref: 'x', yref: 'y', text: 'confident discrepancies', showarrow: false, font: { size: 10, color: '#b91c1c' }, xanchor: 'right', yanchor: 'top' },
                                        ],
                                        legend: { orientation: 'h', y: 1.16, x: 0, font: { size: 11 } },
                                        showlegend: true,
                                      }}
                                      config={{ displayModeBar: 'hover', displaylogo: false, responsive: true }}
                                      style={{ width: '100%' }} useResizeHandler
                                    />
                                  </div>
                                </div>
                              </div>
                            );
                          })()}

                      </Card>
                  </>
              )}

              {/* --- RENDER: TABLE (Level 14 - Conformational differences, PDB vs PDB) --- */}
              {result && mode === 'level14' && (
                  <>
                      <Card className="shadow-sm animate__animated animate__fadeIn">
                          <div className="d-flex justify-content-between align-items-center mb-3">
                              <h4 className="text-primary mb-3"><Activity className="me-2"/>Conformational differences (PDB vs PDB)</h4>
                              <Button
                                  label="Download CSV"
                                  icon="pi pi-download"
                                  severity="secondary"
                                  outlined
                                  size="small"
                                  onClick={downloadCSV}
                              />
                          </div>

                       <div className="card card-spm bg-light border-0 p-3 mb-4 text-start">
                           <h5 className="mb-3">
                               <i className="pi pi-info-circle me-2 text-primary" />
                               Residues that change connectivity between two experimental structures
                           </h5>
                           <p className="mb-3 small">
                               For every residue we compare its <strong>contact neighbourhood</strong> in
                               structure&nbsp;<strong>A</strong> and
                               structure&nbsp;<strong>B</strong> .
                               Intra-chain residue&ndash;residue contacts are read from the graph at
                               <strong>&nbsp;&lt;&nbsp;8&nbsp;&Aring;</strong> in each structure; for each residue we keep the
                               <em> set</em> of partner positions in each &mdash; <em>A</em> and <em>B</em>.
                               Agreement is summarised by the Jaccard index
                               <em>&nbsp;J&nbsp;=&nbsp;|A&nbsp;&cap;&nbsp;B|&nbsp;/&nbsp;|A&nbsp;&cup;&nbsp;B|</em>, and disagreement by the
                               <em>&nbsp;rewiring&nbsp;=&nbsp;1&nbsp;&minus;&nbsp;J</em>. Because both sides are ground truth, this
                               rewiring is a direct read-out of <strong>conformational change</strong>. Each residue is
                               assigned to one class:
                           </p>
                           <p className="mb-2">
                               <Tag value="Rigid core" icon="pi pi-lock" rounded style={{ backgroundColor: '#1e3a8a', color: '#fff' }} />
                               <span className="small text-secondary ms-2">
                                   <strong>Consensus</strong> neighbourhood &mdash; partners are largely shared
                                   (<em>J&nbsp;&ge;&nbsp;0.60</em>). The scaffold that is invariant across the two states.
                               </span>
                           </p>
                           <p className="mb-2">
                               <Tag value="Hinge" icon="pi pi-sync" rounded style={{ backgroundColor: '#7c3aed', color: '#fff' }} />
                               <span className="small text-secondary ms-2">
                                   <strong>Rewired</strong> neighbourhood at <strong>preserved degree</strong>: the
                                   residue keeps a similar number of contacts (both&nbsp;&ge;&nbsp;3,
                                   |&Delta;degree|&nbsp;&le;&nbsp;2) but exchanges most partners (<em>J&nbsp;&lt;&nbsp;0.40</em>) &mdash;
                                   the signature of a conformational pivot / allosteric hinge between the two states.
                               </span>
                           </p>
                           <p className="mb-2">
                               <Tag value="Transient" icon="pi pi-bolt" rounded style={{ backgroundColor: '#f59e0b', color: '#fff' }} />
                               <span className="small text-secondary ms-2">
                                   Remaining residues, dominated by the <strong>symmetric difference</strong> of the two
                                   neighbourhoods &mdash; contacts present in only one state (e.g. ligand-induced, or lost on
                                   activation).
                               </span>
                           </p>
                           <p className="mb-0 small text-secondary">
                               <i className="pi pi-exclamation-circle me-1" />
                               Only residues <strong>resolved in both structures</strong> are shown, so a lost contact reflects
                               real rewiring and not a missing/unmodelled region. Classification thresholds are configurable
                               server-side.
                           </p>
                       </div>
                          <ColumnToggleDataTable value={result.data} sortField="rewiring" sortOrder={-1} className="mb-3 nobreak" paginator rows={5} stripedRows showGridlines filterDisplay="menu" tableStyle={{ minWidth: '60rem' }} responsiveLayout="scroll">
                              <Column field="col1" header="Residue" sortable></Column>
                              <Column field="structure_class" header="Class" sortable filter dataType="text"
                                  filterPlaceholder="rigid / hinge / transient"
                                  body={(row) => {
                                      const map = {
                                          rigid:     { bg: '#1e3a8a', label: 'Rigid core' },
                                          hinge:     { bg: '#7c3aed', label: 'Hinge' },
                                          transient: { bg: '#f59e0b', label: 'Transient' },
                                      };
                                      const m = map[row.structure_class] || { bg: '#9ca3af', label: row.structure_class || '\u2014' };
                                      return <Tag value={m.label} style={{ backgroundColor: m.bg, color: '#fff' }} />;
                                  }}
                              ></Column>
                              <Column field="col2" header="A contacts" sortable></Column>
                              <Column field="col3" header="B contacts" sortable></Column>
                              <Column field="consensus" header="Consensus" sortable dataType="numeric" style={{ textAlign: 'right' }}></Column>
                              <Column field="transient" header="Transient" sortable dataType="numeric" style={{ textAlign: 'right' }}></Column>
                              <Column field="rewiring" header="Rewiring" sortable dataType="numeric" style={{ textAlign: 'right' }}
                                  body={(row) => `${Math.round((row.rewiring ?? 0) * 100)}%`}
                              ></Column>
                              <Column field="clustering_delta" header="Block Δ (A − B)" sortable dataType="numeric" style={{ textAlign: 'right' }}
                                  body={(row) => {
                                      const d = row.clustering_delta;
                                      if (d === null || d === undefined) return <span className="text-muted">{'\u2014'}</span>;
                                      const color = d > 0.15 ? '#b45309' : (d < -0.15 ? '#1e3a8a' : '#6b7280');
                                      return <span style={{ color, fontWeight: 600 }}>{d > 0 ? '+' : ''}{Number(d).toFixed(2)}</span>;
                                  }}
                              ></Column>
                          </ColumnToggleDataTable>
                          <div className="mt-2 small text-500 border-top pt-2 mb-4">
                              <div className="mb-2">
                                  <strong>Class:</strong>
                                  <span className="ms-2" style={{color:'#1e3a8a'}}>&#9632; Rigid core (shared contacts)</span>
                                  <span className="ms-2" style={{color:'#7c3aed'}}>&#9632; Hinge (rewired &mdash; conformational pivot)</span>
                                  <span className="ms-2" style={{color:'#f59e0b'}}>&#9632; Transient (A/B-only &mdash; state-specific)</span>
                              </div>
                              <div className="mb-0">
                                  <strong>Block &Delta;</strong> (A &minus; B local clustering):
                                  <span className="ms-2" style={{color:'#b45309'}}>&#9632; &gt; +0.15 anchoring block tighter in A</span>
                                  <span className="ms-2" style={{color:'#1e3a8a'}}>&#9632; &lt; &minus;0.15 tighter in B</span>
                                  <span className="ms-2" style={{color:'#6b7280'}}>&#9632; &plusmn;0.15 block preserved</span>
                              </div>
                          </div>

                          {result.data && result.data.length > 0 && (<>
                          {/* ───── Structure (structure A; optional B overlay) ─────
                               Buttons mirror Level 5. Colour-by uses Class / Rewiring
                               (no pLDDT for an experimental structure). "Show superposition"
                               overlays structure B (pdbId2) onto structure A. */}
                          <div className="d-flex align-items-center">
                              <h5 className="mb-0 text-primary mb-3"><Grip className="me-2"/>Structure A &mdash; {submittedPdbId}{submittedPdbId2 ? <> vs B &mdash; {submittedPdbId2}</> : null}</h5>
                          </div>
                          <div className="d-flex flex-wrap align-items-end gap-3" style={{ marginBottom: '8rem' }}>
                              <div>
                                  <label className="form-label fw-bold text-secondary mb-1 d-block">Colour by</label>
                                  <SelectButton
                                      value={level14ColorBy}
                                      onChange={(e) => e.value && setLevel14ColorBy(e.value)}
                                      options={[
                                          { label: 'Class', value: 'class' },
                                          { label: 'Rewiring', value: 'rewiring' },
                                      ]}
                                  />
                              </div>
                              <div>
                                  <label className="form-label fw-bold text-secondary mb-1 d-block">Structure B overlay</label>
                                  <Button
                                      label={level14Superpose ? 'Hide superposition' : 'Show superposition'}
                                      icon={level14Superpose ? 'pi pi-eye-slash' : 'pi pi-clone'}
                                      severity={level14Superpose ? 'primary' : 'secondary'}
                                      outlined
                                      size="small"
                                      disabled={!submittedPdbId2}
                                      onClick={() => setLevel14Superpose(v => !v)}
                                  />
                              </div>
                          </div>
                          <div className="viewer-box">
                              <MolstarViewerPath
                                  level="14"
                                  colorBy={level14ColorBy}
                                  superpose={level14Superpose}
                                  pdbId={submittedPdbId}
                                  overlayPdbId={submittedPdbId2}
                                  chain="A"
                                  uniac={result.data && result.data.length > 0 ? result.data[0].col1.split('_')[0] : null}
                                  positions={(result.data || []).map(r => (r.col1 || '').split('_')[1])}
                                  datatable={(result.data || []).map(r => ({
                                      col1: (r.col1 || '').split('_')[1],
                                      chain: 'A',
                                      structure_class: r.structure_class,
                                      rewiring: r.rewiring,
                                  }))}
                              />
                          </div>
                          {level14ColorBy === 'class' ? (
                              <div className="mt-2 p-2 border rounded bg-light small text-500 mb-4 d-flex flex-wrap gap-3">
                                  <span><span style={{display:'inline-block',width:'12px',height:'12px',background:'#1e3a8a',borderRadius:'3px',marginRight:'6px'}}></span>Rigid core (consensus contacts)</span>
                                  <span><span style={{display:'inline-block',width:'12px',height:'12px',background:'#7c3aed',borderRadius:'3px',marginRight:'6px'}}></span>Hinge (rewired &mdash; conformational pivot)</span>
                                  <span><span style={{display:'inline-block',width:'12px',height:'12px',background:'#f59e0b',borderRadius:'3px',marginRight:'6px'}}></span>Transient (A/B-only)</span>
                              </div>
                          ) : (
                              <div className="mt-2 p-2 border rounded bg-light small text-500 mb-4 d-flex flex-wrap gap-3 align-items-center">
                                  <span>Rewiring gradient:</span>
                                  <span><span style={{display:'inline-block',width:'12px',height:'12px',background:'#dbeafe',borderRadius:'3px',marginRight:'6px'}}></span>0% (contacts conserved)</span>
                                  <span><span style={{display:'inline-block',width:'12px',height:'12px',background:'#7c3aed',borderRadius:'3px',marginRight:'6px'}}></span>50%</span>
                                  <span><span style={{display:'inline-block',width:'12px',height:'12px',background:'#b91c1c',borderRadius:'3px',marginRight:'6px'}}></span>100% (fully rewired)</span>
                              </div>
                          )}
                          </>)}

                          {/* ───── Class / rewiring plots ───── */}
                          {(() => {
                            if (!result.data || result.data.length === 0) return null;
                            const PF = { family: 'Inter, Helvetica, Arial, sans-serif', size: 12, color: '#374151' };
                            const counts = { rigid: 0, hinge: 0, transient: 0 };
                            (result.data || []).forEach(r => { if (counts[r.structure_class] !== undefined) counts[r.structure_class]++; });
                            const total = (counts.rigid + counts.hinge + counts.transient) || 1;
                            const pct = (n) => `${Math.round((n / total) * 100)}%`;
                            const CLS = [
                              { key: 'rigid',     name: 'Rigid core', color: '#1e3a8a' },
                              { key: 'hinge',     name: 'Hinge',      color: '#7c3aed' },
                              { key: 'transient', name: 'Transient',  color: '#f59e0b' },
                            ];
                            const scatterTraces = CLS.map(c => {
                              const xs = [], ys = [], ts = [], cd = [];
                              (result.data || []).forEach(r => {
                                if (r.structure_class !== c.key) return;
                                const y = (r.clustering_delta ?? 0);
                                xs.push(r.rewiring); ys.push(y); ts.push(r.col1); cd.push(`${r.col2} \u2192 ${r.col3}`);
                              });
                              return {
                                type: 'scatter', mode: 'markers', name: c.name,
                                x: xs, y: ys, text: ts, customdata: cd,
                                hovertemplate: `<b>%{text}</b><br>${c.name}<br>rewiring %{x:.2f} \u00b7 block \u0394 %{y:.2f}<br>degree A\u2192B %{customdata}<extra></extra>`,
                                marker: { color: c.color, size: 9, opacity: 0.7, line: { width: 0.5, color: '#ffffff' } },
                              };
                            });
                            return (
                              <div className="row mb-2">
                                <div className="col-md-5 mb-3">
                                  <div className="p-3 border rounded bg-white h-100">
                                    <h6 className="mb-3 text-primary"><i className="pi pi-chart-bar me-2" />Residue class composition</h6>
                                    <Plot
                                      data={[{
                                        type: 'bar',
                                        x: CLS.map(c => c.name),
                                        y: CLS.map(c => counts[c.key]),
                                        marker: { color: CLS.map(c => c.color) },
                                        width: 0.6,
                                        text: CLS.map(c => `${counts[c.key]}<br>${pct(counts[c.key])}`),
                                        textposition: 'outside',
                                        textfont: PF,
                                        cliponaxis: false,
                                        hovertemplate: '%{x}: %{y} residues<extra></extra>',
                                      }]}
                                      layout={{ height: 330,
                                        margin: { t: 25, r: 12, b: 40, l: 52 },
                                        font: PF,
                                        bargap: 0.4,
                                        xaxis: { fixedrange: true, tickfont: PF },
                                        yaxis: { title: { text: 'Residues' }, gridcolor: '#eef2f7', zeroline: false, fixedrange: true, rangemode: 'tozero' },
                                        showlegend: false,
                                      }}
                                      config={{ displayModeBar: 'hover', displaylogo: false, responsive: true }}
                                      style={{ width: '100%' }} useResizeHandler
                                    />
                                  </div>
                                </div>
                                <div className="col-md-7 mb-3">
                                  <div className="p-3 border rounded bg-white h-100">
                                    <h6 className="mb-1 text-primary"><i className="pi pi-chart-bar me-2" />Rewiring vs block-coupling shift</h6>
                                    <div className="small text-500 mb-2">Top-right = strongly rewired residues whose local anchoring block also changes between states (candidate conformational pivots).</div>
                                    <Plot
                                      data={scatterTraces}
                                      layout={{ height: 330,
                                        margin: { t: 16, r: 16, b: 46, l: 52 },
                                        font: PF,
                                        xaxis: { title: { text: 'Rewiring  (1 \u2212 Jaccard)' }, range: [-0.02, 1.02], gridcolor: '#eef2f7', zeroline: false, fixedrange: true },
                                        yaxis: { title: { text: 'Block-coupling \u0394 (A \u2212 B)' }, range: [-1.02, 1.02], gridcolor: '#eef2f7', zeroline: true, zerolinecolor: '#cbd5e1', fixedrange: true },
                                        legend: { orientation: 'h', y: 1.16, x: 0, font: { size: 11 } },
                                        showlegend: true,
                                      }}
                                      config={{ displayModeBar: 'hover', displaylogo: false, responsive: true }}
                                      style={{ width: '100%' }} useResizeHandler
                                    />
                                  </div>
                                </div>
                              </div>
                            );
                          })()}

                      </Card>
                  </>
              )}


              {/* --- RENDER: TABLE (Level 6 - PTM Crosstalk) --- */}
              {result && mode === 'level6' && (() => {
                  const data = result.data || [];
                  const pairs = data.filter(r => r.kind === 'pair');
                  const trios = data.filter(r => r.kind === 'trio');
                  const tetrads = data.filter(r => r.kind === 'tetrad');
                  const iface = data.filter(r => r.kind === 'interface');

                  const PF = { family: 'Inter, Helvetica, Arial, sans-serif', size: 12, color: '#374151' };
                  const PLOT_CFG = { displayModeBar: 'hover', displaylogo: false, responsive: true };
                  const TYPE_COLOR = { 'same-residue': '#DC2626', 'proximal': '#3B82F6', 'distal': '#7c3aed' };
                  const typeTag = (r) => <Tag value={r.crosstalk_type} style={{ backgroundColor: TYPE_COLOR[r.crosstalk_type] || '#6b7280', color: '#fff' }} />;
                  const structTag = (id, type) =>
                      type === 'af'   ? <Tag value={id} style={{ backgroundColor: '#f59e0b', color: '#fff' }} /> :
                      type === 'both' ? <Tag value={id} icon="pi pi-verified" style={{ backgroundColor: '#166534', color: '#fff' }} /> :
                      type === 'same' ? <Tag value={id} severity="info" /> :
                                        <Tag value={id} severity="success" />;

                  const tcounts = { 'same-residue': 0, 'proximal': 0, 'distal': 0 };
                  pairs.forEach(p => { if (tcounts[p.crosstalk_type] !== undefined) tcounts[p.crosstalk_type]++; });
                  const ifaceHits = iface.filter(x => x.is_interface);
                  const ifaceTop = ifaceHits.slice(0, 10).reverse();
                  const ifaceCount = ifaceHits.length;

                  const markCount = (s) => { const m = String(s || '').match(/\(([^)]*)\)/); return m ? m[1].split(',').map(x => x.trim()).filter(Boolean).length : 0; };
                  const cliqueMarks = (t) => [t.col1, t.col2, t.col3, t.col4].filter(Boolean).reduce((s, x) => s + markCount(x), 0);
                  const SRC = [['pdb', 'PDB', '#16a34a'], ['both', 'PDB+AF', '#166534'], ['af', 'AlphaFold', '#f59e0b']];
                  const cliqueTraces = (items, yFn) => SRC.map(([st, name, color]) => {
                      const ts = items.filter(t => t.source_type === st);
                      return { type: 'scatter', mode: 'markers', name, x: ts.map(t => t.diameter), y: ts.map(yFn), text: ts.map(t => [t.col1, t.col2, t.col3, t.col4].filter(Boolean).join('<br>')), customdata: ts.map(cliqueMarks), hovertemplate: `%{text}<br>diameter %{x:.2f} \u00c5 \u00b7 tightest %{y:.2f} \u00c5<br>%{customdata} marks \u00b7 ${name}<extra></extra>`, marker: { color, size: ts.map(t => 7 + cliqueMarks(t) * 2.5), opacity: 0.78, line: { width: 0.5, color: '#ffffff' } } };
                  });
                  const trioTraces = cliqueTraces(trios, (t) => Math.min(t.d12, t.d23, t.d13));
                  const tetradTraces = cliqueTraces(tetrads, (t) => t.min_edge);

                  const cliquePlot = (title, traces) => (
                    <div className="row">
                      <div className="col-md-7 mb-2">
                        <div className="p-3 border rounded bg-white h-100">
                          <h6 className="mb-3 text-primary"><i className="pi pi-chart-bar me-2" />{title}</h6>
                          <Plot
                            data={traces}
                            layout={{ height: 320, margin: { t: 16, r: 14, b: 46, l: 52 }, font: PF, xaxis: { title: { text: 'diameter — widest edge (Å)' }, range: [2.5, 8.4], gridcolor: '#eef2f7', zeroline: false, fixedrange: true }, yaxis: { title: { text: 'tightest edge (Å)' }, range: [2.5, 8.4], gridcolor: '#eef2f7', zeroline: false, fixedrange: true }, shapes: [{ type: 'line', x0: 2.5, y0: 2.5, x1: 8.4, y1: 8.4, line: { color: '#cbd5e1', width: 1, dash: 'dot' } }], annotations: [{ x: 8.3, y: 8.3, xref: 'x', yref: 'y', text: 'near line = compact (equilateral)', showarrow: false, font: { size: 10, color: '#64748b' }, xanchor: 'right', yanchor: 'top' }], legend: { orientation: 'h', y: 1.15, x: 0, font: { size: 11 } }, showlegend: true }}
                            config={PLOT_CFG} style={{ width: '100%' }} useResizeHandler
                          />
                        </div>
                      </div>
                    </div>
                  );

                  const panels = [];
                  if (pairs.length > 0) {
                    panels.push(
                      <TabPanel key="pairs" header={`Pairwise (${pairs.length})`}>
                        <ColumnToggleDataTable value={pairs} className="mb-3 nobreak" paginator rows={5} stripedRows showGridlines filterDisplay="menu" tableStyle={{ minWidth: '66rem' }} responsiveLayout="scroll">
                            <Column field="col1" header="Site A" sortable></Column>
                            <Column field="col2" header="Site B" sortable></Column>
                            <Column field="crosstalk_type" header="Type" sortable body={typeTag}></Column>
                            <Column field="col3" header="3D dist (Å)" sortable body={(r)=> r.crosstalk_type === 'same-residue' ? <span className="text-muted">—</span> : <b>{Number(r.col3).toFixed(2)}</b>}></Column>
                            <Column field="seq_sep" header="Seq sep" sortable body={(r)=> r.crosstalk_type === 'same-residue' ? <span className="text-muted">—</span> : r.seq_sep}></Column>
                            <Column field="col4" header="Source" body={(r)=> structTag(r.col4, r.source_type)}></Column>
                        </ColumnToggleDataTable>
                        <div className="row">
                          <div className="col-md-6 mb-2">
                            <div className="p-3 border rounded bg-white h-100">
                              <h6 className="mb-3 text-primary"><i className="pi pi-chart-bar me-2" />Crosstalk composition</h6>
                              <Plot
                                data={[{ type: 'bar', x: ['same-residue', 'proximal', 'distal'], y: [tcounts['same-residue'], tcounts['proximal'], tcounts['distal']], marker: { color: ['#DC2626', '#3B82F6', '#7c3aed'] }, width: 0.6, text: [tcounts['same-residue'], tcounts['proximal'], tcounts['distal']].map(String), textposition: 'outside', textfont: PF, cliponaxis: false, hovertemplate: '%{x}: %{y} pairs<extra></extra>' }]}
                                layout={{ height: 300, margin: { t: 16, r: 12, b: 40, l: 48 }, font: PF, bargap: 0.4, xaxis: { fixedrange: true, tickfont: PF }, yaxis: { title: { text: 'pairs' }, gridcolor: '#eef2f7', zeroline: false, fixedrange: true, rangemode: 'tozero' }, showlegend: false }}
                                config={PLOT_CFG} style={{ width: '100%' }} useResizeHandler
                              />
                            </div>
                          </div>
                        </div>
                      </TabPanel>
                    );
                  }
                  if (trios.length > 0) {
                    panels.push(
                      <TabPanel key="trios" header={`Trio hot-spots (${trios.length})`}>
                        <ColumnToggleDataTable value={trios} className="mb-3 nobreak" paginator rows={5} stripedRows showGridlines filterDisplay="menu" tableStyle={{ minWidth: '66rem' }} responsiveLayout="scroll">
                            <Column field="col1" header="Site 1" sortable></Column>
                            <Column field="col2" header="Site 2" sortable></Column>
                            <Column field="col3" header="Site 3" sortable></Column>
                            <Column field="diameter" header="Diameter (Å)" sortable body={(r)=><b>{Number(r.diameter).toFixed(2)}</b>}></Column>
                            <Column header="Edges (Å)" body={(r)=><span className="text-500 fs--1">{r.d12} / {r.d23} / {r.d13}</span>}></Column>
                            <Column field="source" header="Structure" sortable body={(r)=> structTag(r.source, r.source_type)}></Column>
                        </ColumnToggleDataTable>
                        {cliquePlot('Trio geometry — diameter vs tightest edge', trioTraces)}
                      </TabPanel>
                    );
                  }
                  if (tetrads.length > 0) {
                    panels.push(
                      <TabPanel key="tetrads" header={`Tetrad hot-spots (${tetrads.length})`}>
                        <ColumnToggleDataTable value={tetrads} className="mb-3 nobreak" paginator rows={5} stripedRows showGridlines filterDisplay="menu" tableStyle={{ minWidth: '66rem' }} responsiveLayout="scroll">
                            <Column field="col1" header="Site 1" sortable></Column>
                            <Column field="col2" header="Site 2" sortable></Column>
                            <Column field="col3" header="Site 3" sortable></Column>
                            <Column field="col4" header="Site 4" sortable></Column>
                            <Column field="diameter" header="Diameter (Å)" sortable body={(r)=><b>{Number(r.diameter).toFixed(2)}</b>}></Column>
                            <Column field="source" header="Structure" sortable body={(r)=> structTag(r.source, r.source_type)}></Column>
                        </ColumnToggleDataTable>
                        {cliquePlot('Tetrad geometry — diameter vs tightest edge', tetradTraces)}
                      </TabPanel>
                    );
                  }
                  if (iface.length > 0) {
                    panels.push(
                      <TabPanel key="iface" header={`Interface (${ifaceCount} / ${iface.length})`}>
                        <ColumnToggleDataTable value={iface} className="mb-3 nobreak" paginator rows={5} stripedRows showGridlines filterDisplay="menu" tableStyle={{ minWidth: '66rem' }} responsiveLayout="scroll">
                            <Column field="col1" header="PTM site" sortable></Column>
                            <Column field="interface_partners" header="Partners (best structure)" sortable body={(r)=><b>{r.interface_partners}</b>}></Column>
                            <Column field="best_structure" header="Structure" sortable body={(r)=> r.best_structure ? structTag(r.best_structure, 'pdb') : <span className="text-muted">—</span>}></Column>
                            <Column field="n_structures" header="# structures" sortable></Column>
                            <Column field="is_interface" header="Location" sortable body={(r)=> r.is_interface ? <Tag value="interface" severity="warning" /> : <Tag value="core" severity="secondary" />}></Column>
                        </ColumnToggleDataTable>
                        <div className="row">
                          <div className="col-md-12 mb-2">
                            <div className="p-3 border rounded bg-white h-100">
                              <h6 className="mb-3 text-primary"><i className="pi pi-chart-bar me-2" />Top interface PTMs (partners in best structure)</h6>
                              {ifaceTop.length === 0 ? (
                                <div className="text-muted small p-4 text-center">No PTM residues sit on an inter-chain interface in the available structures.</div>
                              ) : (
                              <Plot
                                data={[{ type: 'bar', orientation: 'h', y: ifaceTop.map(x => x.col1), x: ifaceTop.map(x => x.interface_partners), marker: { color: '#f59e0b' }, text: ifaceTop.map(x => String(x.interface_partners)), textposition: 'outside', textfont: PF, cliponaxis: false, hovertemplate: '%{y}<br>%{x} interface partners<extra></extra>' }]}
                                layout={{ height: 330, margin: { t: 25, r: 16, b: 40, l: 170 }, font: PF, xaxis: { title: { text: 'distinct partners (best structure)' }, gridcolor: '#eef2f7', zeroline: false, fixedrange: true, rangemode: 'tozero' }, yaxis: { fixedrange: true, tickfont: { ...PF, size: 11 }, automargin: true }, showlegend: false }}
                                config={PLOT_CFG} style={{ width: '100%' }} useResizeHandler
                              />
                              )}
                            </div>
                          </div>
                        </div>
                      </TabPanel>
                    );
                  }

                  return (
                 <Card className="shadow-sm animate__animated animate__fadeIn">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                       <h4 className="text-primary mb-0"><Share2 className="me-2"/>PTM Crosstalk Analysis</h4>
                       <Button label="Download CSV" icon="pi pi-download" severity="secondary" outlined size="small" onClick={downloadCSV} />
                   </div>

                    {/* info box */}
                    <div className="card card-spm bg-light border-0 p-3 mb-3 text-start">
                        <h5 className="mb-3"><i className="pi pi-info-circle me-2 text-primary" />Post-translational modifications that regulate each other in 3D</h5>
                        <p className="mb-3 small">
                            Two PTMs can influence one another when they share a structural microenvironment.
                            We read intra-chain residue contacts from the graph and call two modified residues a
                            <strong> crosstalk pair</strong> when they lie within <strong>&lt;&nbsp;8&nbsp;Å</strong>
                            (Cβ–Cβ). Higher-order hot-spots are <strong>network-motif cliques</strong> detected directly
                            on the contact graph. Pairs are typed by sequence separation:
                        </p>
                        <p className="mb-2">
                            <Tag value="same-residue" icon="pi pi-bullseye" rounded style={{ backgroundColor: '#DC2626', color: '#fff' }} />
                            <span className="small text-secondary ms-2">Two distinct marks on the <strong>same residue</strong> — competition / priming for the same site.</span>
                        </p>
                        <p className="mb-2">
                            <Tag value="proximal" icon="pi pi-arrows-h" rounded style={{ backgroundColor: '#3B82F6', color: '#fff' }} />
                            <span className="small text-secondary ms-2">Marks on residues <strong>close in sequence</strong> (|Δpos|&nbsp;≤&nbsp;5) and in 3D contact.</span>
                        </p>
                        <p className="mb-3">
                            <Tag value="distal" icon="pi pi-expand" rounded style={{ backgroundColor: '#7c3aed', color: '#fff' }} />
                            <span className="small text-secondary ms-2">Marks <strong>far in sequence</strong> but brought together in space — the allosterically interesting case.</span>
                        </p>
                        <p className="mb-2">
                            <Tag value="trio · 3-clique" icon="pi pi-asterisk" rounded style={{ backgroundColor: '#0f766e', color: '#fff' }} />
                            <span className="small text-secondary ms-2">Triangle of three modified residues all mutually &lt;&nbsp;8&nbsp;Å <strong>and co-observed in a single structure</strong>.</span>
                        </p>
                        <p className="mb-2">
                            <Tag value="tetrad · 4-clique" icon="pi pi-share-alt" rounded style={{ backgroundColor: '#9333ea', color: '#fff' }} />
                            <span className="small text-secondary ms-2">Four modified residues <strong>all six pairs</strong> in contact in one structure.</span>
                        </p>
                        <p className="mb-0">
                            <Tag value="interface" icon="pi pi-sitemap" rounded style={{ backgroundColor: '#f59e0b', color: '#fff' }} />
                            <span className="small text-secondary ms-2"><strong>Interface density</strong> = distinct partner residues on <em>other</em> chains a PTM contacts (&lt;&nbsp;8&nbsp;Å) <strong>within a single structure</strong> (best one reported) — interface vs core.</span>
                        </p>
                    </div>

                    {/* summary counts */}
                    <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                        <span className="text-500 small me-1">Summary:</span>
                        <Tag icon="pi pi-link" value={`${pairs.length} pairs`} severity="info" />
                        <Tag icon="pi pi-asterisk" value={`${trios.length} trios`} style={{ backgroundColor: '#0f766e', color: '#fff' }} />
                        <Tag icon="pi pi-share-alt" value={`${tetrads.length} tetrads`} style={{ backgroundColor: '#9333ea', color: '#fff' }} />
                        <Tag icon="pi pi-sitemap" value={`${ifaceCount} interface PTMs`} style={{ backgroundColor: '#f59e0b', color: '#fff' }} />
                    </div>

                    {/* tabbed sections (empty ones are hidden) */}
                    {panels.length > 0 ? (
                      <TabView>{panels}</TabView>
                    ) : (
                      <div className="text-muted small p-4 text-center"><i className="pi pi-inbox me-2" />No PTM crosstalk found for this protein.</div>
                    )}
                 </Card>
                  );
                })()}

               {/* --- RENDER: TABLE (Level 7 - PTM Hotspots) --- */}
                  {result && mode === 'level7' && (() => {
                  const rows = result.data || [];
                  const PF = { family: 'Inter, Helvetica, Arial, sans-serif', size: 12, color: '#374151' };
                  const PLOT_CFG = { displayModeBar: 'hover', displaylogo: false, responsive: true };
                  const mapRows = rows.map(r => ({
                      label: String(r.col1).split('_').pop(),
                      enr: (r.enr != null ? r.enr : (parseFloat(String(r.col3)) || 0)),
                  })).sort((a, b) => b.enr - a.enr).slice(0, 60);
                  const labels = mapRows.map(m => m.label);
                  const cats = [
                      ['Strong hotspot (≥4×)', '#ef4444', (e) => e >= 4],
                      ['Notable (≥2×)', '#f59e0b', (e) => e >= 2 && e < 4],
                      ['Unremarkable (<2×)', '#9ca3af', (e) => e < 2],
                  ];

                  return (
                     <Card className="shadow-sm animate__animated animate__fadeIn">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h4 className="text-primary mb-0"><Activity className="me-2"/>PTM Sequence Hotspots</h4>
                          <Button label="Download CSV" icon="pi pi-download" severity="secondary" outlined size="small" onClick={downloadCSV} />
                        </div>

                        {/* info box (Level 4 style) */}
                        <div className="card card-spm bg-light border-0 p-3 mb-4 text-start">
                          <h5 className="mb-3"><i className="pi pi-info-circle me-2 text-primary" />What are PTM sequence hotspots?</h5>
                          <p className="mb-3 small">
                            Residues whose ±15-residue sequence window carries more post-translational modifications
                            than expected from the protein-wide rate. Each modified residue is scored, tested against a
                            label-permutation null, and corrected with a permutation FDR.
                          </p>
                          <p className="mb-2">
                            <Tag value="±15 window" icon="pi pi-arrows-h" severity="info" rounded />
                            <span className="small text-secondary ms-2">Enrichment = (PTMs in window / window size) ÷ protein-wide PTM rate.</span>
                          </p>
                          <p className="mb-2">
                            <Tag value="permutation null (1000×)" icon="pi pi-sync" severity="warning" rounded />
                            <span className="small text-secondary ms-2">PTM labels are shuffled across positions (count preserved) to build the empirical null.</span>
                          </p>
                          <p className="mb-0">
                            <Tag value="permutation FDR" icon="pi pi-filter" severity="warning" rounded />
                            <span className="small text-secondary ms-2">The q-value (≤ 0.05 significant) accounts for the overlap between neighbouring windows.</span>
                          </p>
                        </div>

                        <ColumnToggleDataTable className="mb-2 nobreak" value={rows} paginator rows={5} stripedRows showGridlines filterDisplay="menu" tableStyle={{ minWidth: '66rem' }} responsiveLayout="scroll"
                            emptyMessage="No post-translational modifications found">
                            <Column field="col1" header="Residue" sortable></Column>
                            <Column field="col2" header="Density" sortable></Column>
                            <Column field="col3" header="Enrichment Score" sortable body={(row)=><b>{row.col3}</b>}></Column>
                            <Column field="q_value" header="q-value" sortable dataType="numeric" style={{ textAlign: 'right' }}
                                body={(row)=> row.q_value != null
                                    ? <span style={{ color: row.q_value <= 0.01 ? '#15803d' : (row.q_value <= 0.05 ? '#b45309' : '#9ca3af'), fontWeight: 600 }}>{Number(row.q_value).toFixed(3)}</span>
                                    : <span className="text-muted">—</span>}></Column>
                            <Column field="col4" header="PTM Types Found" body={(row)=><Tag value={row.col4} severity="warning" />}></Column>
                        </ColumnToggleDataTable>

                        {/* legend (Level 5 style) */}
                        <div className="mt-2 mb-4 small text-500 border-top pt-2">
                          <div className="mb-1">
                            <strong>q-value</strong> (permutation FDR):
                            <span className="ms-2" style={{color:'#15803d'}}>■ ≤ 0.01 strong</span>
                            <span className="ms-2" style={{color:'#b45309'}}>■ ≤ 0.05 significant</span>
                            <span className="ms-2" style={{color:'#9ca3af'}}>■ &gt; 0.05 not significant</span>
                          </div>
                          <div>
                            <strong>Enrichment</strong>:
                            <span className="ms-2" style={{color:'#ef4444'}}>■ Strong (≥4×)</span>
                            <span className="ms-2" style={{color:'#f59e0b'}}>■ Notable (≥2×)</span>
                            <span className="ms-2" style={{color:'#9ca3af'}}>■ Unremarkable (&lt;2×)</span>
                          </div>
                        </div>

                        {/* hotspot map: every residue, coloured by enrichment threshold */}
                        <div className="p-3 border rounded bg-white mb-3">
                          <h6 className="mb-3 text-primary"><i className="pi pi-chart-bar me-2" />Hotspot map along the protein</h6>
                          {mapRows.length === 0 ? (
                            <div className="text-muted small p-4 text-center">No modified residues to map.</div>
                          ) : (
                          <Plot
                            data={cats.map(([name, color, test]) => ({
                              type: 'bar', name,
                              x: labels,
                              y: mapRows.map(m => test(m.enr) ? m.enr : null),
                              marker: { color },
                              hovertemplate: `%{x}<br>%{y:.1f}× enriched · ${name}<extra></extra>`,
                            }))}
                            layout={{ barmode: 'overlay', height: 380, margin: { t: 36, r: 14, b: 90, l: 48 }, font: PF, xaxis: { type: 'category', categoryorder: 'array', categoryarray: labels, tickangle: -45, tickfont: { size: 10 }, fixedrange: true }, yaxis: { title: { text: 'enrichment (×)' }, gridcolor: '#eef2f7', zeroline: false, fixedrange: true, rangemode: 'tozero' }, legend: { orientation: 'h', y: 1.12, x: 0, font: { size: 11 } }, showlegend: true }}
                            config={PLOT_CFG} style={{ width: '100%' }} useResizeHandler
                          />
                          )}
                          <div className="small text-500 mt-1">Top modified residues ranked by the enrichment of their ±15 window; colour = strength threshold.</div>
                        </div>
                     </Card>
                  );
                })()}

                     {/* --- RENDER: TABLE (Level 8 - Domain Enrichment) --- */}
                      {result && mode === 'level8' && (() => {
                        const data = result.data || [];
                        const rows = data.filter(r => r.kind === 'domain');
                        const isEnrich = rows.length > 0 || data.some(r => r.fold != null);
                        const useRows = rows.length > 0 ? rows : data;
                        const PF = { family: 'Inter, Helvetica, Arial, sans-serif', size: 12, color: '#374151' };
                        const PLOT_CFG = { displayModeBar: 'hover', displaylogo: false, responsive: true };
                        const cats = [
                            ['Strong (≥4×)', '#ef4444', (e) => e >= 4],
                            ['Notable (≥2×)', '#f59e0b', (e) => e >= 2 && e < 4],
                            ['Unremarkable (<2×)', '#9ca3af', (e) => e < 2],
                        ];
                        const foldOf = (r) => (r.fold != null ? r.fold : (parseFloat(String(r.col4)) || 0));
                        const sourceTags = (s) => String(s || '').split(',').map(x => x.trim()).filter(Boolean).map((src, i) =>
                            <Tag key={i} value={src} severity={src === 'InterPro' ? 'info' : (src === 'Pfam' ? 'warning' : 'success')} className="me-1" />);
                        const qBody = (row) => row.q_value != null
                            ? <span style={{ color: row.q_value <= 0.01 ? '#15803d' : (row.q_value <= 0.05 ? '#b45309' : '#9ca3af'), fontWeight: 600 }}>{Number(row.q_value).toFixed(3)}</span>
                            : <span className="text-muted">—</span>;

                        const top = useRows.slice().sort((a, b) => foldOf(b) - foldOf(a)).slice(0, 30);
                        const labels = top.map(r => r.col1);

                        return (
                         <Card className="shadow-sm animate__animated animate__fadeIn">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <h4 className="text-primary mb-0"><Layers className="me-2"/>Domain–PTM Enrichment</h4>
                              <Button label="Download CSV" icon="pi pi-download" severity="secondary" outlined size="small" onClick={downloadCSV} />
                            </div>

                            {/* info box */}
                            <div className="card card-spm bg-light border-0 p-3 mb-4 text-start">
                              <h5 className="mb-3"><i className="pi pi-info-circle me-2 text-primary" />Which domains concentrate PTMs?</h5>
                              <p className="mb-3 small">
                                For each annotated domain we test whether it carries more modified residues than expected
                                from the protein-wide PTM rate (Fisher's exact test), reporting the
                                <strong> fold-enrichment</strong> (domain PTM density ÷ protein PTM density) and a
                                Benjamini–Hochberg <strong>q-value</strong>.
                              </p>
                              <p className="mb-2">
                                <Tag value="Fisher's exact test" icon="pi pi-calculator" severity="info" rounded />
                                <span className="small text-secondary ms-2">2×2 table: modified vs unmodified residues, inside vs outside the domain.</span>
                              </p>
                              <p className="mb-0">
                                <Tag value="BH-FDR" icon="pi pi-filter" severity="warning" rounded />
                                <span className="small text-secondary ms-2">q-value ≤ 0.05 = significant after correcting for the number of domains tested.</span>
                              </p>
                            </div>

                            {isEnrich ? (
                              <>
                                <ColumnToggleDataTable value={useRows} className="mb-2 nobreak" paginator rows={5} stripedRows showGridlines filterDisplay="menu" tableStyle={{ minWidth: '66rem' }} responsiveLayout="scroll" emptyMessage="No enriched domains">
                                  <Column field="col1" header="Domain" sortable></Column>
                                  <Column field="col2" header="Source" body={(row)=> sourceTags(row.col2)}></Column>
                                  <Column field="col3" header="PTMs / Size" sortable></Column>
                                  <Column field="col4" header="Fold enrichment" sortable body={(row)=><b>{row.col4}</b>}></Column>
                                  <Column field="q_value" header="q-value" sortable dataType="numeric" style={{ textAlign: 'right' }} body={qBody}></Column>
                                </ColumnToggleDataTable>

                                {/* legend (Level 5 style) */}
                                <div className="mt-2 mb-3 small text-500 border-top pt-2">
                                  <div className="mb-1"><strong>q-value</strong> (BH-FDR):
                                    <span className="ms-2" style={{color:'#15803d'}}>■ ≤ 0.01 strong</span>
                                    <span className="ms-2" style={{color:'#b45309'}}>■ ≤ 0.05 significant</span>
                                    <span className="ms-2" style={{color:'#9ca3af'}}>■ &gt; 0.05 not significant</span></div>
                                  <div><strong>Enrichment</strong>:
                                    <span className="ms-2" style={{color:'#ef4444'}}>■ Strong (≥4×)</span>
                                    <span className="ms-2" style={{color:'#f59e0b'}}>■ Notable (≥2×)</span>
                                    <span className="ms-2" style={{color:'#9ca3af'}}>■ Unremarkable (&lt;2×)</span></div>
                                </div>

                                {/* fold-enrichment plot (Level 7 style) */}
                                <div className="p-3 border rounded bg-white">
                                  <h6 className="mb-3 text-primary"><i className="pi pi-chart-bar me-2" />Fold-enrichment by domain</h6>
                                  {top.length === 0 ? (
                                    <div className="text-muted small p-4 text-center">Nothing to plot.</div>
                                  ) : (
                                  <Plot
                                    data={cats.map(([name, color, test]) => ({
                                      type: 'bar', name, x: labels,
                                      y: top.map(r => test(foldOf(r)) ? foldOf(r) : null),
                                      marker: { color },
                                      hovertemplate: `%{x}<br>%{y:.1f}× enriched · ${name}<extra></extra>`,
                                    }))}
                                    layout={{ barmode: 'overlay', height: 380, margin: { t: 36, r: 14, b: 170, l: 48 }, font: PF, xaxis: { type: 'category', categoryorder: 'array', categoryarray: labels, tickangle: -45, tickfont: { size: 9 }, fixedrange: true }, yaxis: { title: { text: 'fold enrichment (×)' }, gridcolor: '#eef2f7', zeroline: false, fixedrange: true, rangemode: 'tozero' }, legend: { orientation: 'h', y: 1.12, x: 0, font: { size: 11 } }, showlegend: true }}
                                    config={PLOT_CFG} style={{ width: '100%' }} useResizeHandler
                                  />
                                  )}
                                  <div className="small text-500 mt-1">Top 30 domains by fold-enrichment; colour = strength threshold.</div>
                                </div>
                              </>
                            ) : (
                              <>
                                <ColumnToggleDataTable value={data} className="mb-4 nobreak" paginator rows={5} stripedRows showGridlines filterDisplay="menu" tableStyle={{ minWidth: '66rem' }} responsiveLayout="scroll">
                                  <Column field="col1" header="Domain Name" sortable></Column>
                                  <Column field="col2" header="Source" body={(row)=> sourceTags(row.col2)}></Column>
                                  <Column field="col3" header="PTMs / Size" sortable></Column>
                                  <Column field="col4" header="Density" sortable body={(row)=><b>{row.col4}</b>}></Column>
                                </ColumnToggleDataTable>
                                {renderDensityPlot()}
                              </>
                            )}
                         </Card>
                        );
                      })()}

                     {/* --- RENDER: TABLE (Level 9 - PTM Disruption) --- */}
                      {result && mode === 'level9' && (() => {
                        const data = result.data || [];
                        const statsRow = data.find(r => r.kind === 'stats');
                        const pairs = data.filter(r => r.kind === 'pair');
                        const vpos = data.filter(r => r.kind === 'vpos');
                        const enr = data.filter(r => r.kind === 'enr');
                        const consData = data.filter(r => r.kind === 'cons');
                        const diseaseData = data.filter(r => r.kind === 'disease');
                        const tableRows = pairs.length ? pairs : data.filter(r => !['stats','vpos','enr','cons','disease'].includes(r.kind));
                        const PF = { family: 'Inter, Helvetica, Arial, sans-serif', size: 12, color: '#374151' };
                        const PLOT_CFG = { displayModeBar: 'hover', displaylogo: false, responsive: true };
                        const fmtP = (p) => (p == null ? '—' : (p < 0.001 ? Number(p).toExponential(2) : Number(p).toFixed(4)));
                        const statNum = (color) => ({ fontSize: '1.9rem', fontWeight: 700, color, lineHeight: 1.1 });
                        const isSig = statsRow && statsRow.p_value != null && statsRow.p_value < 0.05;
                        // Badge reflects the CI: robust only if the whole 95% CI is above 1.
                        const ciRobust = statsRow && statsRow.ci_low != null && statsRow.ci_low > 1;

                        // proximity distribution (near set only)
                        const pathDist = pairs.filter(p => p.variant_class === 'pathogenic' && p.col3 != null).map(p => p.col3);
                        const benDist = pairs.filter(p => p.variant_class === 'benign' && p.col3 != null).map(p => p.col3);

                        // per-PTM-site enrichment (plot)
                        const classified = pairs.filter(p => p.variant_class === 'pathogenic' || p.variant_class === 'benign');
                        const pn = classified.filter(p => p.variant_class === 'pathogenic').length;
                        const globalPathFrac = classified.length ? pn / classified.length : 0;
                        const bySite = {};
                        classified.forEach(p => {
                            const k = p.col2 || 'PTM';
                            if (!bySite[k]) bySite[k] = { site: k, path: 0, total: 0 };
                            bySite[k].total += 1;
                            if (p.variant_class === 'pathogenic') bySite[k].path += 1;
                        });
                        const siteRows = Object.values(bySite)
                            .map(s => ({ ...s, e: globalPathFrac > 0 ? (s.path / s.total) / globalPathFrac : 0 }))
                            .sort((a, b) => b.e - a.e).slice(0, 30);
                        const siteLabels = siteRows.map(s => String(s.site).replace(/^.*\bat\s*/, ''));
                        const cats = [
                            ['Strong (≥4×)', '#ef4444', (e) => e >= 4],
                            ['Notable (≥2×)', '#f59e0b', (e) => e >= 2 && e < 4],
                            ['Unremarkable (<2×)', '#9ca3af', (e) => e < 2],
                        ];

                        // structure viewer data
                        let acc = (statsRow && statsRow.accession) ? statsRow.accession : null;
                        if (!acc) { const m = ((pairs[0] && pairs[0].col2) || '').match(/at\s+([A-Za-z0-9]+)_/); acc = m ? m[1] : null; }
                        const afId = (statsRow && statsRow.af_id) ? statsRow.af_id : (acc ? `AF-${acc}` : null);
                        // Show the structure the user submitted in the form. It used to
                        // read level9Structure, which no control ever wrote to, so it
                        // stayed at its 'AF' initial value, never matched allStructs and
                        // always fell back to the AlphaFold monomer.
                        const selectedStruct = submittedPdbId || afId;
                        // Translate UniProt positions to what Mol* selects on
                        // (auth_seq_id / auth_asym_id) using the level9Mol mapping.
                        // Identity on the AlphaFold monomer; a real lookup on a PDB.
                        const l9Map = (level9Mol.structureId === selectedStruct) ? level9Mol.byPos : {};
                        const l9At = (p) => l9Map[parseInt(p, 10)] || null;

                        // One entry per (position, chain): the same residue sits in
                        // every subunit of an oligomer and Mol* selects chains apart.
                        const l9Expand = (p, make) => {
                            const m = l9At(p);
                            return m ? (m.chains || []).map(ch => make(m.mapped, ch)) : [];
                        };

                        const shownPosAll = vpos.filter(v => level9ViewerFilters.includes(v.vclass)).map(v => v.pos);
                        const shownFlat = shownPosAll.flatMap(p => l9Expand(p, (mapped, ch) => ({ mapped, ch })));
                        const shownPos = shownFlat.map(x => x.mapped);
                        const shownChains = shownFlat.map(x => x.ch);
                        // Positions the chosen structure does not resolve: reported rather
                        // than dropped in silence, since fewer dots than table rows would
                        // otherwise look like a bug.
                        const l9Unmapped = shownPosAll.filter(p => !l9At(p)).length;

                        const enrTable = enr.flatMap(r =>
                            l9Expand(r.col1, (mapped, ch) => ({ col1: mapped, chain: ch, col2: r.col2 })));

                        // ── PTM sites (parsed from the "Nearby PTM" column: "<type> at <ACC>_<pos>") ──
                        const PTM_COLOR_MAP = {
                          phosphorylation:          '#4393C3',
                          acetylation:              '#5aae61',
                          ubiquitination:           '#bf812d',
                          sumoylation:              '#d6604d',
                          methylation:              '#c51b7d',
                          'o-galnac glycosylation': '#e08214',
                          'o-glcnac glycosylation': '#fdb863',
                          other:                    '#666666',
                        };
                        const ptmColorFor = (t) => PTM_COLOR_MAP[String(t || '').toLowerCase().trim()] || PTM_COLOR_MAP.other;
                        const ptmSiteMap = {};
                        tableRows.forEach(r => {
                            const raw = String(r.col2 || '');
                            if (!raw) return;
                            const parts = raw.split(/\s+at\s+/i);
                            const typeRaw = parts[0].trim().toLowerCase();
                            const locus = parts[1] || parts[0];
                            const posMatch = locus.match(/_(\d+)/) || locus.match(/(\d+)/);
                            if (!posMatch) return;
                            const pos = posMatch[1];
                            const type = PTM_COLOR_MAP[typeRaw] ? typeRaw : 'other';
                            if (!ptmSiteMap[pos]) ptmSiteMap[pos] = { pos, type, color: ptmColorFor(type), label: raw };
                        });
                        const ptmSites = Object.values(ptmSiteMap);
                        const ptmTable = ptmSites.flatMap(s =>
                            l9Expand(s.pos, (mapped, ch) => ({ col1: mapped, chain: ch, ptm_type: s.type, color: s.color })));
                        const ptmTypesPresent = [...new Set(ptmSites.map(s => s.type))];
                        const nVariantsNearPtm = new Set(tableRows.map(r => r.col1).filter(Boolean)).size;
                        const nPtmSites = ptmSites.length;

                        return (
                         <Card className="shadow-sm animate__animated animate__fadeIn">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <h4 className="text-danger mb-0"><AlertTriangle className="me-2"/>PTM Structural Enrichment Analysis</h4>
                              <Button label="Download CSV" icon="pi pi-download" severity="secondary" outlined size="small" onClick={downloadCSV} />
                            </div>

                            {/* info box */}
                            <div className="card card-spm bg-light border-0 p-3 mb-4 text-start">
                              <h5 className="mb-3"><i className="pi pi-info-circle me-2 text-primary" />Are disease variants positioned near PTM sites?</h5>
                              <p className="mb-3 small">
                                Each ClinVar variant is flagged as <strong>near a PTM</strong> if it lies on a modified
                                residue (0&nbsp;Å) or contacts one within <strong>8&nbsp;Å</strong> in 3D (PDB or AlphaFold).
                                A two-sided Fisher exact test then asks whether the <strong>pathogenic</strong> vs
                                <strong> benign</strong> composition differs between variants near PTM sites and those far from them.
                              </p>
                              <p className="mb-2">
                                <Tag value="Fisher: pathogenic vs benign" icon="pi pi-chart-bar" severity="danger" rounded />
                                <span className="small text-secondary ms-2">2×2: near / far × pathogenic / benign. Benign variants are the within-protein control.</span>
                              </p>
                              <p className="mb-0">
                                <Tag value="Descriptive, not causal" icon="pi pi-info-circle" severity="info" rounded />
                                <span className="small text-secondary ms-2">The table reports the spatial relationship (same residue / within 8&nbsp;Å); it does not assert a disruption mechanism.</span>
                              </p>
                            </div>

                            {/* summary counts (Level 6 style) */}
                            <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                                <span className="text-500 small me-1">Summary:</span>
                                <Tag icon="pi pi-list" value={`${nVariantsNearPtm} variants near a PTM`} severity="info" />
                                <Tag icon="pi pi-bullseye" value={`${nPtmSites} PTM sites`} style={{ backgroundColor: '#4393C3', color: '#fff' }} />
                                <Tag icon="pi pi-tags" value={`${ptmTypesPresent.length} PTM types`} style={{ backgroundColor: '#c51b7d', color: '#fff' }} />
                            </div>

                            {/* detail table */}
                            <ColumnToggleDataTable value={tableRows} className="mb-4 nobreak" paginator rows={5} stripedRows showGridlines filterDisplay="menu" tableStyle={{ minWidth: '66rem' }} responsiveLayout="scroll"
                                emptyMessage="No variants on or within 8Å of a PTM site">
                                <Column field="col1" header="Variant" sortable filter filterPlaceholder="Search variant"></Column>
                                <Column field="col2" header="Nearby PTM" sortable></Column>
                                <Column field="col3" header="Distance (Å)" sortable dataType="numeric" body={(row)=> row.col3 != null ? <b>{Number(row.col3).toFixed(2)}</b> : '—'}></Column>
                                <Column field="col4" header="Spatial relationship" sortable body={(row)=>
                                    <Tag value={row.col4} severity={row.col4 && row.col4.startsWith('Same') ? 'danger' : 'warning'} />}></Column>
                                <Column field="col5" header="Source Structure" body={(row)=>
                                    (row.col5 && row.col5 !== '—')
                                      ? <Tag value={row.col5} severity={(row.source_type === 'pdb' || (!row.source_type && row.col5 !== 'AlphaFold')) ? 'success' : 'warning'} />
                                      : <span className="text-muted">{'—'}</span>}></Column>
                            </ColumnToggleDataTable>

                            {acc && (shownPos.length > 0 || enrTable.length > 0 || ptmTable.length > 0) && (
                            <>
                              <div className="d-flex align-items-center mt-4 mb-3">
                                <h5 className="mb-0 text-primary"><i className="pi pi-box me-2" />PTM Structure</h5>
                                <span className="ms-2 small text-500">on <strong>{selectedStruct}</strong></span>
                              </div>
                              {level9Mol.loading && (
                                <div className="small text-500 mb-2">
                                  <i className="pi pi-spin pi-spinner me-2" />Mapping positions onto {selectedStruct}…
                                </div>
                              )}
                              {l9Unmapped > 0 && (
                                <div className="alert alert-warning py-2 px-3 small mb-2" role="note">
                                  <i className="pi pi-exclamation-triangle me-2" />
                                  {l9Unmapped} of {shownPosAll.length} variant position(s) are not resolved in {selectedStruct} and cannot be shown. The table above lists all of them.
                                </div>
                              )}
                              <div className="mb-3">
                                <div className="row g-3 align-items-end">

                                  <div className="col-md-4 mb-2">
                                    <label className="form-label fw-bold text-secondary mb-1 d-block">Show in structure</label>
                                    <MultiSelect value={level9ViewerFilters} onChange={(e)=> setLevel9ViewerFilters(e.value || [])}
                                        options={[{ label: 'Pathogenic', value: 'pathogenic' }, { label: 'Benign', value: 'benign' }]}
                                        display="chip" placeholder="Select" style={{ width: '100%' }} />
                                  </div>
                                  <div className="col-md-6 mb-2">
                                    <label className="form-label fw-bold text-secondary mb-1 d-block">View mode</label>
                                    <SelectButton value={level9ViewMode} onChange={(e)=> e.value && setLevel9ViewMode(e.value)}
                                        options={[{ label: 'Variants', value: 'variants' }, { label: 'Enrichment', value: 'enrichment' }, { label: 'PTM sites', value: 'ptm' }]} className="fs--2" />
                                  </div>
                                </div>
                              </div>
                              <div className="viewer-box mb-2" style={{ minHeight: '500px' }}>
                                <MolstarViewerPath
                                    key={`l9-${level9ViewMode}-${selectedStruct}`}
                                    level="9"
                                    uniac={acc}
                                    pdbId={selectedStruct}
                                    positions={level9ViewMode === 'variants' ? shownPos : undefined}
                                    chain={level9ViewMode === 'variants' ? shownChains : undefined}
                                    datatable={level9ViewMode === 'enrichment' ? enrTable : (level9ViewMode === 'ptm' ? ptmTable : undefined)}
                                    centralityMetric={level9ViewMode === 'enrichment' ? undefined : undefined}
                                    colorBy={level9ViewMode === 'ptm' ? 'ptm' : undefined}
                                />
                              </div>
                              {level9ViewMode === 'enrichment' ? (
                                <div className="mt-1 p-3 border rounded bg-light mb-4">
                                  <div className="d-flex justify-content-between mb-1 small fw-bold text-secondary">
                                    <span className="text-secondary">Min: <strong>0</strong></span>
                                    <span className="text-center text-uppercase">3D Neighborhood Enrichment</span>
                                    <span className="text-secondary">Max: <strong>{Math.max(0, ...enr.map(r => r.col2 ?? 0))}</strong></span>
                                  </div>
                                  <div style={{ height: '12px', background: 'linear-gradient(to right, #3B82F6 0%, #FBBF24 50%, #EF4444 100%)', borderRadius: '6px' }}></div>
                                  <div className="d-flex justify-content-between mt-2 small text-500">
                                    <div className="text-start" style={{ width: '45%' }}>Blue: low pathogenic-variant density in the residue's 3D neighbourhood.</div>
                                    <div className="text-end" style={{ width: '45%' }}>Red: high density â clustered pathogenic variants forming a structural hotspot.</div>
                                  </div>
                                </div>
                              ) : level9ViewMode === 'ptm' ? (
                                <div className="mt-2 p-3 border rounded bg-light mb-4">
                                  <div className="small fw-bold text-secondary text-uppercase text-center mb-2">PTM sites coloured by modification type</div>
                                  <div className="d-flex flex-wrap justify-content-center gap-3 small text-500">
                                    {ptmTypesPresent.map(t => (
                                      <span key={t}><span style={{ display: 'inline-block', width: '12px', height: '12px', background: ptmColorFor(t), borderRadius: '3px', marginRight: '6px' }}></span>{t}</span>
                                    ))}
                                  </div>
                                  <div className="small text-500 mt-2 text-center">
                                    Modified residues located near ClinVar variants, coloured by PTM type.{' '}
                                    {selectedStruct && String(selectedStruct).toUpperCase().startsWith('AF-')
                                      ? 'Shown on the AlphaFold model (UniProt numbering).'
                                      : 'Positions follow UniProt numbering and may differ from the selected PDB.'}
                                  </div>
                                </div>
                              ) : (
                                <div className="small text-500 mb-4">
                                  ClinVar variants highlighted on the structure (toggle classes in "Show in structure").
                                  {selectedStruct && !String(selectedStruct).toUpperCase().startsWith('AF-') && ' Residue numbering follows the selected PDB and may differ from UniProt.'}
                                </div>
                              )}
                            </>
                            )}

                            {/* Fisher banner */}
                            {statsRow && (statsRow.path_total > 0 || statsRow.benign_total > 0) ? (
                              <div className="p-3 mb-3 text-start border rounded bg-white">
                                <div className="d-flex align-items-center justify-content-between mb-3">
                                  <h5 className="mb-0"><i className="pi pi-chart-bar me-2 text-primary" />Fisher Exact Test — pathogenic vs benign proximity to PTMs</h5>
                                  <Tag value={ciRobust ? "Robust enrichment" : "Inconclusive"} severity={ciRobust ? "success" : "warning"} />
                                </div>
                                <div className="row g-2 mb-1">
                                  <div className="col-md-3 col-6"><div className="card p-3 border-start border-3 border-danger h-100"><div className="text-secondary small mb-1">Pathogenic near PTM</div><div style={statNum('#dc2626')}>{statsRow.path_rate}<span style={{fontSize:'1.1rem'}}>%</span></div><div className="text-muted small">{statsRow.path_near}/{statsRow.path_total}</div></div></div>
                                  <div className="col-md-3 col-6"><div className="card p-3 border-start border-3 border-success h-100"><div className="text-secondary small mb-1">Benign near PTM</div><div style={statNum('#16a34a')}>{statsRow.benign_rate}<span style={{fontSize:'1.1rem'}}>%</span></div><div className="text-muted small">{statsRow.benign_near}/{statsRow.benign_total}</div></div></div>
                                  <div className="col-md-3 col-6"><div className="card p-3 border-start border-3 border-secondary h-100"><div className="text-secondary small mb-1">Odds ratio</div><div style={statNum('#6c757d')}>{statsRow.odds_ratio != null ? statsRow.odds_ratio : '—'}</div><div className="text-muted small">95% CI {statsRow.ci_low != null ? `${statsRow.ci_low} – ${statsRow.ci_high_is_infinite ? '∞' : statsRow.ci_high}` : '—'}</div></div></div>
                                  <div className="col-md-3 col-6"><div className="card p-3 border-start border-3 border-secondary h-100"><div className="text-secondary small mb-1">P-value</div><div style={statNum(isSig ? '#22c55e' : '#212529')}>{fmtP(statsRow.p_value)}</div></div></div>
                                </div>
                                <div className="small text-500 mt-1">{statsRow.path_rate}% of pathogenic vs {statsRow.benign_rate}% of benign variants lie on or within 8&nbsp;Å of a PTM site.</div>
                              </div>
                            ) : (statsRow ? (
                              <div className="small bg-light p-3 border rounded text-500 mb-3">Not enough classified ClinVar variants (pathogenic and benign) to run the enrichment test for this protein.</div>
                            ) : null)}

                            {/* proximity distribution */}
                            {(pathDist.length > 0 || benDist.length > 0) && (
                            <div className="p-3 border rounded bg-white mb-3">
                              <h6 className="mb-3 text-primary"><i className="pi pi-chart-bar me-2" />Proximity distribution (variant → nearest PTM)</h6>
                              <Plot
                                data={[
                                  { type: 'histogram', x: pathDist, name: 'Pathogenic', marker: { color: '#dc2626' }, opacity: 0.65, xbins: { start: 0, end: 8, size: 1 } },
                                  { type: 'histogram', x: benDist, name: 'Benign', marker: { color: '#16a34a' }, opacity: 0.65, xbins: { start: 0, end: 8, size: 1 } },
                                ]}
                                layout={{ barmode: 'overlay', height: 300, margin: { t: 16, r: 14, b: 44, l: 48 }, font: PF, xaxis: { title: { text: 'distance to PTM (Å)' }, fixedrange: true }, yaxis: { title: { text: 'variants' }, gridcolor: '#eef2f7', zeroline: false, fixedrange: true, rangemode: 'tozero' }, legend: { orientation: 'h', y: 1.15, x: 0 }, showlegend: true }}
                                config={PLOT_CFG} style={{ width: '100%' }} useResizeHandler
                              />
                            </div>
                            )}

                            {/* 3D neighborhood enrichment */}
                            {siteRows.length > 0 && (
                            <div className="p-3 border rounded bg-white mb-3">
                              <h6 className="mb-3 text-primary"><i className="pi pi-bolt me-2" />3D Neighborhood Enrichment Score (per PTM site)</h6>
                              <Plot
                                data={cats.map(([name, color, test]) => ({
                                  type: 'bar', name, x: siteLabels,
                                  y: siteRows.map(s => test(s.e) ? Number(s.e.toFixed(2)) : null),
                                  marker: { color },
                                  customdata: siteRows.map(s => [s.path, s.total]),
                                  hovertemplate: `%{x}<br>%{y:.1f}× · %{customdata[0]}/%{customdata[1]} pathogenic · ${name}<extra></extra>`,
                                }))}
                                layout={{ barmode: 'overlay', height: 340, margin: { t: 36, r: 14, b: 120, l: 48 }, font: PF, xaxis: { type: 'category', categoryorder: 'array', categoryarray: siteLabels, tickangle: -45, tickfont: { size: 10 }, fixedrange: true }, yaxis: { title: { text: 'pathogenic enrichment (×)' }, gridcolor: '#eef2f7', zeroline: false, fixedrange: true, rangemode: 'tozero' }, legend: { orientation: 'h', y: 1.12, x: 0, font: { size: 11 } }, showlegend: true }}
                                config={PLOT_CFG} style={{ width: '100%' }} useResizeHandler
                              />
                            </div>
                            )}

                            {/* consequence composition: near vs far */}
                            {consData.length > 0 && (() => {
                              const consequences = [...new Set(consData.map(r => r.consequence))];
                              const nearTot = consData.filter(r => r.near).reduce((s, r) => s + r.n, 0);
                              const farTot = consData.filter(r => !r.near).reduce((s, r) => s + r.n, 0);
                              const pct = (cq, near) => { const row = consData.find(r => r.consequence === cq && r.near === near); const t = near ? nearTot : farTot; return (row && t) ? (row.n / t * 100) : 0; };
                              const labels = consequences.map(c => c.replace(/_/g, ' '));
                              return (
                              <div className="p-3 border rounded bg-white mb-3">
                                <h6 className="mb-3 text-primary"><i className="pi pi-chart-bar me-2" />Variant composition by consequence (near vs far from PTM)</h6>
                                <Plot
                                  data={[
                                    { type: 'bar', name: 'Near PTM (≤8Å)', x: labels, y: consequences.map(c => pct(c, true)), marker: { color: '#0f766e' } },
                                    { type: 'bar', name: 'Far from PTM', x: labels, y: consequences.map(c => pct(c, false)), marker: { color: '#9ca3af' } },
                                  ]}
                                  layout={{ barmode: 'group', height: 320, margin: { t: 30, r: 14, b: 110, l: 48 }, font: PF, xaxis: { tickangle: -35, tickfont: { size: 10 }, fixedrange: true }, yaxis: { title: { text: '% of classified variants' }, gridcolor: '#eef2f7', zeroline: false, fixedrange: true, rangemode: 'tozero' }, legend: { orientation: 'h', y: 1.15, x: 0 }, showlegend: true }}
                                  config={PLOT_CFG} style={{ width: '100%' }} useResizeHandler
                                />
                                <div className="small text-500 mt-1">Composition within each group (pathogenic + benign variants). Compare the mix of consequences near vs far from PTM sites.</div>
                              </div>
                              );
                            })()}

                            {/* associated diseases (pathogenic, near PTM) */}
                            {diseaseData.length > 0 && (() => {
                              const dz = diseaseData.slice().sort((a, b) => a.n - b.n);
                              return (
                              <div className="p-3 border rounded bg-white mb-1">
                                <h6 className="mb-3 text-primary"><i className="pi pi-heart me-2" />Diseases associated with pathogenic variants near PTM sites</h6>
                                <Plot
                                  data={[{ type: 'bar', orientation: 'h', x: dz.map(d => d.n), y: dz.map(d => d.disease), marker: { color: '#dc2626' }, hovertemplate: '%{y}: %{x}<extra></extra>' }]}
                                  layout={{ height: Math.max(220, dz.length * 26 + 60), margin: { t: 16, r: 14, b: 36, l: 230 }, font: PF, xaxis: { title: { text: 'variants' }, gridcolor: '#eef2f7', zeroline: false, fixedrange: true, dtick: 1, rangemode: 'tozero' }, yaxis: { automargin: true, fixedrange: true, tickfont: { size: 10 } }, showlegend: false }}
                                  config={PLOT_CFG} style={{ width: '100%' }} useResizeHandler
                                />
                                <div className="small text-500 mt-1">ClinVar conditions reported for pathogenic variants located on or within 8&nbsp;Å of a PTM (top 15).</div>
                              </div>
                              );
                            })()}
                         </Card>
                        );
                      })()}

                      {/* --- RENDER: TABLE (Level 10 - HMM Hotspots) --- */}
                      {result && mode === 'level10' && (() => {
                        const data = result.data || [];
                        const crossref = data.filter(r => r.kind === 'crossref' || !r.kind);
                        const conssum = data.filter(r => r.kind === 'conssum');
                        const featdens = data.filter(r => r.kind === 'featdens');
                        const vdpath = data.filter(r => r.kind === 'vdpath');
                        const vdcons = data.filter(r => r.kind === 'vdcons');
                        const PF = { family: 'Inter, Helvetica, Arial, sans-serif', size: 12, color: '#374151' };
                        const PLOT_CFG = { displayModeBar: 'hover', displaylogo: false, responsive: true, modeBarButtons: [['toImage', 'pan2d', 'zoom2d', 'resetScale2d']] };
                        const BOTTOM_LEGEND = { orientation: 'h', y: -0.45, x: 0, yanchor: 'top', font: { size: 10 } };
                        const pct = (n, d) => (d > 0 ? (n / d * 100) : 0);

                        // ----- own-residue ClinVar class / AlphaMissense tag styling -----
                        const clinBucket = (c) => {
                          const p = String(c || '').toLowerCase();
                          if (['pathogenic', 'likely_pathogenic', 'pathogenic/likely_pathogenic'].includes(p)) return 'Pathogenic';
                          if (p.includes('conflicting')) return 'Conflicting';
                          if (p === 'uncertain_significance') return 'VUS';
                          if (['benign', 'likely_benign', 'benign/likely_benign'].includes(p)) return 'Benign';
                          return 'Other';
                        };
                        const CLASS_SEV = { 'Pathogenic': 'danger', 'Conflicting': 'info', 'VUS': 'warning', 'Benign': 'success', 'Other': 'secondary' };
                        const amSev = (am) => {
                          const a = String(am || '').toLowerCase();
                          if (a.includes('patho')) return 'danger';
                          if (a.includes('ambig')) return 'warning';
                          if (a.includes('benign')) return 'success';
                          return 'secondary';
                        };

                        // ----- PTM conservation across the family (fraction of family members) -----
                        const PTM_COLORS = { 'Phosphorylation': '#2563eb', 'Ubiquitination': '#9333ea', 'Acetylation': '#0891b2', 'Methylation': '#16a34a', 'SUMOylation': '#db2777', 'Modified residue': '#6b7280' };
                        const colorFor = (t) => PTM_COLORS[t] || '#f59e0b';
                        const consSorted = conssum.slice().sort((a, b) => pct(b.n_homologs, b.family_size) - pct(a.n_homologs, a.family_size)).slice(0, 40);
                        const consX = consSorted.map(r => `${r.dom}_${r.hmm}`);
                        const ptmTypes = [...new Set(consSorted.map(r => r.ptm))];
                        const consPlot = consSorted.length >= 3 ? (
                          <div className="p-3 border rounded bg-white mb-3">
                            <h6 className="mb-3 text-primary"><i className="pi pi-share-alt me-2" />PTM conservation across the family (per HMM column)</h6>
                            <Plot
                              data={ptmTypes.map(t => ({
                                type: 'bar', name: t, x: consX,
                                y: consSorted.map(r => r.ptm === t ? pct(r.n_homologs, r.family_size) : null),
                                marker: { color: colorFor(t) },
                                customdata: consSorted.map(r => [r.n_homologs, r.family_size]),
                                hovertemplate: `%{x}<br>%{customdata[0]}/%{customdata[1]} family members (%{y:.0f}%)<extra>${t}</extra>`,
                              }))}
                              layout={{ barmode: 'stack', height: 360, margin: { t: 24, r: 14, b: 150, l: 52 }, font: PF, xaxis: { type: 'category', categoryorder: 'array', categoryarray: consX, tickangle: -45, tickfont: { size: 9 }, title: { text: 'Pfam-A HMM column' }, fixedrange: false }, yaxis: { title: { text: '% of family members (in Atlantis)' }, gridcolor: '#eef2f7', zeroline: false, fixedrange: false, rangemode: 'tozero' }, legend: BOTTOM_LEGEND, showlegend: true }}
                              config={PLOT_CFG} style={{ width: '100%' }} useResizeHandler
                            />
                            <div className="small text-500 mt-1">Each bar = a PTM site of this protein; height = fraction of family members (in Atlantis) carrying a PTM at the same Pfam-A HMM column. Colour = PTM type.</div>
                          </div>
                        ) : null;

                        // ----- feature density across the family (per HMM column): PTM + binding + active -----
                        // Consumes `featdens` rows: { dom, hmm, family_size, ptm_n, bind_n, active_n, q_ptm, q_bind, q_active }
                        // (one row per HMM column where the QUERY has a residue). Stacked fraction of family
                        // members carrying each feature type (PTM / binding / active), stacked per column.
                        const FD_SERIES = [
                          { n: 'ptm_n', q: 'q_ptm', name: 'PTM', color: '#f59e0b' },
                          { n: 'bind_n', q: 'q_bind', name: 'Binding', color: '#3B82F6' },
                          { n: 'active_n', q: 'q_active', name: 'Active site', color: '#8B5CF6' },
                        ];
                        const fdSorted = featdens.slice()
                          .map(r => ({ ...r, _load: (r.ptm_n || 0) + (r.bind_n || 0) + (r.active_n || 0) }))
                          .sort((a, b) => b._load - a._load).slice(0, 40);
                        const fdX = fdSorted.map(r => `${r.dom}_${r.hmm}`);
                        const featDensPlot = fdSorted.length >= 3 ? (
                          <div className="p-3 border rounded bg-white mb-3">
                            <h6 className="mb-3 text-primary"><i className="pi pi-chart-bar me-2" />Feature density across the family (per HMM column)</h6>
                            <Plot
                              data={FD_SERIES.map(s => ({
                                type: 'bar', name: s.name, x: fdX,
                                y: fdSorted.map(r => pct(r[s.n] || 0, r.family_size)),
                                marker: { color: s.color },
                                customdata: fdSorted.map(r => [r[s.n] || 0, r.family_size]),
                                hovertemplate: `%{x}<br>%{customdata[0]}/%{customdata[1]} family members (%{y:.0f}%)<extra>${s.name}</extra>`,
                              }))}
                              layout={{ barmode: 'stack', height: 380, margin: { t: 24, r: 14, b: 150, l: 52 }, font: PF, xaxis: { type: 'category', categoryorder: 'array', categoryarray: fdX, tickangle: -45, tickfont: { size: 9 }, title: { text: 'Pfam-A HMM column (query positions)' }, fixedrange: false }, yaxis: { title: { text: '% of family members (in Atlantis)' }, gridcolor: '#eef2f7', zeroline: false, fixedrange: false, rangemode: 'tozero' }, legend: BOTTOM_LEGEND, showlegend: true }}
                              config={PLOT_CFG} style={{ width: '100%' }} useResizeHandler
                            />
                            <div className="small text-500 mt-1">For each Pfam-A HMM column where the query has a residue: fraction of family members (in Atlantis) carrying a <strong>PTM</strong>, <strong>ligand-binding</strong> or <strong>active-site</strong> annotation, stacked into the column's combined functional load. A member counted in two layers adds to both, so the stack reflects feature occurrences, not unique members.</div>
                          </div>
                        ) : null;

                        // ----- multi-feature residues per HMM column (where function converges) -----
                        const FEAT = [
                          { key: 'has_ptm', name: 'PTM', color: '#f59e0b' },
                          { key: 'has_bind', name: 'Binding', color: '#3B82F6' },
                          { key: 'has_active', name: 'Active site', color: '#8B5CF6' },
                        ];
                        const mfSeen = new Set();
                        const mfCols = [];
                        crossref.forEach(r => {
                          const key = r.col3;
                          if (mfSeen.has(key)) return;
                          mfSeen.add(key);
                          if (!(r.has_ptm || r.has_bind || r.has_active)) return;
                          mfCols.push({ key, residue: r.col2,
                            has_ptm: !!r.has_ptm, has_bind: !!r.has_bind, has_active: !!r.has_active,
                            total: (r.has_ptm ? 1 : 0) + (r.has_bind ? 1 : 0) + (r.has_active ? 1 : 0) });
                        });
                        mfCols.sort((a, b) => b.total - a.total).splice(40);
                        const mfX = mfCols.map(c => c.key);
                        const multiFeaturePlot = mfCols.length >= 1 ? (
                          <div className="p-3 border rounded bg-white mb-3">
                            <h6 className="mb-3 text-primary"><i className="pi pi-th-large me-2" />Multi-feature residues (per HMM column)</h6>
                            <Plot
                              data={FEAT.map(f => ({
                                type: 'bar', name: f.name, x: mfX,
                                y: mfCols.map(c => c[f.key] ? 1 : 0),
                                marker: { color: f.color },
                                customdata: mfCols.map(c => c.residue),
                                hovertemplate: `%{x}<br>residue %{customdata}<extra>${f.name}</extra>`,
                              }))}
                              layout={{ barmode: 'stack', height: 340, margin: { t: 24, r: 14, b: 150, l: 52 }, font: PF, xaxis: { type: 'category', categoryorder: 'array', categoryarray: mfX, tickangle: -45, tickfont: { size: 9 }, title: { text: 'Pfam-A HMM column (family-pathogenic)' }, fixedrange: false }, yaxis: { title: { text: 'functional layers on the query residue' }, dtick: 1, gridcolor: '#eef2f7', zeroline: false, fixedrange: false, rangemode: 'tozero' }, legend: BOTTOM_LEGEND, showlegend: true }}
                              config={PLOT_CFG} style={{ width: '100%' }} useResizeHandler
                            />
                            <div className="small text-500 mt-1">Columns where a query residue aligned to a family-pathogenic position is itself a functional site. Bar height = how many functions (PTM / binding / active) converge there; height &gt; 1 marks a multi-feature hotspot.</div>
                          </div>
                        ) : null;

                        // ----- family variant distribution at those HMM columns -----
                        const stackedPlot = (rows, title, hint, colorOf, labelOrder) => {
                          if (!rows.length) return null;
                          const cols = [...new Set(rows.map(r => r.hmm))].sort((a, b) => {
                            const da = String(a).split('_')[0], db = String(b).split('_')[0];
                            const na = Number(String(a).split('_').pop()), nb = Number(String(b).split('_').pop());
                            return da === db ? na - nb : da.localeCompare(db);
                          });
                          let labels = [...new Set(rows.map(r => r.label))];
                          if (labelOrder) labels = labelOrder.filter(l => labels.includes(l)).concat(labels.filter(l => !labelOrder.includes(l)));
                          const lk = {}; rows.forEach(r => { lk[`${r.hmm}|${r.label}`] = r.n; });
                          const nL = labels.length;
                          const tall = nL > 8;
                          const legRows = Math.ceil(nL / 2);
                          const totalH = tall ? Math.min(720, 380 + legRows * 18) : 360;
                          const bMargin = tall ? Math.min(500, 180 + legRows * 24) : 150;
                          const legendY = tall ? -0.6 : -0.45;
                          return (
                            <div className="p-3 border rounded bg-white mb-3">
                              <h6 className="mb-3 text-primary"><i className="pi pi-chart-bar me-2" />{title}</h6>
                              <Plot
                                data={labels.map(l => ({
                                  type: 'bar', name: l, x: cols,
                                  y: cols.map(c => lk[`${c}|${l}`] || 0),
                                  marker: { color: colorOf(l) },
                                  hovertemplate: `%{x}<br>%{y} variants<extra>${l}</extra>`,
                                }))}
                                layout={{ barmode: 'stack', height: totalH, margin: { t: 24, r: 14, b: bMargin, l: 52 }, font: PF, xaxis: { type: 'category', categoryorder: 'array', categoryarray: cols, tickangle: -45, tickfont: { size: 10 }, title: tall ? undefined : { text: 'Pfam-A HMM column', standoff: 12 }, automargin: false, fixedrange: false }, yaxis: { title: { text: 'ClinVar variants in family' }, gridcolor: '#eef2f7', zeroline: false, fixedrange: false, rangemode: 'tozero' }, legend: { ...BOTTOM_LEGEND, y: legendY }, showlegend: true }}
                                config={PLOT_CFG} style={{ width: '100%' }} useResizeHandler
                              />
                              <div className="small text-500 mt-1">{hint}</div>
                            </div>
                          );
                        };
                        const PATH_COLORS = { 'Pathogenic': '#ef4444', 'Conflicting': '#06b6d4', 'VUS': '#9ca3af', 'Benign': '#22c55e', 'Other': '#d1d5db' };
                        const PATH_ORDER = ['Pathogenic', 'Conflicting', 'VUS', 'Benign', 'Other'];
                        const CONS_PALETTE = ['#2563eb', '#dc2626', '#f59e0b', '#16a34a', '#a855f7', '#0891b2', '#db2777', '#65a30d', '#9ca3af', '#7c3aed', '#ea580c', '#0d9488'];
                        const consLabels = [...new Set(vdcons.map(r => r.label))];
                        const consColorMap = {}; consLabels.forEach((l, i) => { consColorMap[l] = CONS_PALETTE[i % CONS_PALETTE.length]; });
                        const pathDistPlot = stackedPlot(vdpath, 'Variant pathogenicity per HMM column (family)', 'All ClinVar variants in family homologs at these columns, by clinical significance.', (l) => PATH_COLORS[l] || '#d1d5db', PATH_ORDER);
                        const consDistPlot = stackedPlot(vdcons, 'Variant consequence per HMM column (family)', 'Same columns, broken down by molecular consequence.', (l) => consColorMap[l] || '#9ca3af', null);

                        return (
                         <Card className="shadow-sm animate__animated animate__fadeIn">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <h4 className="text-primary mb-0"><Network className="me-2"/>Pan-Family Evolutionary Mapping</h4>
                              <Button label="Download CSV" icon="pi pi-download" severity="secondary" outlined size="small" onClick={downloadCSV} />
                            </div>
                            {/* info box */}
                            <div className="card card-spm bg-light border-0 p-3 mb-4 text-start">
                              <h5 className="mb-3"><i className="pi pi-info-circle me-2 text-primary" />Which residues align to positions that are pathogenic elsewhere in the protein family?</h5>
                              <p className="mb-3 small">
                                For each residue of the query protein that maps to a <strong>Pfam-A HMM column</strong>,
                                Atlantis inspects the same column in homologous family members and reports those that
                                carry a <strong>pathogenic</strong> ClinVar variant at that position. Where available,
                                each match is cross-referenced with the query residue's functional annotation:
                                <strong> binding site</strong>, <strong>active site</strong> and
                                <strong> post-translational modification</strong>, together with its own ClinVar
                                significance and AlphaMissense class.
                              </p>
                              <p className="mb-2">
                                <Tag value="Positional homology" icon="pi pi-sitemap" severity="warning" rounded />
                                <span className="small text-secondary ms-2">Alignment is by shared Pfam-A match column, not proof of an identical modification or function.</span>
                              </p>
                              <p className="mb-0">
                                <Tag value="Descriptive, not causal" icon="pi pi-info-circle" severity="info" rounded />
                                <span className="small text-secondary ms-2">Hypothesis-generating; coverage is limited to columns the Pfam-A HMM aligns, and family fractions in the plots are computed over family members present in Atlantis, not the complete Pfam family.</span>
                              </p>
                            </div>

                            <ColumnToggleDataTable value={crossref} className="nobreak" paginator rows={5} stripedRows showGridlines filterDisplay="menu" tableStyle={{ minWidth: '82rem' }} responsiveLayout="scroll"
                                emptyMessage="No residue of this protein aligns to a strict-pathogenic position in a family homolog">
                              <Column field="col1" header="Domain Family" sortable></Column>
                              <Column field="col2" header="Query residue" sortable></Column>
                              <Column field="col3" header="Pfam-A HMM pos" sortable></Column>
                              <Column field="my_site" header="Microenvironment" sortable body={(row)=> (row.my_site && row.my_site !== '—') ? <span className="fw-semibold text-secondary">{row.my_site}</span> : <span className="text-muted">{'—'}</span>}></Column>
                              <Column field="col4" header="Related Protein" sortable></Column>
                              <Column field="col5" header="Residue in Relative" sortable></Column>
                              <Column field="col6" header="Known Pathogenic Mutation" body={(row)=><Tag value={row.col6} severity="danger" />}></Column>
                              <Column field="col7" header="AlphaMissense (homolog)" sortable body={(row)=> row.col7 ? <Tag value={row.col7} severity={amSev(row.col7)} /> : <span className="text-muted">{'—'}</span>}></Column>
                            </ColumnToggleDataTable>

                            {pathDistPlot}
                            {consDistPlot}
                            {consPlot}
                            {featDensPlot}
                            {multiFeaturePlot}
                         </Card>
                        );
                      })()}

                      {result && mode === 'level11' && (() => {
                        const rows = result.data || [];
                        const meta = rows.find(r => r.kind === 'meta') || {};
                        const scope = meta.scope || 'inter';
                        const targetAcc = meta.target_acc || 'query';
                        const LUT = (result.molstarData && result.molstarData.lookup) || {};
                        const PLUT = (result.molstarData && result.molstarData.partnerLookup) || {};

                        const posNum = (p) => { const t = String(p == null ? '' : p).split('_').pop(); const n = parseInt(t, 10); return (String(n) === t) ? n : null; };
                        const isPDB = (s) => s && !String(s).toUpperCase().startsWith('AF') && String(s).length <= 5;
                        const asArr = (x) => Array.isArray(x) ? x.filter(v => v != null && v !== '') : (x == null || x === '' ? [] : [x]);
                        const ClinTags = ({ vals }) => (<>{asArr(vals).map((v, i) => <Tag key={i} value={v} rounded className="me-1 mb-1" style={{ backgroundColor: variantPathogenicityColor(v), color: '#fff', fontSize: '0.64rem' }} />)}</>);
                        const ListText = ({ vals }) => { const a = asArr(vals); return <span style={{ fontSize: '0.78rem' }} className="text-secondary">{a.length ? a.join(', ') : '—'}</span>; };
                        const StructTag = ({ s }) => <Tag value={s} severity={isPDB(s) ? 'success' : 'warning'} />;
                        const PF = { family: 'Inter, Helvetica, Arial, sans-serif', size: 12, color: '#374151' };
                        const PLOT_CFG = { displayModeBar: 'hover', displaylogo: false, responsive: true, modeBarButtons: [['toImage', 'pan2d', 'zoom2d', 'resetScale2d']] };

                        // resolve mapped (structure auth_seq_id + chain) positions via the Level-11 mapper
                        const mapPositions = (structureId, uniprotPositions) => {
                          const lut = LUT[structureId] || {};
                          const pos = [], chain = [];
                          uniprotPositions.forEach(p => { const e = lut[p]; if (e && e.mapped != null) { const mp = parseInt(e.mapped, 10); if (!Number.isNaN(mp)) { pos.push(mp); chain.push(e.chain || 'A'); } } });
                          if (pos.length) return { pos, chain };
                          return { pos: uniprotPositions, chain: uniprotPositions.map(() => 'A') }; // fallback
                        };

                       const InfoBox = ({ wcc }) => (
                          <div className="card card-spm bg-light border-0 p-3 mb-4 text-start">
                            <h5 className="mb-3">
                              <i className="pi pi-info-circle me-2 text-primary" />
                              Where does disease converge in 3D?
                            </h5>

                            <p className="mb-3 small">
                              A <strong>co-pathogenic contact</strong> is formed when two residues are in
                              <strong> 3D contact</strong> (&le; 8 Å) and <strong>both</strong> carry
                              strict pathogenic ClinVar variants (Pathogenic/Likely Pathogenic).
                              This indicates a shared structural location affected by disease, not a causal relationship.
                            </p>

                            {wcc ? (
                              <p className="mb-2">
                                <Tag
                                  value="Structural clusters"
                                  icon="pi pi-share-alt"
                                  severity="danger"
                                  rounded
                                />
                                <span className="small text-secondary ms-2">
                                  Connected pathogenic contacts are grouped into
                                  <strong> structural clusters</strong> (weakly connected components).
                                  Clusters containing <strong>3 or more residues</strong> highlight
                                  potential disease hotspots within the protein.
                                </span>
                              </p>
                            ) : (
                              <p className="mb-2">
                                <Tag
                                  value="Interface contacts"
                                  icon="pi pi-shield"
                                  severity="danger"
                                  rounded
                                />
                                <span className="small text-secondary ms-2">
                                  Both contacting residues carry pathogenic variants, highlighting
                                  structural contacts disrupted by disease.
                                </span>
                              </p>
                            )}
                          </div>
                        );

                        // ================= INTER =================
                        if (scope === 'inter') {
                          const rawPairs = rows.filter(r => r.kind === 'pair');
                          const structRows = [];
                          rawPairs.forEach(p => {
                            if (p.structure) structRows.push({ ...p, _dist: (p.distance != null ? p.distance : p.min_distance) });
                            else if (Array.isArray(p.structures) && p.structures.length) p.structures.forEach(st => structRows.push({ ...p, structure: st, _dist: p.min_distance }));
                            else structRows.push({ ...p, structure: '—', _dist: p.min_distance });
                          });
                          const pairKey = (r) => `${r.partner_acc}|${r.res1}|${r.res2}`;
                          const nPairs = new Set(structRows.map(pairKey)).size;
                          const nPartners = new Set(structRows.map(r => r.partner_acc)).size;
                          const allStructs = [...new Set(structRows.map(r => r.structure).filter(s => s && s !== '—'))];
                          const selectedStruct = (level11Structure && allStructs.includes(level11Structure)) ? level11Structure : (allStructs[0] || null);
                          const viewerPos = [...new Set(structRows.filter(r => r.structure === selectedStruct).map(r => posNum(r.res1)).filter(v => v != null))];
                          const mapped = selectedStruct ? mapPositions(selectedStruct, viewerPos) : { pos: [], chain: [] };

                          // distinct pairs for the single co-pathogenicity dot plot (one point per pair)
                          const pairAgg = [...new Set(structRows.map(pairKey))].map(key => {
                            const grp = structRows.filter(r => pairKey(r) === key);
                            const dists = grp.map(r => r._dist).filter(d => d != null);
                            const nstr = new Set(grp.map(r => r.structure).filter(s => s && s !== '—')).size || 1;
                            return { ...grp[0], _nstr: nstr, _min: dists.length ? Math.min(...dists) : null };
                          });
                          const byPartner = {};
                          pairAgg.forEach(p => { (byPartner[p.partner_acc] = byPartner[p.partner_acc] || []).push(p); });
                          const partners = Object.keys(byPartner);
                          const partnerLabel = (pa) => { const g = (byPartner[pa][0] || {}).partner_gene; return g ? `${pa} (${g})` : pa; };
                          const dotHeight = Math.max(300, partners.length * 42 + 130);
                          const dotTraces = [{
                            type: 'scatter', mode: 'markers', showlegend: false,
                            x: pairAgg.map(p => posNum(p.res1)),
                            y: pairAgg.map(p => partnerLabel(p.partner_acc)),
                            text: pairAgg.map(p => `${targetAcc} ${p.res1} \u2194 ${p.partner_acc} ${p.res2}<br>${p._min != null ? p._min.toFixed(2) : '?'} \u00c5 \u00b7 ${p._nstr} structure(s)`),
                            hovertemplate: '%{text}<extra></extra>',
                            marker: { size: pairAgg.map(p => 8 + 3 * Math.min(6, p._nstr)), color: pairAgg.map(p => p._min == null ? 8 : p._min), colorscale: [[0, 'rgb(8,48,107)'], [0.5, 'rgb(66,146,198)'], [1, 'rgb(222,235,247)']], cmin: 2, cmax: 8, line: { width: 1, color: '#fff' }, showscale: true, colorbar: { title: { text: '\u00c5', side: 'right' }, len: 0.6, thickness: 12, x: 1.02 } },
                          }];
                          const dotAnn = [];
                          const dotAxes = { xaxis: { gridcolor: '#eef2f7', zeroline: false, tickfont: { size: 9 }, title: { text: `${targetAcc} position`, font: { size: 11 } } }, yaxis: { type: 'category', automargin: true, gridcolor: '#f3f4f6', tickfont: { size: 10 } } };

                          // partner residues (res2) to highlight in yellow, per "Show in structure" choice
                          const interShowOpts = [{ label: 'Query + all partners', value: 'all' }, { label: 'Query only', value: 'query' }, ...partners.map(pa => ({ label: `Query + ${pa}`, value: pa }))];
                          const showPartners = level11InterShow === 'query' ? [] : (level11InterShow === 'all' ? partners : (partners.includes(level11InterShow) ? [level11InterShow] : partners));
                          const partnerPosArr = [], partnerChainArr = [];
                          if (selectedStruct) showPartners.forEach(pa => {
                            const plut = (PLUT[selectedStruct] || {})[pa] || {};
                            [...new Set(structRows.filter(r => r.structure === selectedStruct && r.partner_acc === pa).map(r => posNum(r.res2)).filter(v => v != null))]
                              .forEach(p => { const e = plut[p]; if (e && e.mapped != null) { const mp = parseInt(e.mapped, 10); if (!Number.isNaN(mp)) { partnerPosArr.push(mp); partnerChainArr.push(e.chain || 'A'); } } });
                          });

                          return (
                            <Card className="shadow-sm animate__animated animate__fadeIn">
                              <div className="d-flex justify-content-between align-items-center mb-3">
                                <h4 className="text-primary mb-0"><Network className="me-2"/>Structural Co-Pathogenicity — interface partners</h4>
                                <Button label="Download CSV" icon="pi pi-download" severity="secondary" outlined size="small" onClick={downloadCSV} />
                              </div>
                              <InfoBox />

                              {structRows.length === 0 ? (
                                <div className="alert alert-info small mb-0">No bilateral co-pathogenic interface found for <strong>{targetAcc}</strong>. It needs the partner protein to also be modelled with its own pathogenic ClinVar variant at the contact.</div>
                              ) : (
                                <>
                                  <ColumnToggleDataTable value={structRows} className="mb-4 nobreak" paginator rows={5} stripedRows showGridlines filterDisplay="menu" sortField="_dist" sortOrder={1} tableStyle={{ minWidth: '66rem' }} responsiveLayout="scroll">
                                    <Column field="partner_acc" header="Partner" sortable filter filterPlaceholder="Search" body={(r)=><span><b>{r.partner_acc}</b>{r.partner_gene ? <span className="text-secondary"> ({r.partner_gene})</span> : null}</span>}></Column>
                                    <Column field="res1" header="Query variant" sortable body={(r)=><span><b>{r.res1}</b> <ClinTags vals={r.clin1}/><br/><ListText vals={r.aa1}/></span>}></Column>
                                    <Column field="res2" header="Partner variant" sortable body={(r)=><span><b>{r.res2}</b> <ClinTags vals={r.clin2}/><br/><ListText vals={r.aa2}/></span>}></Column>
                                    <Column header="Consequence" body={(r)=><span style={{ fontSize: '0.78rem' }}><span className="text-secondary">Q:</span> <ListText vals={r.cons1}/><br/><span className="text-secondary">P:</span> <ListText vals={r.cons2}/></span>}></Column>
                                    <Column header="Disease" body={(r)=><span style={{ fontSize: '0.78rem' }}><span className="text-secondary">Q:</span> <ListText vals={r.dis1}/><br/><span className="text-secondary">P:</span> <ListText vals={r.dis2}/></span>}></Column>
                                    <Column field="structure" header="Source Structure" sortable body={(r)=><StructTag s={r.structure}/>}></Column>
                                    <Column field="_dist" header="Distance (Å)" sortable dataType="numeric" body={(r)=> r._dist != null ? <b>{Number(r._dist).toFixed(2)}</b> : '—'}></Column>
                                  </ColumnToggleDataTable>

                                  {selectedStruct && mapped.pos.length > 0 && (
                                    <>
                                      <div className="d-flex align-items-center mt-2 mb-2"><h5 className="mb-0 text-primary"><i className="pi pi-box me-2" />Interface structure</h5></div>
                                      <div className="row g-3 align-items-end mb-4">
                                        <div className="col-md-5 mb-4">
                                          <label className="form-label fw-bold text-secondary mb-2 d-block">Structure</label>
                                          <Dropdown value={selectedStruct} options={allStructs.map(id => ({ label: id, value: id }))} onChange={(e)=> setLevel11Structure(e.value)} filter placeholder="Select" style={{ width: '100%' }} className="fs--2" />
                                        </div>
                                        <div className="col-md-5 mb-4">
                                          <label className="form-label fw-bold text-secondary mb-2 d-block">Show in structure</label>
                                          <Dropdown value={level11InterShow} options={interShowOpts} onChange={(e)=> setLevel11InterShow(e.value)} placeholder="Select" style={{ width: '100%' }} className="fs--2" />
                                        </div>
                                      </div>
                                      <div className="viewer-box mb-3" style={{ minHeight: '500px' }}>
                                        <MolstarViewerPath key={`l11-inter-${selectedStruct}-${level11InterShow}`} level="11" uniac={targetAcc} pdbId={selectedStruct} positions={mapped.pos} chain={mapped.chain} partnerPositions={partnerPosArr} partnerChain={partnerChainArr} />
                                      </div>
                                      <div className="small text-500 mb-3">Query residues of <strong>{targetAcc}</strong> in <span style={{color:'#c93c20',fontWeight:600}}>red</span>, partner residues in <span style={{color:'#b8860b',fontWeight:600}}>yellow</span> on <strong>{selectedStruct}</strong> (mapped to the structure's numbering &amp; chain). Use “Show in structure” to pick which partner's variants to display.</div>
                                    </>
                                  )}

                                  <Message severity="info" className="w-100 mb-4"
                                    text={`${nPairs} co-pathogenic interface pair(s) across ${nPartners} partner(s) for ${targetAcc}, spanning ${allStructs.length} structure(s).`} />

                                  {partners.length > 0 && (
                                    <div className="p-3 border rounded bg-white mt-2">
                                      <h6 className="mb-3 text-primary"><i className="pi pi-th-large me-2" />Interface co-pathogenicity map</h6>
                                      <Plot data={dotTraces}
                                        layout={{ height: dotHeight, margin: { t: 20, r: 70, b: 44, l: 160 }, font: PF, annotations: dotAnn, ...dotAxes }}
                                        config={PLOT_CFG} style={{ width: '100%' }} useResizeHandler />
                                      <div className="small text-500 mt-1">Each dot is a co-pathogenic pair (query residue × a partner residue); colour = contact distance (dark&nbsp;=&nbsp;closer), size = number of structures confirming it. Partners are stacked on the vertical axis, so it scales past 6 partners without truncation.</div>
                                    </div>
                                  )}
                                </>
                              )}
                            </Card>
                          );
                        }

                        // ================= INTRA (per structure) =================
                        const edges = rows.filter(r => r.kind === 'edge');
                        const intraStructsRaw = [...new Set(edges.flatMap(e => e.structures || []).filter(Boolean))];
                        const intraStructs = intraStructsRaw.length ? intraStructsRaw : [`AF-${targetAcc}`];
                        const intraSel = (level11IntraStructure && intraStructs.includes(level11IntraStructure)) ? level11IntraStructure : intraStructs[0];
                        const selEdges = edges.filter(e => (e.structures || []).includes(intraSel));
                        const connectedComponents = (elist) => {
                          const adj = {}; const push = (a, b) => { (adj[a] = adj[a] || []).push(b); };
                          elist.forEach(e => { push(e.res1, e.res2); push(e.res2, e.res1); });
                          const seen = new Set(); const comps = [];
                          Object.keys(adj).forEach(n => {
                            if (seen.has(n)) return;
                            const stack = [n], comp = []; seen.add(n);
                            while (stack.length) { const u = stack.pop(); comp.push(u); (adj[u] || []).forEach(v => { if (!seen.has(v)) { seen.add(v); stack.push(v); } }); }
                            comps.push(comp);
                          });
                          return comps;
                        };
                        const pk = (m) => { const n = posNum(m); return n == null ? 0 : n; };
                        const resfeat = rows.filter(r => r.kind === 'resfeat');
                        const featByPos = {}; resfeat.forEach(f => { const q = posNum(f.position); if (q != null) featByPos[q] = f; });
                        const uniqA = (arr) => [...new Set(arr)];
                        const aggFeat = (members) => { const F = members.map(m => featByPos[posNum(m)] || {}); return { variants: uniqA(F.flatMap(f => f.variants || [])), actives: uniqA(F.flatMap(f => f.active_labels || [])), domains: uniqA(F.flatMap(f => f.domain_labels || [])), binding: uniqA(F.flatMap(f => f.ligand_labels || [])), ptms: uniqA(F.flatMap(f => f.ptm_labels || [])) }; };
                        const clusterMetrics = (members, elist) => { const S = new Set(members); const adj = {}; members.forEach(m => adj[m] = new Set()); let E = 0; elist.forEach(e => { if (S.has(e.res1) && S.has(e.res2)) { adj[e.res1].add(e.res2); adj[e.res2].add(e.res1); E++; } }); const N = members.length; const density = N > 1 ? (2 * E) / (N * (N - 1)) : 0; let csum = 0; members.forEach(m => { const nb = [...adj[m]]; const k = nb.length; if (k < 2) return; let links = 0; for (let i = 0; i < k; i++) for (let j = i + 1; j < k; j++) if (adj[nb[i]].has(nb[j])) links++; csum += (2 * links) / (k * (k - 1)); }); const clustering = N > 0 ? csum / N : 0; return { density, clustering }; };
                        const clusters = connectedComponents(selEdges)
                          .map(comp => { const members = comp.slice().sort((a, b) => pk(a) - pk(b)); const mt = clusterMetrics(members, selEdges); const ft = aggFeat(members); return { members, size: comp.length, meaningful: comp.length >= 3, density: mt.density, clustering: mt.clustering, ...ft }; })
                          .sort((a, b) => b.size - a.size)
                          .map((c, i) => ({ ...c, cluster_id: i + 1 }));
                        const nMeaningful = clusters.filter(c => c.meaningful).length;
                        const allMemberPos = [...new Set(clusters.flatMap(c => c.members.map(posNum)).filter(v => v != null))];
                        const clusterOpts = [{ label: 'All clusters', value: 'all' }, ...clusters.map(c => ({ label: `C${c.cluster_id} (n=${c.size})${c.meaningful ? ' \u2605' : ''}`, value: String(c.cluster_id) }))];
                        const pickedCluster = clusters.find(c => String(c.cluster_id) === String(level11ClusterPick));
                        const shownMemberPos = pickedCluster ? [...new Set(pickedCluster.members.map(posNum).filter(v => v != null))] : allMemberPos;
                        const intraMapped = mapPositions(intraSel, shownMemberPos);
                        const cidOf = {}; clusters.forEach(c => c.members.forEach(m => { cidOf[m] = c.cluster_id; }));

                        const landscape = (() => {
                          const nodes = [...new Set(selEdges.flatMap(e => [e.res1, e.res2]))];
                          const n = nodes.length;
                          if (n < 2) return null;
                          const idx = {}; nodes.forEach((id, i) => { idx[id] = i; });
                          const jit = (s) => ((((s * 2654435761) >>> 0) % 1000) / 1000 - 0.5) * 0.5;
                          const pos = nodes.map((_, i) => [Math.cos(2 * Math.PI * i / n) + jit(i + 1), Math.sin(2 * Math.PI * i / n) + jit(i + 7)]);
                          const E = selEdges.map(e => [idx[e.res1], idx[e.res2]]).filter(([a, b]) => a != null && b != null);
                          const k = 1.0; let temp = 0.6;
                          for (let it = 0; it < 250; it++) {
                            const disp = nodes.map(() => [0, 0]);
                            for (let a = 0; a < n; a++) for (let b = a + 1; b < n; b++) {
                              let dx = pos[a][0] - pos[b][0], dy = pos[a][1] - pos[b][1];
                              const d = Math.sqrt(dx * dx + dy * dy) || 0.01; const f = k * k / d;
                              disp[a][0] += dx / d * f; disp[a][1] += dy / d * f; disp[b][0] -= dx / d * f; disp[b][1] -= dy / d * f;
                            }
                            E.forEach(([a, b]) => {
                              let dx = pos[a][0] - pos[b][0], dy = pos[a][1] - pos[b][1];
                              const d = Math.sqrt(dx * dx + dy * dy) || 0.01; const f = d * d / k;
                              disp[a][0] -= dx / d * f; disp[a][1] -= dy / d * f; disp[b][0] += dx / d * f; disp[b][1] += dy / d * f;
                            });
                            for (let a = 0; a < n; a++) { const dl = Math.sqrt(disp[a][0] ** 2 + disp[a][1] ** 2) || 0.01; pos[a][0] += disp[a][0] / dl * Math.min(dl, temp); pos[a][1] += disp[a][1] / dl * Math.min(dl, temp); }
                            temp *= 0.985;
                          }
                          const xs = nodes.map((_, i) => pos[i][0]), ys = nodes.map((_, i) => pos[i][1]);
                          const cen = {};
                          nodes.forEach((id, i) => { const c = cidOf[id]; if (!c) return; (cen[c] = cen[c] || { x: 0, y: 0, n: 0 }); cen[c].x += pos[i][0]; cen[c].y += pos[i][1]; cen[c].n += 1; });
                          const labels = Object.keys(cen).map(c => { const sz = (clusters.find(cl => String(cl.cluster_id) === String(c)) || {}).size || cen[c].n; return { x: cen[c].x / cen[c].n, y: cen[c].y / cen[c].n, t: `C${c} (n=${sz})` }; });
                          return (
                            <Plot data={[
                                { type: 'histogram2dcontour', x: xs, y: ys, colorscale: 'Greens', showscale: false, ncontours: 12, contours: { coloring: 'lines' }, line: { width: 1.5 }, hoverinfo: 'skip' },
                                { type: 'scatter', mode: 'markers', x: xs, y: ys, marker: { size: 7, color: '#14532d', line: { width: 1, color: '#fff' } }, text: nodes, hovertemplate: '%{text}<extra></extra>', showlegend: false },
                                { type: 'scatter', mode: 'text', x: labels.map(l => l.x), y: labels.map(l => l.y), text: labels.map(l => l.t), textfont: { size: 11, color: '#0f3d24' }, hoverinfo: 'skip', showlegend: false },
                              ]}
                              layout={{ height: 480, margin: { t: 12, r: 12, b: 12, l: 12 }, font: PF, xaxis: { visible: false, zeroline: false }, yaxis: { visible: false, zeroline: false, scaleanchor: 'x' }, paper_bgcolor: 'rgba(0,0,0,0)', plot_bgcolor: 'rgba(0,0,0,0)' }}
                              config={PLOT_CFG} style={{ width: '100%' }} useResizeHandler />
                          );
                        })();

                        return (
                          <Card className="shadow-sm animate__animated animate__fadeIn">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <h4 className="text-primary mb-0"><Network className="me-2"/>Structural Co-Pathogenicity — intramolecular clusters</h4>
                              <Button label="Download CSV" icon="pi pi-download" severity="secondary" outlined size="small" onClick={downloadCSV} />
                            </div>
                            <InfoBox wcc />

                            {intraStructsRaw.length === 0 ? (
                              <div className="alert alert-info small mb-0">No intramolecular co-pathogenic contact found for <strong>{targetAcc}</strong>.</div>
                            ) : (
                              <>
                                <div className="row g-3 align-items-end mb-4">
                                  <div className="col-md-5">
                                    <label className="form-label fw-bold text-secondary mb-2 d-block">Structure</label>
                                    <Dropdown value={intraSel} options={intraStructs.map(id => ({ label: id, value: id }))} onChange={(e)=> setLevel11IntraStructure(e.value)} filter placeholder="Select" style={{ width: '100%' }} className="fs--2" />
                                  </div>
                                </div>

                                <ColumnToggleDataTable value={clusters} className="mb-4 nobreak" paginator rows={5} stripedRows showGridlines sortField="size" sortOrder={-1} tableStyle={{ minWidth: '96rem' }} responsiveLayout="scroll" rowClassName={(r)=> r.meaningful ? 'table-primary' : ''}>
                                  <Column field="cluster_id" header="#" sortable style={{ width: '4rem' }}></Column>
                                  <Column field="size" header="Size" sortable body={(r)=><span className="badge rounded-pill" style={{ backgroundColor: r.meaningful ? '#16a34a' : '#9ca3af', color: '#fff', minWidth: '40px' }}>{r.size}</span>}></Column>
                                  <Column field="members" header="Pathogenic residues in contact" body={(r)=><span style={{ whiteSpace: 'nowrap' }}>{r.members.map((m, i) => <Tag key={i} value={m} rounded className="me-1" style={{ backgroundColor: variantPathogenicityColor('pathogenic'), color: '#fff', fontSize: '0.66rem' }} />)}</span>}></Column>
                                  <Column field="meaningful" header="Signal" body={(r)=> r.meaningful ? <Tag value="cluster ≥ 3" severity="success" rounded /> : <span className="text-secondary small">pair</span>}></Column>
                                  <Column field="density" header="Density" sortable body={(r)=><span>{(r.density ?? 0).toFixed(2)}</span>}></Column>
                                  <Column field="clustering" header="Clustering coeff." sortable body={(r)=><span>{(r.clustering ?? 0).toFixed(2)}</span>}></Column>
                                  <Column header="Variants" style={{ maxWidth: '26rem' }} body={(r)=> (r.variants && r.variants.length) ? <span style={{ display: 'block', whiteSpace: 'nowrap', overflowX: 'auto', maxWidth: '26rem' }}>{r.variants.map((v, i) => <Tag key={i} value={v} rounded className="me-1" severity="danger" style={{ fontSize: '0.62rem' }} />)}</span> : <span className="text-500">—</span>}></Column>
                                  <Column header="Features" body={(r)=>{ const t=[]; if (r.actives && r.actives.length) t.push([`Active: ${r.actives.join(', ')}`, 'success']); if (r.domains && r.domains.length) t.push([`Domain: ${r.domains.join(', ')}`, 'warning']); if (r.binding && r.binding.length) t.push([`Binds: ${r.binding.join(', ')}`, 'info']); if (r.ptms && r.ptms.length) t.push([`PTM: ${r.ptms.join(', ')}`, null]); return t.length ? <span className="d-flex flex-column gap-1">{t.map(([v, sv], k) => <Tag key={k} value={v} severity={sv || undefined} style={{ fontSize: '0.62rem' }} />)}</span> : <span className="text-500">—</span>; }}></Column>
                                </ColumnToggleDataTable>

                                {intraMapped.pos.length > 0 && (
                                  <>
                                    <div className="d-flex align-items-center mt-2 mb-2"><h5 className="mb-0 text-primary"><i className="pi pi-box me-2" />Cluster structure</h5></div>
                                    <div className="row g-3 align-items-end mb-8">
                                      <div className="col-md-5">
                                        <label className="form-label fw-bold text-secondary mb-2 d-block">Show in structure</label>
                                        <Dropdown value={level11ClusterPick} options={clusterOpts} onChange={(e)=> setLevel11ClusterPick(e.value)} placeholder="Select" style={{ width: '100%' }} className="fs--2" />
                                      </div>
                                    </div>
                                    <div className="viewer-box mb-3" style={{ minHeight: '500px' }}>
                                      <MolstarViewerPath key={`l11-intra-${intraSel}-${level11ClusterPick}`} level="11" uniac={targetAcc} pdbId={intraSel} positions={intraMapped.pos} chain={intraMapped.chain} partnerPositions={[]} partnerChain={[]} />
                                    </div>
                                    <div className="small text-500 mb-3">Clustered pathogenic residues for <strong>{intraSel}</strong> highlighted (red spheres), mapped to the structure's numbering &amp; chain.</div>
                                  </>
                                )}

                                <Message severity="info" className="w-100 mb-4"
                                  text={`${clusters.length} cluster(s) of mutually-contacting pathogenic residues for ${targetAcc} in ${intraSel}; ${nMeaningful} of size ≥ 3 `} />

                                {landscape && (
                                  <div className="p-3 border rounded bg-white mt-2">
                                    <h6 className="mb-3 text-primary"><i className="pi pi-map me-2" />Pathogenic cluster landscape — {intraSel}</h6>
                                    {landscape}
                                    <div className="small text-500 mt-1">Force-directed layout of pathogenic residues in mutual contact <strong>within {intraSel}</strong>; green contours outline the clusters. Switch the structure above to recompute clusters, viewer and landscape.</div>
                                  </div>
                                )}
                              </>
                            )}
                          </Card>
                        );
                      })()}
                    {/* LEVEL 12: VUS Resolver (Neighbor Analysis) */}
                            {result && mode === 'level12' && (() => {
                                const vus = (result.data || []).filter((r) => r && r.kind === 'vus');
                                const meta = (result.data || []).find((r) => r && r.kind === 'meta') || {};
                                const hasNb = (r) => ((r.intra_n_neighbours || 0) + (r.inter_n_neighbours || 0)) > 0;
                                const vusShown = vus.filter(hasNb);
                                vusShown.forEach((r) => { const tot = (r.intra_n_neighbours || 0) + (r.inter_n_neighbours || 0); r._pathFrac = tot > 0 ? r.obs_n_total / tot : null; });
                                const vusUncovered = vus.filter((r) => !hasNb(r));
                                const fmt = (x) => (x === null || x === undefined ? '\u2014' : (typeof x === 'number' ? x.toFixed(3) : x));
                                const fmtDist = (x) => (x === null || x === undefined ? '\u2014' : (typeof x === 'number' ? x.toFixed(2) : x));
                                const clinTag = (c) => <Tag value={c} severity={c && String(c).includes('conflicting') ? 'warning' : 'info'} />;
                                const microBody = (r) => { const t=[]; const P=(r.ptm_labels_total||[]), A=(r.active_labels_total||[]), B=(r.binding_labels_total||[]); if (P.length) t.push([`PTM: ${P.join(', ')}`,'info']); if (A.length) t.push([`Active: ${A.join(', ')}`,'success']); if (B.length) t.push([`Binds: ${B.join(', ')}`,'warning']); return t.length ? <span className="d-flex gap-1 flex-wrap">{t.map(([v,s],k) => <Tag key={k} value={v} severity={s} />)}</span> : <span className="text-500">—</span>; };
                                const topBars = [...vusShown].sort((a, b) => b.obs_n_total - a.obs_n_total).slice(0, 15);
                                return (
                                <Card className="shadow-sm animate__animated animate__fadeIn">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h4 className="text-primary mb-0"><AlertTriangle className="me-2"/>VUS Structural Prioritisation</h4>
                                        <Button label="Download CSV" icon="pi pi-download" severity="secondary" outlined size="small" onClick={downloadCSV} />
                                    </div>

                                    <div className="card card-spm bg-light border-0 p-3 mb-4 text-start">
                                    <h5 className="mb-3">
                                        <i className="pi pi-info-circle me-2 text-primary" />
                                        Which VUS are structurally supported for re-evaluation?
                                    </h5>

                                    <p className="mb-3 small">
                                        Every ClinVar <strong>VUS</strong> (uncertain / conflicting) is evaluated
                                        using its <strong>3D structural neighbourhood</strong> within
                                        <strong> &le;&nbsp;8&nbsp;&Aring;</strong>, considering both
                                        <strong> intramolecular contacts</strong> and
                                        <strong> protein&ndash;protein interfaces</strong>. The analysis reports
                                        two complementary sources of evidence <strong>independently</strong>;
                                        they are <strong>never combined into a single score</strong>.
                                    </p>

                                    <p className="mb-2">
                                        <Tag
                                            value="Clinical structural evidence"
                                            icon="pi pi-shield"
                                            severity="danger"
                                            rounded
                                        />
                                        <span className="small text-secondary ms-2">
                                            Nearby residues carrying <strong>known pathogenic ClinVar variants </strong>
                                            identify structural regions already associated with disease.
                                            The local structural neighbourhood is also annotated with
                                            <strong> PTMs</strong>, <strong>active sites</strong> and
                                            <strong> binding sites</strong>, providing additional biological context.
                                        </span>
                                    </p>

                                    <p className="mb-2">
                                        <Tag
                                            value="Computational structural evidence"
                                            icon="pi pi-chart-line"
                                            severity="warning"
                                            rounded
                                        />
                                        <span className="small text-secondary ms-2">
                                            The surrounding residues are evaluated using
                                            <strong> AlphaMissense</strong> to estimate local mutation intolerance.
                                            The analysis reports the number of neighbouring residues predicted as
                                            likely pathogenic (mean AlphaMissense &ge; 0.564; Cheng <em>et&nbsp;al.</em>, 2023)
                                            together with their average AlphaMissense score.
                                        </span>
                                    </p>

                                    <p className="mb-0 small text-secondary">
                                        This analysis is <strong>hypothesis-generating</strong> and is intended to
                                        <strong> prioritise VUS for further investigation</strong>. It does not
                                        provide a clinical classification or diagnostic verdict. Observed
                                        pathogenic neighbours may also reflect ClinVar ascertainment bias rather
                                        than biology alone.
                                    </p>
                                </div>
                                    <ColumnToggleDataTable value={vusShown} className="nobreak mb-4" paginator rows={5} stripedRows showGridlines filterDisplay="menu" tableStyle={{ minWidth: '72rem' }} responsiveLayout="scroll">
                                        <Column field="col1" header="Position" sortable />
                                        <Column field="variant" header="Variant" sortable />
                                        <Column field="clinvar" header="ClinVar" body={(r) => clinTag(r.clinvar)} sortable />
                                        <Column field="res_am_frac" header="AM Pathogenic Frac" body={(r) => fmt(r.res_am_frac)} sortable />
                                        <Column header="Neighbours (intra/inter)" sortable sortField="intra_n_neighbours" body={(r) => `${(r.intra_n_neighbours||0)+(r.inter_n_neighbours||0)}  (${r.intra_n_neighbours||0}/${r.inter_n_neighbours||0})`} />
                                        <Column field="obs_n_total" header="Obs. pathogenic (intra/inter)" body={(r) => `${r.obs_n_total}  (${r.obs_intra_n}/${r.obs_inter_n})`} sortable />
                                        <Column field="obs_min_dist" header="Nearest pathogenic" body={(r) => fmtDist(r.obs_min_dist)} sortable />
                                        <Column field="_pathFrac" header="Path. nb. fraction" body={(r) => fmt(r._pathFrac)} sortable />
                                        <Column header="Microenvironment" body={microBody} />
                                        <Column field="pred_best" header="AM mean (neighbours)" body={(r) => fmt(r.pred_best)} sortable />
                                        <Column field="pred_lp_total" header="Likely-path. neighbours (AM)" sortable />
                                    </ColumnToggleDataTable>


                                  <Message severity="info" className="mb-3 w-100" text={`${vusShown.length} VUS with a 3D neighbourhood \u00b7 ${meta.n_with_observed_pathogenic_env || 0} sit in an observed pathogenic environment \u00b7 ${meta.n_with_predicted_lp_env || 0} in a predicted (AlphaMissense) likely-pathogenic environment.`} />

                                    <div className="row g-3 mt-1">
                                        <div className="col-12 col-lg-6">
                                            <div className="p-3 border rounded bg-white h-100">
                                                <h6 className="mb-3 text-primary"><i className="pi pi-chart-line me-2"/>Observed vs predicted environment</h6>
                                                <Plot
                                                    data={[{
                                                        x: vusShown.map((r) => r.obs_n_total),
                                                        y: vusShown.map((r) => (r.pred_best == null ? 0 : r.pred_best)),
                                                        text: vusShown.map((r) => `${r.variant} (${r.position})`),
                                                        mode: 'markers', type: 'scatter',
                                                        marker: { size: vusShown.map((r) => 7 + 2.2 * Math.sqrt((r.intra_n_neighbours || 0) + (r.inter_n_neighbours || 0))), line: { width: 0.5, color: '#555' },
                                                            color: vusShown.map((r) => (r.res_am_frac == null ? 0 : r.res_am_frac)),
                                                            colorscale: [[0, 'rgb(255,247,236)'], [0.5, 'rgb(253,141,60)'], [1, 'rgb(127,0,0)']], cmin: 0, cmax: 1, showscale: true,
                                                            colorbar: { title: 'Own<br>AM frac' } },
                                                        hovertemplate: '%{text}<br>observed path.: %{x}<br>pred. mean AM: %{y:.3f}<extra></extra>'
                                                    }]}
                                                    layout={{ autosize: true, height: 380, margin: { t: 10, b: 50, l: 55, r: 10 },
                                                        xaxis: { title: 'Observed pathogenic neighbours (ClinVar)' },
                                                        yaxis: { title: 'Predicted env. (mean neighbour AM)', range: [0, 1] } }}
                                                    useResizeHandler style={{ width: '100%' }} config={{ displayModeBar: false }} />
                                                <div className="small text-500 mt-1">Each point is a VUS. X = observed pathogenic neighbours; Y = predicted (mean neighbour AM); colour = the VUS's own AlphaMissense intolerance (dark = higher); <strong>point size = number of neighbours</strong> (a small dot has few neighbours &mdash; read its AM mean cautiously). Top-right, dark and large = strongest case to re-evaluate.</div>
                                            </div>
                                        </div>
                                        <div className="col-12 col-lg-6">
                                            <div className="p-3 border rounded bg-white h-100">
                                                <h6 className="mb-3 text-primary"><i className="pi pi-chart-bar me-2"/>Top VUS by observed pathogenic neighbours</h6>
                                                <Plot
                                                    data={[
                                                        { y: topBars.map((r) => r.variant), x: topBars.map((r) => r.obs_intra_n), name: 'intra', type: 'bar', orientation: 'h', marker: { color: '#3B82F6' } },
                                                        { y: topBars.map((r) => r.variant), x: topBars.map((r) => r.obs_inter_n), name: 'inter', type: 'bar', orientation: 'h', marker: { color: '#f0a020' } }
                                                    ]}
                                                    layout={{ barmode: 'stack', autosize: true, height: 380, margin: { t: 10, b: 45, l: 110, r: 10 },
                                                        xaxis: { title: '# pathogenic neighbours' },
                                                        yaxis: { autorange: 'reversed' }, legend: { orientation: 'h' } }}
                                                    useResizeHandler style={{ width: '100%' }} config={{ displayModeBar: false }} />
                                                <div className="small text-500 mt-1">Intra (same chain) vs inter (across the interface, from a partner protein).</div>
                                            </div>
                                        </div>
                                    </div>
                                {(() => {
                                  const mol = result.molstarData || {};
                                  const sId = mol.structureId || meta.structure;
                                  const LUT = (mol.lookup && mol.lookup[sId]) || {};
                                  const tAcc = meta.target_acc || (vus[0] && String(vus[0].position).split('_')[0]);
                                  const vp = [], vc = [];
                                  vusShown.forEach((r) => { const q = parseInt(String(r.position).split('_').pop(), 10); const e = LUT[q]; if (e && e.mapped != null) { vp.push(parseInt(e.mapped, 10)); vc.push(e.chain || 'A'); } });
                                  if (!sId || vp.length === 0) return null;
                                  return (
                                    <div className="mt-4">
                                      <div className="d-flex align-items-center mb-8"><h5 className="mb-0 text-primary"><i className="pi pi-box me-2" />VUS on structure {sId}</h5></div>
                                      <div className="viewer-box mb-2" style={{ minHeight: '500px' }}>
                                        <MolstarViewerPath key={`l12-${sId}`} level="12" uniac={tAcc} pdbId={sId} positions={vp} chain={vc} />
                                      </div>
                                      <div className="small text-500">VUS residues of {tAcc} as spheres on {sId}.</div>
                                    </div>
                                  );
                                })()}
                                </Card>
                                );
                            })()}

                            {result && mode === 'level13' && (() => {
                                const rows = (result.data || []);
                                const st = result.enrichmentStats || {};
                                const fmtD = (x) => (x === null || x === undefined ? '\u2014' : (typeof x === 'number' ? x.toFixed(2) : x));

                                // --- AlphaMissense cell: Variant + Residue, separate lines, real values ---
                                const amSev = (c) => (c === 'likely_pathogenic' ? 'danger'
                                                    : (c === 'ambiguous' ? 'warning' : 'success'));
                                const amBody = (r) => {
                                  const hasVar = r.exact_am_class != null || r.exact_am_score != null;
                                  const hasRes = r.res_am_class != null || r.res_am_mean != null;
                                  if (!hasVar && !hasRes) return <span className="text-500">&#8212;</span>;
                                  return (
                                    <div className="d-flex flex-column gap-1">
                                      {hasVar && (
                                        <Tag value={`Variant: ${r.exact_am_class || 'n/a'}${r.exact_am_score != null ? ` (${r.exact_am_score})` : ''}`}
                                             severity={amSev(r.exact_am_class)} />
                                      )}
                                      {hasRes && (
                                        <Tag value={`Residue: ${r.res_am_class || 'n/a'}${r.res_am_mean != null ? ` (${r.res_am_mean})` : ''}`}
                                             severity={amSev(r.res_am_class)} />
                                      )}
                                    </div>
                                  );
                                };

                                // --- ClinVar cell: flag + (on exact) clinical significance & consequence ---
                                const clinBody = (r) => {
                                  if (r.clinvar_match === 'exact') {
                                    return (
                                      <div className="d-flex flex-column gap-1">
                                        <Tag value="Exact match" severity="danger" />
                                        {r.clinvar_significance && <span className="small text-500">{r.clinvar_significance}</span>}
                                        {r.clinvar_consequence && <span className="small text-500">{r.clinvar_consequence}</span>}
                                      </div>
                                    );
                                  }
                                  if (r.clinvar_match === 'same_pos')
                                    return <Tag value={`Same position (${r.samepos_aa_change || '?'})`} severity="warning" />;
                                  return <Tag value="Novel" severity="info" />;
                                };

                                // --- functional site / microenvironment ---
                                //   self  → "Binds: ATP" / "Active: Active site"  (clean)
                                //   near  → "Binds near: ATP · 7.69Å" / "Active near: ... · XÅ" / "PTM near: ... · XÅ"
                                const siteBody = (r) => {
                                  const t = [];
                                  const oS = r.own_sites || [], oL = r.own_ligands || [];
                                  if (oS.length) t.push([`Active: ${oS.join(', ')}`, 'success']);
                                  if (oL.length) t.push([`Binds: ${oL.join(', ')}`, 'warning']);
                                  const near = (arr, prefix, sev) => {
                                    (arr || []).forEach((it) => {
                                      const d = it.dist != null ? ` \u00b7 ${it.dist}\u00c5` : '';
                                      t.push([`${prefix} near: ${it.name}${d}`, sev]);
                                    });
                                  };
                                  near(r.active_near, 'Active', 'success');
                                  // PTM: own residue shows its name clean; neighbours show 'PTM near: ... · XÅ'
                                  const oP = r.own_ptm_labels || [];
                                  if (oP.length) t.push([`PTM: ${oP.join(', ')}`, 'info']);
                                  near(r.ptm_near, 'PTM', 'info');
                                  near(r.binding_near, 'Binds', 'warning');
                                  return t.length
                                    ? <span className="d-flex flex-column gap-1">{t.map(([v, s], k) => <Tag key={k} value={v} severity={s} />)}</span>
                                    : <span className="text-500">&#8212;</span>;
                                };

                                // --- neighbours (intra/inter) like L12 ---
                                const nbBody = (r) => {
                                  const tot = (r.intra_n_neighbours || 0) + (r.inter_n_neighbours || 0);
                                  if (tot === 0) return <span className="text-500">&#8212;</span>;
                                  return <span>{tot} <span className="text-500 small">({r.intra_n_neighbours || 0}/{r.inter_n_neighbours || 0})</span></span>;
                                };

                                // --- observed pathogenic (intra/inter) like L12 ---
                                const obsBody = (r) => {
                                  if ((r.obs_n_total || 0) === 0) return <span className="text-500">&#8212;</span>;
                                  return <span><strong>{r.obs_n_total}</strong> <span className="text-500 small">({r.obs_intra_n || 0}/{r.obs_inter_n || 0}) &#183; nearest {fmtD(r.nearest_pathogenic)} &#8491;</span></span>;
                                };

                                // --- evolutionary: homolog residues (protein + position) below the count ---
                                const evoBody = (r) => (r.homolog_pathogenic > 0
                                  ? <div className="d-flex flex-column gap-1">
                                      <Tag value={`${r.homolog_pathogenic} homolog(s)`} severity="danger" />
                                      <span className="text-500 small">{(r.homolog_residues || []).join(', ')}</span>
                                    </div>
                                  : <span className="text-500">&#8212;</span>);

                                // --- PPI interface: partners listed below the count (like homologs) ---
                                const ppiBody = (r) => (r.interface_partners > 0
                                  ? <div className="d-flex flex-column gap-1">
                                      <Tag value={`${r.interface_partners} partner(s)`} severity="warning" />
                                      <span className="text-500 small">{(r.partners || []).join(', ')}</span>
                                    </div>
                                  : <span className="text-500">&#8212;</span>);

                                // --- Class connectivity (was Hinge) — same colours/legend as Level 5 ---
                                const CLASS5 = {
                                  rigid:     { name: 'Rigid core', color: '#1e3a8a' },
                                  hinge:     { name: 'Hinge',      color: '#7c3aed' },
                                  transient: { name: 'Transient',  color: '#f59e0b' },
                                };
                                const classBody = (r) => {
                                  const c = CLASS5[r.hinge_class];
                                  if (!c) return <span className="text-500">&#8212;</span>;
                                  return <span><span style={{ display: 'inline-block', width: '10px', height: '10px', background: c.color, borderRadius: '3px', marginRight: '6px' }}></span>{c.name}</span>;
                                };

                                // --- convergence badge (count of strong signals fired) ---
                                const convBody = (r) => {
                                  const n = r.convergence || 0;
                                  const sev = n >= 3 ? 'danger' : (n === 2 ? 'warning' : (n === 1 ? 'info' : 'secondary'));
                                  return <Tag value={`${n} / 4`} severity={sev} rounded />;
                                };

                                // summary counts (L6-style)
                                const nStrong = rows.filter((r) => (r.convergence || 0) >= 3).length;
                                const nAM = rows.filter((r) => r.res_am_class === 'likely_pathogenic').length;
                                const nObs = rows.filter((r) => (r.obs_n_total || 0) > 0).length;
                                const nEvo = rows.filter((r) => (r.homolog_pathogenic || 0) > 0).length;
                                const nSite = rows.filter((r) => r.own_binding || r.own_active).length;
                                const convDist = [0, 1, 2, 3, 4].map((k) => rows.filter((r) => (r.convergence || 0) === k).length);
                                const topBars = [...rows].sort((a, b) => (b.res_am_mean || 0) - (a.res_am_mean || 0)).slice(0, 15);


                                return (
                                <Card className="shadow-sm animate__animated animate__fadeIn">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h4 className="text-primary mb-0"><AlertTriangle className="me-2"/>Variant Convergence Triage</h4>
                                        <Button label="Download CSV" icon="pi pi-download" severity="secondary" outlined size="small" onClick={downloadCSV} />
                                    </div>

                                    <div className="card card-spm bg-light border-0 p-3 mb-4 text-start">
                                      <h5 className="mb-3"><i className="pi pi-info-circle me-2 text-primary" />How to read this triage</h5>
                                     <p className="mb-3 small">
                                      Each of your variants is cross-referenced against the graph and scored on up to <strong>eight independent lines of evidence</strong> — this is a prioritisation aid, not a clinical verdict. The <strong>Convergence</strong> column counts how many of the four <em>strong</em> signals fired: <strong>AlphaMissense</strong> (likely-pathogenic), <strong>functional site</strong>, <strong>observed pathogenic neighbourhood</strong> and <strong>evolutionary recurrence</strong>.
                                    </p>

                                      <p className="mb-2">
                                        <Tag value="AlphaMissense" icon="pi pi-chart-line" severity="danger" rounded />
                                        <span className="small text-secondary ms-2">
                                          Residue-level mutation intolerance.
                                        </span>
                                      </p>
                                      <p className="mb-2">
                                        <Tag value="Functional site" icon="pi pi-map-marker" severity="warning" rounded />
                                        <span className="small text-secondary ms-2">
                                          Whether the variant sits on, or in the 3D neighbourhood of, a UniProt <strong>binding site</strong>, <strong>active site</strong> or <strong>post-translational modification</strong>.
                                        </span>
                                      </p>
                                      <p className="mb-2">
                                        <Tag value="Observed pathogenic environment" icon="pi pi-shield" severity="danger" rounded />
                                        <span className="small text-secondary ms-2">
                                          Nearby residues carrying <strong>known pathogenic ClinVar variants</strong> (within 8&nbsp;&Aring;, intra- and inter-chain) identify structural regions already associated with disease. May reflect ClinVar ascertainment bias.
                                        </span>
                                      </p>
                                      <p className="mb-2">
                                        <Tag value="Evolutionary recurrence" icon="pi pi-sitemap" severity="success" rounded />
                                        <span className="small text-secondary ms-2">
                                          A homologous protein carrying a pathogenic variant at the <strong>same Pfam-A HMM column</strong>.
                                        </span>
                                      </p>
                                      <p className="mb-0 small text-secondary">
                                      Flags reflect ClinVar records ingested in Atlantis, not live ClinVar. <strong>Novel</strong>: not in this database; <strong>Same position</strong>: a different substitution at this residue is present; <strong>Exact match</strong>: shows the recorded significance and consequence.
                                    </p>
                                    </div>

                                    {st.analysisWarning && <Message severity="warn" className="mb-3 w-100" text={st.analysisWarning} />}

                                    {/* summary counts (Level 6 style) */}
                                    <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                                        <span className="text-500 small me-1">Summary:</span>
                                        <Tag icon="pi pi-list" value={`${rows.length} variants`} severity="info" />
                                        <Tag icon="pi pi-flag-fill" value={`${nStrong} strong convergence (\u2265 3)`} severity="danger" />
                                        <Tag icon="pi pi-chart-line" value={`${nAM} AlphaMissense likely-path.`} style={{ backgroundColor: '#7c3aed', color: '#fff' }} />
                                        <Tag icon="pi pi-shield" value={`${nObs} observed pathogenic env.`} style={{ backgroundColor: '#dc2626', color: '#fff' }} />
                                        <Tag icon="pi pi-sitemap" value={`${nEvo} evolutionary recurrence`} style={{ backgroundColor: '#0f766e', color: '#fff' }} />
                                        <Tag icon="pi pi-map-marker" value={`${nSite} on a functional site`} style={{ backgroundColor: '#f59e0b', color: '#fff' }} />
                                    </div>

                                    <ColumnToggleDataTable value={rows} className="nobreak mb-4" paginator rows={5} stripedRows showGridlines
                                               sortField="convergence" sortOrder={-1}
                                               filterDisplay="menu" tableStyle={{ minWidth: '96rem' }} responsiveLayout="scroll">
                                        <Column field="col2" header="Variant" sortable frozen />
                                        <Column field="convergence" header="Convergence" body={convBody} sortable />
                                        <Column field="clinvar_match" header="ClinVar" body={clinBody} sortable />
                                        <Column header="AlphaMissense" body={amBody} />
                                        <Column field="res_am_frac" header="AlphaMissense pathogenic fraction" body={(r) => (r.res_am_frac == null ? <span className="text-500">&#8212;</span> : r.res_am_frac)} sortable />
                                        <Column header="Microenvironment" body={siteBody} />
                                        <Column header="Neighbours (intra / inter)" sortField="intra_n_neighbours" body={nbBody} sortable />
                                        <Column field="obs_n_total" header="Observed pathogenic (intra / inter)" body={obsBody} sortable />
                                        <Column header="Evolutionary recurrence" body={evoBody} sortField="homolog_pathogenic" sortable />
                                        <Column header="Protein interface" body={ppiBody} sortField="interface_partners" sortable />
                                        <Column header="Class connectivity" body={classBody} sortField="hinge_class" sortable />
                                        <Column field="betweenness_percentile" header="Betweenness centrality" sortable body={(r) => {
                                          if (r.betweenness_percentile == null) return <span className="text-500">&#8212;</span>;
                                          const pct = r.betweenness_percentile;
                                          const topN = Math.max(1, Math.round(100 - pct));
                                          const sev = pct >= 90 ? 'danger' : (pct >= 75 ? 'warning' : (pct >= 50 ? 'info' : 'secondary'));
                                          return <div className="d-flex flex-column gap-1"><Tag value={`top ${topN}%`} severity={sev} /><span className="text-500 small">{r.betweenness != null ? r.betweenness : ''}</span></div>;
                                        }} />
                                        <Column field="clinvar_disease" header="ClinVar disease" body={(r) => r.clinvar_disease || <span className="text-500">&#8212;</span>} />
                                    </ColumnToggleDataTable>

                                    {/* Class connectivity legend (same as Level 5) */}
                                    <div className="d-flex flex-wrap gap-3 small text-500 mb-3">
                                        <span className="me-1">Class connectivity:</span>
                                        <span><span style={{ display: 'inline-block', width: '12px', height: '12px', background: '#1e3a8a', borderRadius: '3px', marginRight: '6px' }}></span>Rigid core (consensus contacts)</span>
                                        <span><span style={{ display: 'inline-block', width: '12px', height: '12px', background: '#7c3aed', borderRadius: '3px', marginRight: '6px' }}></span>Hinge (rewired — allosteric)</span>
                                        <span><span style={{ display: 'inline-block', width: '12px', height: '12px', background: '#f59e0b', borderRadius: '3px', marginRight: '6px' }}></span>Transient (PDB/AF-only)</span>
                                    </div>


                                    <div className="row g-3 mt-1">
                                        <div className="col-12 col-lg-6">
                                            <div className="p-3 border rounded bg-white h-100">
                                                <h6 className="mb-3 text-primary"><i className="pi pi-chart-bar me-2"/>Convergence distribution</h6>
                                                <Plot
                                                    data={[{ x: ['0', '1', '2', '3', '4'], y: convDist, type: 'bar',
                                                             marker: { color: ['#9ca3af', '#3B82F6', '#f59e0b', '#f97316', '#dc2626'] } }]}
                                                    layout={{ autosize: true, height: 340, margin: { t: 10, b: 40, l: 40, r: 10 },
                                                        xaxis: { title: 'strong signals fired' }, yaxis: { title: '# variants' } }}
                                                    useResizeHandler style={{ width: '100%' }} config={{ displayModeBar: false }} />
                                                <div className="small text-500 mt-1">How many of your variants converge on 0–4 strong signals.</div>
                                            </div>
                                        </div>
                                        <div className="col-12 col-lg-6">
                                            <div className="p-3 border rounded bg-white h-100">
                                                <h6 className="mb-3 text-primary"><i className="pi pi-chart-line me-2"/>Residue AlphaMissense mean</h6>
                                                <Plot
                                                    data={[{ y: topBars.map((r) => r.col2), x: topBars.map((r) => r.res_am_mean || 0), type: 'bar', orientation: 'h',
                                                             marker: { color: topBars.map((r) => (r.res_am_class === 'likely_pathogenic' ? '#dc2626' : '#3B82F6')) } }]}
                                                    layout={{ autosize: true, height: 340, margin: { t: 10, b: 40, l: 80, r: 10 },
                                                        xaxis: { title: 'residue AM mean', range: [0, 1] },
                                                        yaxis: { autorange: 'reversed' } }}
                                                    useResizeHandler style={{ width: '100%' }} config={{ displayModeBar: false }} />
                                                <div className="small text-500 mt-1">Dashed clinical threshold is 0.564 (Cheng et al. 2023).</div>
                                            </div>
                                        </div>
                                    </div>

                                    {(() => {
                                      const mol = result.molstarData || {};
                                      const sId = mol.structureId || st.structure || formData.pdbId;
                                      const LUT = (mol.lookup && mol.lookup[sId]) || {};
                                      const tAcc = st.proteinA || formData.proteinA;
                                      const vp = [], vc = [];
                                      rows.forEach((r) => {
                                        const q = parseInt(String(r.col1), 10);
                                        const e = LUT[q];
                                        if (e && e.mapped != null) { vp.push(parseInt(e.mapped, 10)); vc.push(e.chain || 'A'); }
                                        else if (Object.keys(LUT).length === 0 && !isNaN(q)) { vp.push(q); vc.push('A'); }
                                      });
                                      if (!sId || vp.length === 0) return null;
                                      return (
                                        <div className="mt-4">
                                          <div className="d-flex align-items-center mb-8"><h5 className="mb-0 text-primary"><i className="pi pi-box me-2" />Variants on structure {sId}</h5></div>
                                          <div className="viewer-box mb-2" style={{ minHeight: '500px' }}>
                                            <MolstarViewerPath key={`l13-${sId}`} level="13" uniac={tAcc} pdbId={sId} positions={vp} chain={vc} />
                                          </div>
                                          <div className="small text-500">Your variant residues of {tAcc} as spheres on {sId}.</div>
                                        </div>
                                      );
                                    })()}
                                </Card>
                                );
                            })()}
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
};

export default PathfinderPage;